import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { DEAD_SIDE_LABEL, deadBracketSides, isDeadSide } from "../lib/bracketDeadSide";
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import EliminationBracket from "../components/EliminationBracket";
import ConsolationBracketCard from "../components/ConsolationBracketCard";
import TournamentBottomNav from "../components/TournamentBottomNav";
import DivisionFormatGuardCard, { divisionNeedsFormat } from "../components/DivisionFormatGuardCard";
import DivisionChecklistCard from "../components/DivisionChecklistCard";
import DrawCeremonyAdminCard from "../components/DrawCeremonyCard";
import FieldHint from "../components/FieldHint";
import { makeMatchesOwnerLifecycle, type MatchesOwnerLifecycle } from "../lib/matchesOwnerLifecycle";
import { runMatchesMutation, runProducerLoad } from "../lib/matchesProducer";
import { drawCeremonyRefreshFingerprint } from "../lib/drawCeremonyLines";
import { matchScoreFromWinner } from "../lib/scoreFromWinner";
import TournamentSetupProgress from "../components/TournamentSetupProgress";
import { useTournamentMutationTick } from "../lib/useTournamentMutationTick";
import { uploadAssetHref } from "../lib/assets";
import { rrNoScoreLabel, rrRolWhen, rrWhen } from "../lib/rrMatrix";
import { buildDrawPosterHtml, openDrawPosterWindow, type PosterGroup, type PosterStanding } from "../lib/drawPoster";
import { savedJornadaRange, savedJornadaRangeShort } from "../lib/jornadaDates";
import { bracketVisualSideOrder, type BracketCompetitor, type BracketMatch, type BracketRound } from "../lib/bracketLayout";
import { competitorLoserTag, isConsolationLabel } from "../lib/bracketFromMatches";
import { orderPreliminaryTargetEntrants } from "../lib/bracketSeeding";
import {
  buildPlayoffPreviewRounds,
  manualSeedLockReason,
  PLAYOFF_NAMES_PENDING_NOTE,
  playoffNamesPending,
  qualifierPlaceholderLabels,
} from "../lib/playoffPreviewPlaceholders";
import { formatDate, formatDateWithTime } from "../lib/dateFormat";
import { checklistBasePath } from "../lib/draftWizard";
import { canAdvanceFromSetupStep, setupGate } from "../lib/setupGates";
import { getEliminationDirectAdvancerSummary } from "../lib/elimination";
import {
  directAdvancerIdsFromNotes,
  effectivePreviewDrawSize,
  isPartialRoundOf16Preview,
  partialEliminationNextRoundSources,
  partialEliminationPreviewStructure,
  partialEliminationRoundSlots,
} from "../lib/eliminationDraw";
import { collectAppStyleMarkup, openPrintWindow } from "../lib/printTemplate";
import { groupJornadasPrintHtml, restScreenLines, restsForJornada } from "../lib/jornadaRests";
import { eliminationRoundDisplayLabel, groupPlayoffVisualRounds, isCancelledPlayoffMatch, isStructuralPlayoffMatch } from "../lib/roundRobinPhases";
import { showPointsColumn } from "../lib/standingsRules";
import { courtNoticeName } from "../lib/courts";
import { dropMatchLabel, dropMatchState, nameWithDropMark, type DropMatchState } from "../lib/dropMatch";
import RoundRobinChangeGroupDialog, { type RoundRobinChangeGroupTarget } from "../components/RoundRobinChangeGroupDialog";
import GroupStrayMatchesNotice from "../components/GroupStrayMatchesNotice";
import RoundRobinPreviewJornadas from "../components/RoundRobinPreviewJornadas";
import {
  groupSheetPositions,
  initialArrangeDraft,
  joinSpanishList,
  moveInList,
  orderMatrixParticipants,
  RR_SHEET_PLACE_TOOLTIP,
  sameMembers,
  sameOrder,
} from "../lib/rrSheetOrder";
import {
  playerSelectDisabled,
  playoffPlayerOptionLabel,
  regeneratePlayoffsState,
  sortPlayoffPlayerOptions,
  type PlayoffOptionInfo,
} from "../lib/playoffEditOptions";
import {
  api,
  isUnfilledPlayoffPlaceholder,
  playoffSeedInfoByParticipant,
  ApiError,
  type GroupArrangementCategory,
  type GroupOrderPreview,
  type EliminationFormatConfig,
  type JornadaOverviewResponse,
  type LadderFormatConfig,
  type RoundRobinFormatConfig,
  type RoundRobinQualificationPreviewPayload,
  type MatchupBulkUpdateItem,
  type MatchParticipantBrief,
  type ScheduleReadinessRow,
  type MatchupValidationError,
  type Tournament,
  type TournamentFormatConfig,
  type TournamentRound,
  type TournamentRoundMatchup,
  type TournamentMatch,
  type TournamentMatchScoreSet,
  type TournamentParticipant,
  type TournamentGroup,
  type TournamentDivision,
} from "../lib/api";

interface PendingChange {
  player1: string;
  player2: string;
}

type PendingChanges = Record<number, PendingChange>;

interface RoundRobinEdit {
  roundNumber: string;
  groupId: string;
  player1: string;
  player2: string;
}

type RoundRobinEdits = Record<number, RoundRobinEdit>;

interface EliminationEdit {
  groupId: string;
  player1: string;
  player2: string;
}

type EliminationEdits = Record<number, EliminationEdit>;

interface RoundRobinVisualMatch {
  id: number;
  matchNumber: number | null;
  roundNumber: number | null;
  status: string;
  scheduledDate: string | null;
  scheduledTime: string | null;
  court: string | null;
  /** Effective player1 id — orients score_sets for the row's perspective. */
  player1Id: number;
  player2Id: number;
  winnerParticipantId: number | null;
  scoreSets: TournamentMatchScoreSet[];
  scoreText: string | null;
  /** Serie por equipos (Mini Copa): la celda solo dice "Serie" y quién la
   * ganó; sus líneas se ven en Resultados, no en la matriz. */
  isTie: boolean;
  /** Contra alguien dado de baja (Eduardo 2026-10-05, torneo 69): la celda
   * dice «Cancelado por baja», «W.O. por baja» o «Baja de … · pendiente». */
  drop: DropMatchState | null;
}

/** Tamaños de letra del PDF de la matriz según el grupo más grande (Eduardo
 * 2026-09-18: con 7 participantes la tabla se cortaba a la derecha). La tabla
 * siempre ocupa el 100% de la hoja; lo que cambia es la letra para que quepan
 * los marcadores de tres sets. */
export function roundRobinPrintScale(maxParticipants: number): { base: string; score: string; detail: string; nameCol: string } {
  if (maxParticipants >= 10) return { base: "0.5rem", score: "0.5rem", detail: "0.42rem", nameCol: "5.2rem" };
  if (maxParticipants >= 8) return { base: "0.56rem", score: "0.56rem", detail: "0.46rem", nameCol: "6rem" };
  if (maxParticipants >= 6) return { base: "0.62rem", score: "0.62rem", detail: "0.5rem", nameCol: "7rem" };
  return { base: "0.68rem", score: "0.72rem", detail: "0.58rem", nameCol: "9rem" };
}

/** Marcador global de una serie ("2-1") visto desde la fila: si la fila es
 * el equipo 2 se invierte. Eduardo 2026-09-17: la celda muestra el resultado. */
export function tieScoreFromPerspective(scoreText: string | null | undefined, fromP1: boolean): string | null {
  const raw = (scoreText ?? "").trim();
  const found = raw.match(/^(\d+)\s*-\s*(\d+)$/);
  if (!found) return raw || null;
  return fromP1 ? `${found[1]}-${found[2]}` : `${found[2]}-${found[1]}`;
}

/** Played-match score seen from `perspectiveId`'s side, e.g. "6-3, 4-6, 10-7". */
function roundRobinVisualResultText(match: RoundRobinVisualMatch, perspectiveId: number): string | null {
  if (match.winnerParticipantId === null) return null;
  if (match.scoreSets.length > 0) {
    const fromP1 = match.player1Id === perspectiveId;
    return match.scoreSets
      .map((set) => {
        const score = fromP1
          ? `${set.player1_games}-${set.player2_games}`
          : `${set.player2_games}-${set.player1_games}`;
        return set.tiebreak_points != null ? `${score}(${set.tiebreak_points})` : score;
      })
      .join(", ");
  }
  return match.scoreText;
}

interface RoundRobinVisualGroup {
  id: number;
  name: string;
  participants: TournamentParticipant[];
  matchesByPair: Map<string, RoundRobinVisualMatch>;
  /** Grupo acomodado: las filas siguen el lugar en la hoja (Eduardo 2026-10-01). */
  hasPositions: boolean;
  /** Filas "fantasma": salen solo de partidos (bajas), no del listado del grupo. */
  ghostIds: Set<number>;
}

interface RoundRobinPlayoffVisualRound {
  roundNumber: number;
  label: string;
  matches: TournamentMatch[];
}

function selectValueFromMatchupSide(
  participantId: number | null,
): string {
  return participantId === null ? "" : String(participantId);
}

function changeFromMatchup(matchup: TournamentRoundMatchup): PendingChange {
  return {
    player1: selectValueFromMatchupSide(matchup.player1_participant_id),
    player2: selectValueFromMatchupSide(matchup.player2_participant_id),
  };
}

function changeMatchesMatchup(
  change: PendingChange,
  matchup: TournamentRoundMatchup,
): boolean {
  return (
    change.player1 === selectValueFromMatchupSide(matchup.player1_participant_id)
    && change.player2 === selectValueFromMatchupSide(matchup.player2_participant_id)
  );
}

function displayWeekName(name: string): string {
  return name
    .replace(/^\s*Jornada\b/i, "Semana")
    .replace(/^\s*Enfrentamiento\b/i, "Semana");
}

function ladderRoundButtonLabel(roundNumber: number, regularRoundCount: number): string {
  if (roundNumber > regularRoundCount) return `Playoff ${roundNumber - regularRoundCount}`;
  return `Semana ${roundNumber}`;
}

function statusLabel(status: string): string {
  switch (status) {
    case "draft":
      return "Borrador";
    case "published":
      return "Publicada";
    case "closed":
      return "Cerrada";
    case "bye":
      return "BYE";
    case "manual":
      return "Manual";
    case "cancelled":
      return "Cancelado";
    case "scheduled":
      return "Programado";
    case "completed":
      return "Completado";
    default:
      return status;
  }
}

function isEliminationBye(match: TournamentMatch): boolean {
  return !match.player1 || !match.player2 || match.status === "bye";
}

function isByeValue(value: string | number | null | undefined): boolean {
  return value === "" || value === null || value === undefined || value === "BYE";
}

function eliminationEditFromMatch(match: TournamentMatch): EliminationEdit {
  return {
    groupId: selectValueFromMatchupSide(match.group_id),
    player1: selectValueFromMatchupSide(match.player1_participant_id),
    player2: selectValueFromMatchupSide(match.player2_participant_id),
  };
}

function eliminationEditMatches(edit: EliminationEdit, match: TournamentMatch): boolean {
  return (
    edit.groupId === selectValueFromMatchupSide(match.group_id)
    && edit.player1 === selectValueFromMatchupSide(match.player1_participant_id)
    && edit.player2 === selectValueFromMatchupSide(match.player2_participant_id)
  );
}

function isEliminationResolved(match: TournamentMatch): boolean {
  return Boolean(
    match.winner_participant_id
      || match.score_text
      || ["completed", "walkover", "no_show", "defaulted", "retired", "double_walkover", "bye"].includes(match.status),
  );
}

/** Un matchup está ARMADO cuando ya tiene ambos jugadores (o resultado, p.ej.
 * BYE). Los placeholders del esqueleto del cuadro (rondas futuras) no cuentan
 * como pendientes de resultado. */
function isEliminationArmed(match: TournamentMatch): boolean {
  return (
    (match.player1_participant_id !== null && match.player2_participant_id !== null)
    || isEliminationResolved(match)
  );
}

function scoreTextFromWinnerPerspective(match: TournamentMatch): string | null {
  const sets = [...(match.score_sets ?? [])].sort((a, b) => a.set_number - b.set_number);
  // Eduardo 2026-10-06: sin sets estructurados, el texto también se voltea al ganador.
  if (sets.length === 0 || !match.winner_participant_id) return matchScoreFromWinner(match).trim() || null;

  const winnerIsPlayer2 = match.winner_participant_id === match.player2_participant_id;
  const winnerIsPlayer1 = match.winner_participant_id === match.player1_participant_id;
  if (!winnerIsPlayer1 && !winnerIsPlayer2) return matchScoreFromWinner(match).trim() || null;

  return sets
    .map((set) => {
      const winnerGames = winnerIsPlayer2 ? set.player2_games : set.player1_games;
      const loserGames = winnerIsPlayer2 ? set.player1_games : set.player2_games;
      const tiebreak = set.tiebreak_points != null ? `(${set.tiebreak_points})` : "";
      const score = `${winnerGames}-${loserGames}${tiebreak}`;
      return set.is_super_tiebreak ? `TB ${score}` : score;
    })
    .join(" ");
}

function playoffMatchResultLabel(match: TournamentMatch): string | null {
  const score = scoreTextFromWinnerPerspective(match);
  if (score && score.toUpperCase() !== "BYE") return `Resultado: ${score}`;
  if (match.status === "bye" || score?.toUpperCase() === "BYE") return "Resultado: BYE";
  if (match.winner_participant_id) {
    const winnerName = matchParticipantName(match.winner, "Ganador definido");
    return `Resultado: Ganó ${winnerName}`;
  }
  if (["completed", "walkover", "no_show", "defaulted", "retired", "double_walkover"].includes(match.status)) {
    return `Resultado: ${statusLabel(match.status)}`;
  }
  return null;
}

function formatRank(rank: number | null | undefined): string {
  return rank === null || rank === undefined ? "—" : `#${rank}`;
}

function firstNameFromDisplayName(name: string | null | undefined): string | null {
  // Primer token CON letras: los demos/importados traen prefijos numéricos
  // ("01 Alonso Demo") y el token "01" no identifica a nadie en el grid.
  for (const token of (name ?? "").trim().split(/\s+/)) {
    if (/[a-záéíóúüñ]/i.test(token)) return token;
  }
  return null;
}

/** «Pasan a playoffs» del formato (1.º y 2.º; + 2 mejores 3.º). */
function drawPosterQualifyText(preview: RoundRobinQualificationPreviewPayload | null): string | null {
  if (!preview?.enabled) return null;
  const parts: string[] = [];
  for (const rule of preview.rules ?? []) {
    if (rule.type === "top_n_per_group") {
      const from = rule.positions_from || 1;
      const to = rule.positions_to;
      if (!to) continue;
      parts.push(from === to ? `${to}.º` : to - from === 1 ? `${from}.º y ${to}.º` : `${from}.º a ${to}.º`);
    } else if (rule.type === "best_n_by_group_position" && rule.count) {
      parts.push(`${rule.count} mejor${rule.count === 1 ? "" : "es"} ${rule.group_position}.º`);
    } else if (rule.type === "best_n_overall" && rule.count) {
      parts.push(`${rule.count} mejor${rule.count === 1 ? "" : "es"} generales`);
    }
  }
  return parts.length ? parts.join(" + ") : null;
}

function participantDisplayName(participant: TournamentParticipant): string {
  return (
    participant.participant_label
    || participant.team?.name
    || participant.player?.display_name
    || `#${participant.id}`
  );
}

/** Participante "de presentación" armado desde el brief del partido: la
 * matriz visual necesita filas aunque el jugador ya no esté en el grupo
 * (dados de baja al cerrar el torneo, como el Otoño RR 2026 donde los 70
 * están en Baja y el listado de grupos los filtra — Eduardo 2026-09-07). */
function participantFromMatchBrief(brief: NonNullable<TournamentMatch["player1"]>): TournamentParticipant {
  const displayName = brief.display_name?.trim() || `#${brief.id}`;
  return {
    id: brief.id,
    participant_kind: "player",
    participant_label: displayName,
    participant_short_label: brief.short_name?.trim() || null,
    player: { display_name: displayName },
    team: null,
    current_rank: brief.current_rank ?? null,
    initial_rank: brief.initial_rank ?? brief.tournament_rank ?? null,
    // Fila que sale del partido (una baja): se marca «· baja» en la matriz.
    current_status: brief.current_status,
  } as unknown as TournamentParticipant;
}

function compactParticipantDisplayName(participant: TournamentParticipant): string {
  // El backend ya sirve el nombre corto canónico ("Alonso / Bruno"); los
  // caminos siguientes quedan de fallback para payloads viejos en caché.
  const shortLabel = participant.participant_short_label?.trim();
  if (shortLabel) return shortLabel;
  if (participant.participant_kind === "team") {
    const memberFirstNames = [...(participant.team?.members ?? [])]
      .sort((a, b) => a.position - b.position)
      .map((member) => firstNameFromDisplayName(member.player?.display_name))
      .filter((name): name is string => Boolean(name));

    if (memberFirstNames.length > 0) {
      return memberFirstNames.join(" / ");
    }

    const labelFirstNames = participantDisplayName(participant)
      .split("/")
      .map((name) => firstNameFromDisplayName(name))
      .filter((name): name is string => Boolean(name));

    if (labelFirstNames.length > 0) {
      return labelFirstNames.join(" / ");
    }
  }

  return participantDisplayName(participant);
}

/** Feedback de Eduardo (2026-07-14): en los matchups del draw los jugadores
 * se eligen POR RANKING — la lista va ordenada por rank y cada opción lo
 * muestra ("Rank#1 Diego Eloy Méndez Montiel"). Sin rank van al final. */
function rankedPlayerOptions(participants: TournamentParticipant[]): TournamentParticipant[] {
  return [...participants].sort((a, b) => {
    const aRank = a.current_rank ?? a.initial_rank ?? Number.POSITIVE_INFINITY;
    const bRank = b.current_rank ?? b.initial_rank ?? Number.POSITIVE_INFINITY;
    if (aRank !== bRank) return aRank - bRank;
    return compactParticipantDisplayName(a).localeCompare(compactParticipantDisplayName(b), "es");
  });
}

function rankedPlayerOptionLabel(participant: TournamentParticipant): string {
  const rank = participant.current_rank ?? participant.initial_rank;
  const name = compactParticipantDisplayName(participant);
  return rank != null ? `Rank#${rank} ${name}` : name;
}

function normalizeSearchText(value: string | null | undefined): string {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function buildGroupRankMap(
  participants: TournamentParticipant[],
  rankingScope: string | null | undefined,
): Map<number, number> {
  const ordered = [...participants].sort((a, b) => {
    const useInitial = rankingScope === "group_initial_rank";
    const aPrimary = useInitial ? a.initial_rank : a.current_rank;
    const bPrimary = useInitial ? b.initial_rank : b.current_rank;
    const aSecondary = useInitial ? a.current_rank : a.initial_rank;
    const bSecondary = useInitial ? b.current_rank : b.initial_rank;
    const primary = (aPrimary ?? 1_000_000_000) - (bPrimary ?? 1_000_000_000);
    if (primary !== 0) return primary;
    const secondary =
      (aSecondary ?? 1_000_000_000) - (bSecondary ?? 1_000_000_000);
    if (secondary !== 0) return secondary;
    return participantDisplayName(a).localeCompare(participantDisplayName(b));
  });

  return new Map(ordered.map((participant, index) => [participant.id, index + 1]));
}

function participantGroupRankValue(
  participant: TournamentParticipant,
  groupRankMap: Map<number, number>,
): number | null {
  const value = groupRankMap.get(participant.id);
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function participantGroupRankLabel(
  participant: TournamentParticipant,
  groupRankMap: Map<number, number>,
): string {
  const groupRank = participantGroupRankValue(participant, groupRankMap);
  return `${compactParticipantDisplayName(participant)}${groupRank !== null ? ` · #${groupRank}` : ""}`;
}

function isLadderConfig(
  config: TournamentFormatConfig | null,
): config is TournamentFormatConfig & { config_json: LadderFormatConfig } {
  if (!config || config.format_type !== "ladder") return false;
  const json = config.config_json as (LadderFormatConfig & {
    format_mode?: string;
    ladder_stage?: { round_count?: number; current_round?: number };
  });
  return (
    typeof json.round_count === "number"
    || json.format_mode === "ladder_playoffs"
    || typeof json.ladder_stage?.round_count === "number"
    || typeof json.ladder_stage?.current_round === "number"
  );
}

function ladderBaseRoundCount(config: LadderFormatConfig | null): number {
  if (!config) return 0;
  const json = config as LadderFormatConfig & {
    ladder_stage?: { round_count?: number; current_round?: number };
  };
  return json.round_count ?? json.ladder_stage?.round_count ?? json.ladder_stage?.current_round ?? 0;
}

function isRoundRobinConfig(
  config: TournamentFormatConfig | null,
): config is TournamentFormatConfig & { config_json: RoundRobinFormatConfig } {
  return (
    !!config
    && config.format_type === "round_robin"
    && typeof (config.config_json as RoundRobinFormatConfig).round_count === "number"
  );
}

function isEliminationConfig(
  config: TournamentFormatConfig | null,
): config is TournamentFormatConfig & { config_json: EliminationFormatConfig } {
  return !!config && config.format_type === "elimination";
}

function isLadderPlayoffPhaseConfig(config: TournamentFormatConfig | null): boolean {
  return config?.format_type === "ladder"
    && (config.config_json as { format_mode?: string } | null | undefined)?.format_mode === "ladder_playoffs";
}

function isGeneratedPhasePlayoffMatch(match: TournamentMatch): boolean {
  return match.group_id === null
    && typeof match.notes === "string"
    && /\bPlayoff v2\b/.test(match.notes);
}

function legacyPlayoffRoundOrder(label: string | null | undefined): number | null {
  const normalized = (label ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  if (!normalized || normalized.includes("tercer")) return null;
  if (normalized.includes("ronda de 64")) return 1;
  if (normalized.includes("dieciseisavos") || normalized.includes("ronda de 32")) return 2;
  if (normalized.includes("octavos") || normalized.includes("ronda de 16")) return 3;
  if (normalized.includes("cuartos")) return 4;
  if (normalized.includes("semifinal")) return 5;
  if (normalized === "final" || normalized.includes(" final")) return 6;
  return null;
}

/** "3er Lugar" / "Tercer lugar" — shown as a mini-box under the final, never as a column. */
function isThirdPlaceRoundLabel(label: string | null | undefined): boolean {
  const normalized = (label ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
  return normalized.includes("tercer") || normalized.startsWith("3er") || normalized.includes("consolacion");
}

export function isPhasePlayoffMatch(match: TournamentMatch): boolean {
  // Cancelados NUNCA cuentan para la estructura del cuadro (una Final
  // cancelada + regenerada dejaría dos filas y el árbol parecería incompleto).
  if (isCancelledPlayoffMatch(match)) return false;
  // El cuadro de consolación («C · Final»…) no es parte de los playoffs.
  if (isConsolationLabel(match.round_label)) return false;
  // Jornadas ESTRUCTURALES v1/v2 (round_label "Jornada N" + notes de playoffs):
  // contrato COMPARTIDO con el backend (roundRobinPhases.isStructuralPlayoffMatch),
  // que sí reconoce el v1 legado ("Playoff semifinal:" / "Playoff ronda N:").
  if (isStructuralPlayoffMatch(match)) return true;
  // Rondas cuyo round_label YA es canónico (Semifinal/Cuartos/…): soporte de
  // eliminación/legado, fuera del predicado de "Jornada N".
  return match.group_id === null && legacyPlayoffRoundOrder(match.round_label) !== null;
}

function parseJornadaNumber(label: string | null | undefined): number | null {
  const match = (label ?? "").trim().match(/^(?:semana|jornada|matchups?|j)\s*#?\s*(\d+)/i);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function localDateIso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function parseLocalDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return new Date(year, month - 1, day);
}

function daysBetween(start: Date, end: Date): number {
  const startUtc = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.floor((endUtc - startUtc) / 86_400_000);
}

function currentRoundFromCalendar(
  startDate: string | null | undefined,
  durationDays: number | null | undefined,
  availableRounds: number[],
): number | null {
  if (availableRounds.length === 0) return null;
  const firstRound = Math.min(...availableRounds);
  const lastRound = Math.max(...availableRounds);
  const start = parseLocalDate(startDate);
  const duration = Math.max(1, Number(durationDays) || 7);
  if (!start) return firstRound;
  const today = parseLocalDate(localDateIso(new Date())) ?? new Date();
  const elapsedDays = daysBetween(start, today);
  if (elapsedDays < 0) return firstRound;
  const current = firstRound + Math.floor(elapsedDays / duration);
  return Math.min(Math.max(current, firstRound), lastRound);
}

function computeOperationalRoundNumber(rounds: TournamentRound[]): number | null {
  const latestPublished = [...rounds]
    .filter((round) => round.status === "published")
    .sort((a, b) => b.round_number - a.round_number)[0]?.round_number ?? null;
  if (latestPublished !== null) return latestPublished;

  const firstDraft = [...rounds]
    .filter((round) => round.status === "draft")
    .sort((a, b) => a.round_number - b.round_number)[0]?.round_number ?? null;
  if (firstDraft !== null) return firstDraft;

  return [...rounds].sort((a, b) => a.round_number - b.round_number)[0]?.round_number ?? null;
}

interface RoundResultSummary {
  total: number;
  published: number;
  pending: number;
}

function isPublishedResultStatus(status: string | null | undefined): boolean {
  return ["completed", "walkover", "no_show", "defaulted", "retired", "double_walkover"].includes(status ?? "");
}

function isTerminalEliminationMatch(match: TournamentMatch): boolean {
  return ["completed", "walkover", "no_show", "defaulted", "retired", "double_walkover", "cancelled", "bye"].includes(match.status);
}

function editFromRoundRobinMatch(match: TournamentMatch): RoundRobinEdit {
  return {
    roundNumber: String(parseJornadaNumber(match.round_label) ?? 1),
    groupId: match.group_id === null ? "" : String(match.group_id),
    player1: match.player1_participant_id === null ? "" : String(match.player1_participant_id),
    player2: match.player2_participant_id === null ? "" : String(match.player2_participant_id),
  };
}

function roundRobinEditMatches(edit: RoundRobinEdit, match: TournamentMatch): boolean {
  const current = editFromRoundRobinMatch(match);
  return (
    edit.roundNumber === current.roundNumber
    && edit.groupId === current.groupId
    && edit.player1 === current.player1
    && edit.player2 === current.player2
  );
}

function roundRobinEffectiveRoundNumber(
  match: TournamentMatch,
  edit: RoundRobinEdit | undefined,
): number | null {
  const parsed = edit ? Number(edit.roundNumber) : parseJornadaNumber(match.round_label);
  if (parsed === null || !Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

/** Respaldo del candado «la categoría ya empezó» si el backend no trae texto. */
const ARRANGE_LOCK_FALLBACK = "La categoría ya empezó: ya no se pueden mover jugadores ni cambiar el orden.";

function roundRobinPairKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

function ladderSemifinalPlaceholderName(
  match: TournamentMatch,
  roundNumber: number,
  regularRoundCount: number,
): string {
  const isSemifinalAfterPrelim = regularRoundCount > 0 && roundNumber === regularRoundCount + 2;
  if (!isSemifinalAfterPrelim || !isGeneratedPhasePlayoffMatch(match)) return "Por definir";
  if (match.match_number === 1) return "Peor sembrado";
  if (match.match_number === 2) return "Mejor sembrado";
  return "Por definir";
}

const ELIMINATION_ROUND_ORDER = new Map<string, number>([
  ["Preliminar", 0],
  ["Round of 128", 1],
  ["Round of 64", 2],
  ["Round of 32", 3],
  ["Round of 16", 4],
  ["Quarterfinals", 5],
  ["Semifinals", 6],
  ["Final", 7],
  ["3er Lugar", 8],
]);

interface EliminationVisualDrawSlot {
  key: string;
  roundLabel: string;
  slotNumber: number;
  match: TournamentMatch | null;
  sources: string[];
  directAdvancers: [TournamentParticipant | null, TournamentParticipant | null];
  gridRow: string;
  rowSpan: number;
}

interface EliminationVisualDrawRound {
  label: string;
  displayLabel: string;
  expectedMatchCount: number;
  slots: EliminationVisualDrawSlot[];
}

function eliminationPreliminaryDisplayLabel(nextRoundLabel?: string): string {
  switch (nextRoundLabel) {
    case "Round of 128":
      return "Ronda 256";
    case "Round of 64":
      return "Ronda 128";
    case "Round of 32":
      return "Ronda 64";
    case "Round of 16":
      return "Ronda 32";
    case "Quarterfinals":
      return "Ronda 16";
    case "Semifinals":
      return "Cuartos";
    case "Final":
      return "Semis";
    default:
      return "Preliminar";
  }
}

function eliminationRoundCompactDisplayLabel(label: string, nextRoundLabel?: string): string {
  switch (label) {
    case "Preliminar":
      return eliminationPreliminaryDisplayLabel(nextRoundLabel);
    case "Round of 128":
      return "128";
    case "Round of 64":
      return "64";
    case "Round of 32":
      return "32";
    case "Round of 16":
      return "Ronda 16";
    case "Quarterfinals":
      return "Cuartos";
    case "Semifinals":
      return "Semis";
    case "Final":
      return "Final";
    case "3er Lugar":
      return "3ro";
    default:
      return label;
  }
}

function eliminationRoundLabelForDrawSize(drawSize: number): string {
  if (drawSize >= 128) return "Round of 128";
  if (drawSize >= 64) return "Round of 64";
  if (drawSize >= 32) return "Round of 32";
  if (drawSize >= 16) return "Round of 16";
  if (drawSize >= 8) return "Quarterfinals";
  if (drawSize >= 4) return "Semifinals";
  return "Final";
}

function eliminationMainRoundLabelsForDrawSize(drawSize: number, roundCount?: number | null): string[] {
  const bracketSize = Math.max(drawSize, 2);
  const maxRoundCount = Math.max(roundCount ?? 0, Math.ceil(Math.log2(bracketSize)));
  const labels: string[] = [];
  for (let currentDrawSize = bracketSize; currentDrawSize >= 2 && labels.length < maxRoundCount; currentDrawSize = Math.max(currentDrawSize / 2, 1)) {
    labels.push(eliminationRoundLabelForDrawSize(currentDrawSize));
  }
  return labels.length > 0 ? labels : ["Final"];
}

function eliminationMainRoundLabelsForConfig(config: EliminationFormatConfig | null): string[] {
  const bracketSize = Math.max(config?.bracket_size ?? 0, 2);
  return eliminationMainRoundLabelsForDrawSize(bracketSize, config?.bracket_round_count);
}

function matchParticipantName(participant: MatchParticipantBrief | null, fallback: string): string {
  // short_name primero: en dobles el display_name es el team.name crudo y
  // truncado en el bracket no identifica a nadie (pedido Eduardo 2026-08-27).
  return participant?.short_name?.trim() || participant?.display_name || fallback;
}

interface MatchupRowProps {
  tournamentId: number;
  round: TournamentRound;
  matchup: TournamentRoundMatchup;
  groupParticipants: TournamentParticipant[];
  rankingScope: string | null | undefined;
  pendingChange: PendingChange;
  hasInvalidChange: boolean;
  willAutoDelete: boolean;
  onPlayerChange: (matchupId: number, side: "player1" | "player2", value: string) => void;
  onDeletePendingChange: (matchupId: number) => void;
  onChanged: () => Promise<void> | void;
  token: string;
  rowBusy: boolean;
  setRowBusy: (value: boolean) => void;
  pendingChangeCount: number;
}

function MatchupRow({
  tournamentId,
  round,
  matchup,
  groupParticipants,
  rankingScope,
  pendingChange,
  hasInvalidChange,
  willAutoDelete,
  onPlayerChange,
  onDeletePendingChange,
  onChanged,
  token,
  rowBusy,
  setRowBusy,
  pendingChangeCount,
}: MatchupRowProps) {
  const { askConfirm } = useAbortableConfirm([tournamentId, round.id]);
  const isLocked =
    round.status !== "draft" || matchup.status === "published";
  const editable = !isLocked;
  const [error, setError] = useState<string | null>(null);

  async function persist(
    payload: Parameters<typeof api.rounds.updateMatchup>[4],
  ) {
    setRowBusy(true);
    setError(null);
    try {
      await api.rounds.updateMatchup(
        token,
        tournamentId,
        round.id,
        matchup.id,
        payload,
      );
      await onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo guardar el cambio.");
      } else {
        setError("Error de red.");
      }
    } finally {
      setRowBusy(false);
    }
  }

  async function handleDelete() {
    if (!(await askConfirm("¿Eliminar este reto del borrador?", { acceptLabel: "Sí, eliminar reto", tone: "danger" }))) return;
    setRowBusy(true);
    setError(null);
    try {
      await api.rounds.deleteMatchup(
        token,
        tournamentId,
        round.id,
        matchup.id,
      );
      onDeletePendingChange(matchup.id);
      await onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo eliminar el reto.");
      } else {
        setError("Error de red.");
      }
    } finally {
      setRowBusy(false);
    }
  }

  async function handleMarkBye() {
    if (pendingChangeCount > 0) {
      setError(
        "Guarda o cancela los cambios pendientes antes de marcar BYE.",
      );
      return;
    }
    await persist({ clear_player2: true, status: "bye" });
  }

  async function handleResetToDraft() {
    if (pendingChangeCount > 0) {
      setError(
        "Guarda o cancela los cambios pendientes antes de reabrir.",
      );
      return;
    }
    await persist({ status: "draft" });
  }

  const challengerRank =
    matchup.challenger_group_rank ?? matchup.player1_group_rank ??
    matchup.player1_position;
  const challengedRank =
    matchup.challenged_group_rank ?? matchup.player2_group_rank ??
    matchup.player2_position;

  const groupRankMap = useMemo(
    () => buildGroupRankMap(groupParticipants, rankingScope),
    [groupParticipants, rankingScope],
  );

  const orderedGroupParticipants = useMemo(
    () => {
      const list = [...groupParticipants];
      list.sort((a, b) => {
        const aRank = participantGroupRankValue(a, groupRankMap);
        const bRank = participantGroupRankValue(b, groupRankMap);
        const aSort = aRank ?? Number.POSITIVE_INFINITY;
        const bSort = bRank ?? Number.POSITIVE_INFINITY;
        if (aSort !== bSort) return aSort - bSort;
        return participantDisplayName(a).localeCompare(participantDisplayName(b));
      });
      return list;
    },
    [groupParticipants, groupRankMap],
  );

  const player1Name =
    matchup.player1?.display_name ??
    (matchup.player1_participant_id !== null
      ? `#${matchup.player1_participant_id}`
      : "—");
  const player2Name =
    matchup.player2?.display_name ??
    (matchup.player2_participant_id !== null
      ? `#${matchup.player2_participant_id}`
      : matchup.status === "bye"
        ? "BYE"
        : "Sin rival");

  const isDirty = !changeMatchesMatchup(pendingChange, matchup);

  const rowClasses: string[] = [];
  if (hasInvalidChange) rowClasses.push("rounds-row--invalid");
  else if (willAutoDelete) rowClasses.push("rounds-row--will-delete");
  else if (isDirty) rowClasses.push("rounds-row--dirty");

  return (
    <tr className={rowClasses.join(" ") || undefined}>
      <td>
        <span className="kbd kbd--ok">
          {matchup.position_label ||
            (challengerRank !== null && challengedRank !== null
              ? `#${challengerRank} vs #${challengedRank}`
              : "—")}
        </span>
      </td>
      <td className="rounds-rank-cell">
        <span className="kbd">{formatRank(challengerRank)}</span>
      </td>
      <td>
        {editable ? (
          <select className="t123-select"
            value={pendingChange.player1}
            onChange={(e) =>
              onPlayerChange(matchup.id, "player1", e.target.value)
            }
            disabled={rowBusy}
          >
            <option value="">— vacío —</option>
            {orderedGroupParticipants.map((p) => (
              <option key={p.id} value={p.id}>
                {participantGroupRankLabel(p, groupRankMap)}
              </option>
            ))}
          </select>
        ) : (
          <span>{player1Name}</span>
        )}
      </td>
      <td className="rounds-rank-cell">
        <span className="kbd">{formatRank(challengedRank)}</span>
      </td>
      <td>
        {editable ? (
          <select className="t123-select"
            value={pendingChange.player2}
            onChange={(e) =>
              onPlayerChange(matchup.id, "player2", e.target.value)
            }
            disabled={rowBusy}
          >
            <option value="">— vacío / BYE —</option>
            {orderedGroupParticipants.map((p) => (
              <option key={p.id} value={p.id}>
                {participantGroupRankLabel(p, groupRankMap)}
              </option>
            ))}
          </select>
        ) : (
          <span>{player2Name}</span>
        )}
      </td>
      <td>
        <span
          className={`kbd ${matchup.status === "published" ? "kbd--ok" : ""}`}
        >
          {statusLabel(matchup.status)}
        </span>
        {willAutoDelete && (
          <p className="muted rounds-row__hint">
            Vacío — se eliminará al guardar.
          </p>
        )}
      </td>
      <td>
        {!isLocked && matchup.status !== "bye" && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => void handleMarkBye()}
            disabled={rowBusy || pendingChangeCount > 0}
            title="Marcar como BYE"
          >
            BYE
          </button>
        )}
        {!isLocked && matchup.status === "bye" && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => void handleResetToDraft()}
            disabled={rowBusy || pendingChangeCount > 0}
          >
            Reabrir
          </button>
        )}
        {!isLocked && (
          <button
            type="button"
            className="btn btn--ghost btn--danger"
            onClick={() => void handleDelete()}
            disabled={rowBusy}
            title="Quitar este reto del borrador"
          >
            Eliminar
          </button>
        )}
        {error && <p className="form-error">{error}</p>}
      </td>
    </tr>
  );
}

interface PlayoffDraftBracketProps {
  matchups: TournamentRoundMatchup[];
  participants: TournamentParticipant[];
  pendingChanges: PendingChanges;
  invalidMatchupIds: Set<number>;
  onPlayerChange: (matchupId: number, side: "player1" | "player2", value: string) => void;
  rowBusy: boolean;
  isLocked: boolean;
  tournamentName: string;
  divisionName: string;
  onPrintError: (message: string) => void;
}

export function PlayoffDraftBracket({
  matchups,
  participants,
  pendingChanges,
  invalidMatchupIds,
  onPlayerChange,
  rowBusy,
  isLocked,
  tournamentName,
  divisionName,
  onPrintError,
}: PlayoffDraftBracketProps) {
  const bracketRef = useRef<HTMLElement | null>(null);
  const orderedParticipants = useMemo(() => {
    const list = [...participants];
    list.sort((a, b) => {
      const aRank = a.current_rank ?? a.initial_rank ?? Number.POSITIVE_INFINITY;
      const bRank = b.current_rank ?? b.initial_rank ?? Number.POSITIVE_INFINITY;
      if (aRank !== bRank) return aRank - bRank;
      return participantDisplayName(a).localeCompare(participantDisplayName(b));
    });
    return list;
  }, [participants]);

  const participantById = useMemo(() => {
    const map = new Map<number, TournamentParticipant>();
    for (const participant of participants) map.set(participant.id, participant);
    return map;
  }, [participants]);

  const sortedMatchups = useMemo(
    () => [...matchups].sort((a, b) => a.sort_order - b.sort_order),
    [matchups],
  );

  const playerLabel = (participantId: number | null, fallback?: string) => {
    if (participantId === null) return fallback ?? "Por definir";
    const participant = participantById.get(participantId);
    if (participant) return participantGroupRankLabel(participant, new Map());
    const matchupName = matchups.find((m) => m.player1_participant_id === participantId)?.player1?.display_name
      ?? matchups.find((m) => m.player2_participant_id === participantId)?.player2?.display_name;
    return matchupName ?? `#${participantId}`;
  };

  function printEditableBracket() {
    const node = bracketRef.current;
    if (!node) {
      onPrintError("No encontré el bracket editable para imprimir. Abre la jornada de playoffs e inténtalo de nuevo.");
      return;
    }
    const printWindow = window.open("", "_blank", "width=1280,height=900");
    if (!printWindow) {
      onPrintError("El navegador bloqueó la ventana de impresión. Permite pop-ups para Torneos123 y vuelve a intentar.");
      return;
    }
    const styles = collectAppStyleMarkup();
    const generatedAt = new Intl.DateTimeFormat("es-MX", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date());
    printWindow.document.write(`<!doctype html>
<html data-theme="light">
<head>
  <meta charset="utf-8" />
  <title>Bracket Playoffs · ${tournamentName} · ${divisionName}</title>
  ${styles}
  <style>
    body.ladder-playoff-editable-print-body { margin: 0; padding: 16px; color: #10220f; background: #ffffff; }
    .ladder-playoff-editable-print-sheet { box-sizing: border-box; width: 100%; max-width: 1180px; margin: 0 auto; padding: 18px; background: #ffffff; }
    .ladder-playoff-editable-print-hero { display: grid; grid-template-columns: 190px minmax(0, 1fr) auto; gap: 16px; align-items: center; margin-bottom: 16px; padding: 14px; border: 1px solid #d8e7cf; border-radius: 16px; }
    .ladder-playoff-editable-print-hero__brand { padding: 8px; border-radius: 12px; background: #ffffff; }
    .ladder-playoff-editable-print-hero__brand img { display: block; width: 100%; max-height: 72px; object-fit: contain; }
    .ladder-playoff-editable-print-frame .ladder-playoff-editable-bracket { box-shadow: none; background: #ffffff; border-color: #d8e7cf; }
    .ladder-playoff-editable-print-frame .ladder-playoff-editable-bracket__header button { display: none; }
    .ladder-playoff-editable-print-frame select { appearance: none; border: 0; background: transparent; padding: 0; font: inherit; color: #10220f; }
    .ladder-playoff-editable-print-frame select::-ms-expand { display: none; }
    @media print { .elimination-print-toolbar { display: none !important; } body.ladder-playoff-editable-print-body { padding: 0; } }
  </style>
</head>
<body class="ladder-playoff-editable-print-body">
  <div class="elimination-print-toolbar ladder-playoff-editable-print-toolbar">
    <button type="button" onclick="window.close()">Regresar</button>
    <button type="button" onclick="window.print()">Imprimir / PDF</button>
  </div>
  <main class="ladder-playoff-editable-print-sheet">
    <header class="ladder-playoff-editable-print-hero">
      <div class="ladder-playoff-editable-print-hero__brand">
        <img src="/tenis/torneos123/logo-torneos123-header-on-light.png" alt="Torneos123" />
      </div>
      <div>
        <p>Bracket de playoffs editable</p>
        <h1>${tournamentName}</h1>
        <strong>${divisionName}</strong>
      </div>
      <small>Generado ${generatedAt}</small>
    </header>
    <section class="ladder-playoff-editable-print-frame">
      ${node.outerHTML}
    </section>
  </main>
  <script>
    async function waitForPrintAssets() {
      const styleSheets = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map((link) => new Promise((resolve) => {
        if (link.sheet) return resolve(true);
        link.addEventListener('load', () => resolve(true), { once: true });
        link.addEventListener('error', () => resolve(false), { once: true });
        setTimeout(() => resolve(false), 1800);
      }));
      const images = Array.from(document.images).map((img) => img.complete ? Promise.resolve(true) : new Promise((resolve) => {
        img.addEventListener('load', () => resolve(true), { once: true });
        img.addEventListener('error', () => resolve(false), { once: true });
        setTimeout(() => resolve(false), 1800);
      }));
      await Promise.all([...styleSheets, ...images]);
      if (document.fonts?.ready) await document.fonts.ready;
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    }
    window.addEventListener('load', () => waitForPrintAssets().then(() => setTimeout(() => window.print(), 250)));
  <\/script>
</body>
</html>`);
    printWindow.document.close();
  }

  return (
    <section ref={bracketRef} className="ladder-playoff-editable-bracket" aria-label="Bracket editable de playoffs">
      <div className="ladder-playoff-editable-bracket__header">
        <div>
          <span className="ladder-playoff-editable-bracket__eyebrow">Bracket de playoffs</span>
        </div>
        <div className="table-actions">
          <span className="kbd kbd--ok">{sortedMatchups.length} partido{sortedMatchups.length === 1 ? "" : "s"}</span>
          <button type="button" className="btn btn--small btn--ghost" onClick={printEditableBracket}>
            PDF / imprimir
          </button>
        </div>
      </div>

      <div className="ladder-playoff-editable-bracket__grid">
        {sortedMatchups.map((matchup, index) => {
          const pendingChange = pendingChanges[matchup.id] ?? changeFromMatchup(matchup);
          const player1Seed = matchup.player1_position ?? index + 1;
          const player2Seed = matchup.player2_position ?? sortedMatchups.length * 2 - index;
          const visualSides = bracketVisualSideOrder(player1Seed).map((visualSide) => {
            const side: "player1" | "player2" = visualSide === "top" ? "player1" : "player2";
            return side === "player1"
              ? {
                  side,
                  seed: player1Seed,
                  participantId: matchup.player1_participant_id,
                  value: pendingChange.player1,
                  emptyLabel: "Por definir",
                  lockedFallback: undefined,
                }
              : {
                  side,
                  seed: player2Seed,
                  participantId: matchup.player2_participant_id,
                  value: pendingChange.player2,
                  emptyLabel: "Por definir / BYE",
                  lockedFallback: matchup.status === "bye" ? "BYE" : "Por definir",
                };
          });
          const isInvalid = invalidMatchupIds.has(matchup.id);
          return (
            <article
              key={matchup.id}
              className={`ladder-playoff-editable-card${isInvalid ? " is-invalid" : ""}`}
            >
              <div className="ladder-playoff-editable-card__meta">
                <span>Partido {index + 1}</span>
                <strong>Sembrado {visualSides[0].seed} vs {visualSides[1].seed}</strong>
              </div>
              {visualSides.map((visualSide) => (
                <label key={visualSide.side} className="ladder-playoff-editable-card__player">
                  <span>Sembrado {visualSide.seed}</span>
                  {isLocked ? (
                    <strong>{playerLabel(visualSide.participantId, visualSide.lockedFallback)}</strong>
                  ) : (
                    <select className="t123-select"
                      value={visualSide.value}
                      onChange={(event) => onPlayerChange(matchup.id, visualSide.side, event.target.value)}
                      disabled={rowBusy}
                    >
                      <option value="">{visualSide.emptyLabel}</option>
                      {orderedParticipants.map((participant) => (
                        <option key={participant.id} value={participant.id}>
                          {participantGroupRankLabel(participant, new Map())}
                        </option>
                      ))}
                    </select>
                  )}
                </label>
              ))}
              <small className="ladder-playoff-editable-card__status">{statusLabel(matchup.status)}</small>
            </article>
          );
        })}
      </div>
    </section>
  );
}

interface GroupAccordionProps {
  tournamentId: number;
  round: TournamentRound;
  group: TournamentGroup;
  matchups: TournamentRoundMatchup[];
  groupParticipants: TournamentParticipant[];
  rankingScope: string | null | undefined;
  pendingChanges: PendingChanges;
  invalidMatchupIds: Set<number>;
  emptyDraftMatchupIds: Set<number>;
  onPlayerChange: (matchupId: number, side: "player1" | "player2", value: string) => void;
  onDeletePendingChange: (matchupId: number) => void;
  onAddMatchup: (groupId: number) => Promise<void>;
  onChanged: () => Promise<void> | void;
  token: string;
  rowBusy: boolean;
  setRowBusy: (value: boolean) => void;
  pendingChangeCount: number;
  addingGroupId: number | null;
  defaultOpen: boolean;
  isLocked: boolean;
}

function GroupAccordion({
  tournamentId,
  round,
  group,
  matchups,
  groupParticipants,
  rankingScope,
  pendingChanges,
  invalidMatchupIds,
  emptyDraftMatchupIds,
  onPlayerChange,
  onDeletePendingChange,
  onAddMatchup,
  onChanged,
  token,
  rowBusy,
  setRowBusy,
  pendingChangeCount,
  addingGroupId,
  defaultOpen,
  isLocked,
}: GroupAccordionProps) {
  const publishedCount = matchups.filter((m) => m.status === "published").length;
  const byeCount = matchups.filter((m) => m.status === "bye").length;
  const summaryBits: string[] = [
    `${matchups.length} reto${matchups.length === 1 ? "" : "s"}`,
  ];
  if (publishedCount > 0)
    summaryBits.push(`${publishedCount} publicado${publishedCount === 1 ? "" : "s"}`);
  if (byeCount > 0) summaryBits.push(`${byeCount} BYE`);
  const isAddingThis = addingGroupId === group.id;

  return (
    <details className="rounds-group" open={defaultOpen}>
      <summary className="rounds-group__summary">
        <span className="rounds-group__title">{group.name}</span>
        <span className="rounds-group__meta">{summaryBits.join(" · ")}</span>
      </summary>
      {matchups.length === 0 ? (
        <p className="muted">Sin retos en este grupo.</p>
      ) : (
        <div className="weekly-patterns__table-wrap">
          <table className="weekly-patterns__table rounds-matchup-table">
            <thead>
              <tr>
                <th>Reto</th>
                <th>Rank</th>
                <th>Retador</th>
                <th>Rank</th>
                <th>Retado</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {matchups.map((m) => (
                <MatchupRow
                  key={m.id}
                  tournamentId={tournamentId}
                  round={round}
                  matchup={m}
                  groupParticipants={groupParticipants}
                  rankingScope={rankingScope}
                  pendingChange={
                    pendingChanges[m.id] ?? changeFromMatchup(m)
                  }
                  hasInvalidChange={invalidMatchupIds.has(m.id)}
                  willAutoDelete={emptyDraftMatchupIds.has(m.id)}
                  onPlayerChange={onPlayerChange}
                  onDeletePendingChange={onDeletePendingChange}
                  onChanged={onChanged}
                  token={token}
                  rowBusy={rowBusy}
                  setRowBusy={setRowBusy}
                  pendingChangeCount={pendingChangeCount}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!isLocked && (
        <div className="form__actions rounds-group__actions">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => void onAddMatchup(group.id)}
            disabled={rowBusy || addingGroupId !== null}
            title="Agrega un reto vacío para asignar jugadores manualmente."
          >
            {isAddingThis ? "Agregando…" : "+ Agregar reto"}
          </button>
        </div>
      )}
    </details>
  );
}

interface SelectedRoundDetailsProps {
  tournamentId: number;
  round: TournamentRound;
  groups: TournamentGroup[];
  groupParticipants: Record<number, TournamentParticipant[]>;
  participants: TournamentParticipant[];
  rankingScope: string | null | undefined;
  tournamentName: string;
  divisionName: string;
  onChanged: () => Promise<void> | void;
  onPublished?: () => void;
  token: string;
}

function validatePendingState(
  round: TournamentRound,
  pendingChanges: PendingChanges,
  participantNameById: Map<number, string>,
): {
  errors: MatchupValidationError[];
  invalidMatchupIds: Set<number>;
  emptyDraftMatchupIds: Set<number>;
} {
  const errors: MatchupValidationError[] = [];
  const invalidIds = new Set<number>();
  const emptyDraftIds = new Set<number>();

  type Resolved = {
    matchupId: number;
    groupId: number | null;
    p1: number | null;
    p2: number | null;
    status: string;
    matchId: number | null;
    locked: boolean;
  };
  const resolved: Resolved[] = round.matchups.map((m) => {
    const change = pendingChanges[m.id];
    const p1 = change
      ? change.player1 === ""
        ? null
        : Number(change.player1)
      : m.player1_participant_id;
    const p2 = change
      ? change.player2 === ""
        ? null
        : Number(change.player2)
      : m.player2_participant_id;
    return {
      matchupId: m.id,
      groupId: m.group_id,
      p1,
      p2,
      status: m.status,
      matchId: m.match_id,
      locked: m.status === "published",
    };
  });

  const nameOf = (pid: number) => participantNameById.get(pid) ?? `#${pid}`;

  for (const row of resolved) {
    if (row.p1 !== null && row.p2 !== null && row.p1 === row.p2) {
      errors.push({
        type: "same_player",
        matchup_id: row.matchupId,
        participant_id: row.p1,
        message: `${nameOf(row.p1)} aparece contra sí mismo.`,
      });
      invalidIds.add(row.matchupId);
    }
  }

  // Track empty draft rows so the UI can hint that they will be removed on
  // save; a non-draft (or match-linked) empty row is a hard error because
  // we never auto-delete those server-side.
  for (const row of resolved) {
    if (row.p1 === null && row.p2 === null) {
      const safeAutoDelete =
        row.status === "draft" && row.matchId === null;
      if (safeAutoDelete) {
        emptyDraftIds.add(row.matchupId);
      } else if (!row.locked) {
        errors.push({
          type: "empty",
          matchup_id: row.matchupId,
          message:
            "Este reto está vacío y no se puede eliminar automáticamente.",
        });
        invalidIds.add(row.matchupId);
      }
    }
  }

  const occurrences = new Map<string, number[]>();
  for (const row of resolved) {
    for (const pid of [row.p1, row.p2]) {
      if (pid === null) continue;
      const key = `${row.groupId ?? "null"}::${pid}`;
      const list = occurrences.get(key) ?? [];
      list.push(row.matchupId);
      occurrences.set(key, list);
    }
  }
  for (const [key, matchupIds] of occurrences) {
    if (matchupIds.length > 1) {
      const pid = Number(key.split("::")[1]);
      errors.push({
        type: "duplicate",
        participant_id: pid,
        matchup_ids: [...matchupIds].sort((a, b) => a - b),
        message: `${nameOf(pid)} está repetido en ${matchupIds.length} retos.`,
      });
      for (const mid of matchupIds) invalidIds.add(mid);
    }
  }

  return { errors, invalidMatchupIds: invalidIds, emptyDraftMatchupIds: emptyDraftIds };
}

function SelectedRoundDetails({
  tournamentId,
  round,
  groups,
  groupParticipants,
  participants,
  rankingScope,
  onChanged,
  onPublished,
  token,
  tournamentName,
  divisionName,
}: SelectedRoundDetailsProps) {
  const { askConfirm } = useAbortableConfirm([tournamentId, round.id]);
  const [savingBulk, setSavingBulk] = useState(false);
  const [rowBusy, setRowBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [addingGroupId, setAddingGroupId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({});
  const [validationErrors, setValidationErrors] = useState<
    MatchupValidationError[]
  >([]);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  // Reset pending changes when selecting a different jornada. Server refreshes
  // after row deletes should not wipe unsaved edits on other rows.
  useEffect(() => {
    setPendingChanges({});
    setValidationErrors([]);
    setHasAttemptedSave(false);
    setError(null);
  }, [round.id]);

  const matchupsByGroup = useMemo(() => {
    const map = new Map<number | null, TournamentRoundMatchup[]>();
    for (const m of round.matchups) {
      const list = map.get(m.group_id) || [];
      list.push(m);
      map.set(m.group_id, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.sort_order - b.sort_order);
    }
    return map;
  }, [round.matchups]);

  const firstGroupWithMatchups = useMemo(() => {
    if ((matchupsByGroup.get(null) || []).length > 0) return null;
    for (const g of groups) {
      const list = matchupsByGroup.get(g.id) || [];
      if (list.length > 0) return g.id;
    }
    return groups[0]?.id ?? null;
  }, [groups, matchupsByGroup]);

  const ungroupedMatchups = matchupsByGroup.get(null) || [];

  const participantNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of participants) {
      map.set(p.id, participantDisplayName(p));
    }
    for (const list of Object.values(groupParticipants)) {
      for (const p of list) {
        map.set(p.id, participantDisplayName(p));
      }
    }
    for (const matchup of round.matchups) {
      if (matchup.player1_participant_id !== null && matchup.player1?.display_name) {
        map.set(matchup.player1_participant_id, matchup.player1.display_name);
      }
      if (matchup.player2_participant_id !== null && matchup.player2?.display_name) {
        map.set(matchup.player2_participant_id, matchup.player2.display_name);
      }
    }
    return map;
  }, [groupParticipants, participants, round.matchups]);

  // Build the list of pending changes that actually differ from the server
  // matchup state. A user can pick a value, then change it back — that does
  // not count as a pending change.
  const dirtyMatchupIds = useMemo(() => {
    const dirty: number[] = [];
    for (const m of round.matchups) {
      const change = pendingChanges[m.id];
      if (change && !changeMatchesMatchup(change, m)) dirty.push(m.id);
    }
    return dirty;
  }, [pendingChanges, round.matchups]);

  const pendingChangeCount = dirtyMatchupIds.length;

  // Live validation for inline row marking (only after the user has tried
  // to save, to avoid yelling at empty drafts on first load).
  const liveValidation = useMemo(
    () => validatePendingState(round, pendingChanges, participantNameById),
    [round, pendingChanges, participantNameById],
  );

  const invalidMatchupIds = hasAttemptedSave
    ? liveValidation.invalidMatchupIds
    : new Set<number>();

  const emptyDraftMatchupIds = liveValidation.emptyDraftMatchupIds;
  const emptyDraftCount = emptyDraftMatchupIds.size;

  function handlePlayerChange(
    matchupId: number,
    side: "player1" | "player2",
    value: string,
  ) {
    setError(null);
    setMessage(null);
    setPendingChanges((prev) => {
      const matchup = round.matchups.find((m) => m.id === matchupId);
      if (!matchup) return prev;
      const current = prev[matchupId] ?? changeFromMatchup(matchup);
      const next: PendingChange = { ...current, [side]: value };
      // If the new state matches the server state exactly, drop the entry.
      if (changeMatchesMatchup(next, matchup)) {
        const { [matchupId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [matchupId]: next };
    });
  }

  function handleDeletePendingChange(matchupId: number) {
    setPendingChanges((prev) => {
      if (!(matchupId in prev)) return prev;
      const { [matchupId]: _, ...rest } = prev;
      return rest;
    });
    setValidationErrors((prev) =>
      prev.filter(
        (err) =>
          err.matchup_id !== matchupId &&
          !(err.matchup_ids || []).includes(matchupId),
      ),
    );
  }

  function handleResetPending() {
    setPendingChanges({});
    setValidationErrors([]);
    setHasAttemptedSave(false);
    setError(null);
    setMessage(null);
  }

  function applyServerErrors(details: Record<string, unknown> | undefined) {
    if (!details) return false;
    const raw = (details as { errors?: unknown }).errors;
    if (!Array.isArray(raw)) return false;
    const parsed: MatchupValidationError[] = raw
      .filter((entry): entry is Record<string, unknown> =>
        typeof entry === "object" && entry !== null,
      )
      .map((entry) => ({
        type: (entry.type as string) ?? "unknown",
        message:
          typeof entry.message === "string" ? entry.message : undefined,
        matchup_id:
          typeof entry.matchup_id === "number" ? entry.matchup_id : undefined,
        matchup_ids: Array.isArray(entry.matchup_ids)
          ? (entry.matchup_ids.filter((v) => typeof v === "number") as number[])
          : undefined,
        participant_id:
          typeof entry.participant_id === "number"
            ? entry.participant_id
            : undefined,
        count: typeof entry.count === "number" ? entry.count : undefined,
      }));
    if (parsed.length === 0) return false;
    setValidationErrors(parsed);
    setHasAttemptedSave(true);
    return true;
  }

  async function handleBulkSave() {
    if (pendingChangeCount === 0 && emptyDraftCount === 0) return;
    setHasAttemptedSave(true);

    const { errors } = liveValidation;
    if (errors.length > 0) {
      setValidationErrors(errors);
      setError("Revisa los errores antes de guardar.");
      setMessage(null);
      return;
    }

    const items: MatchupBulkUpdateItem[] = dirtyMatchupIds.map((mid) => {
      const change = pendingChanges[mid]!;
      const item: MatchupBulkUpdateItem = { matchup_id: mid };
      if (change.player1 === "") item.clear_player1 = true;
      else item.player1_participant_id = Number(change.player1);
      if (change.player2 === "") item.clear_player2 = true;
      else item.player2_participant_id = Number(change.player2);
      return item;
    });

    setSavingBulk(true);
    setError(null);
    setMessage(null);
    setValidationErrors([]);
    try {
      const resp = await api.rounds.bulkUpdateMatchups(
        token,
        tournamentId,
        round.id,
        { matchups: items },
      );
      const updated = resp.data.updated_count;
      const deleted = resp.data.deleted_matchup_ids.length;
      const parts: string[] = [];
      if (updated > 0) {
        parts.push(`Se guardaron ${updated} cambio${updated === 1 ? "" : "s"}`);
      }
      if (deleted > 0) {
        parts.push(
          `Se eliminaron ${deleted} reto${deleted === 1 ? "" : "s"} vacío${
            deleted === 1 ? "" : "s"
          }`,
        );
      }
      setMessage(parts.length > 0 ? `${parts.join(" · ")}.` : "Sin cambios.");
      setPendingChanges({});
      setHasAttemptedSave(false);
      await onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        const surfaced = applyServerErrors(err.details);
        if (!surfaced) {
          setError(err.message || "No se pudieron guardar los cambios.");
        } else {
          setError(err.message || "No se puede guardar el matchup.");
        }
      } else {
        setError("Error de red.");
      }
    } finally {
      setSavingBulk(false);
    }
  }

  async function handleAddMatchup(groupId: number) {
    if (round.status !== "draft") {
      setError("Solo se pueden agregar retos en enfrentamientos en borrador.");
      return;
    }
    setAddingGroupId(groupId);
    setError(null);
    setMessage(null);
    try {
      await api.rounds.createMatchup(token, tournamentId, round.id, {
        group_id: groupId,
      });
      setMessage(
        "Reto vacío agregado. Asigna jugadores y guarda; si lo dejas vacío se eliminará al guardar.",
      );
      await onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo agregar el reto.");
      } else {
        setError("Error de red.");
      }
    } finally {
      setAddingGroupId(null);
    }
  }

  async function handlePublish() {
    if (pendingChangeCount > 0) {
      setError(
        "Tienes cambios sin guardar. Guarda o cancela antes de publicar.",
      );
      setMessage(null);
      return;
    }
    setPublishing(true);
    setError(null);
    setMessage(null);
    try {
      const resp = await api.rounds.publish(token, tournamentId, round.id);
      const created = resp.data.created_match_ids.length;
      setMessage(
        resp.data.already_published
          ? "Enfrentamiento ya estaba publicado."
          : `Enfrentamiento publicado. ${created} partido(s) creado(s) en Calendario.`,
      );
      if (onPublished) {
        onPublished();
        return;
      }
      await onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        const surfaced = applyServerErrors(err.details);
        if (!surfaced) {
          setError(err.message || "No se pudo publicar el matchup.");
        } else {
          setError(err.message || "No se puede publicar con retos inválidos.");
        }
      } else {
        setError("Error de red.");
      }
    } finally {
      setPublishing(false);
    }
  }

  async function handleBackToDraft() {
    if (
      !(await askConfirm(
        "¿Regresar este enfrentamiento a borrador? Los partidos ya creados en Calendario no se eliminarán.", { acceptLabel: "Sí, regresar a borrador" }))
    ) {
      return;
    }
    setReverting(true);
    setError(null);
    setMessage(null);
    try {
      const resp = await api.rounds.backToDraft(token, tournamentId, round.id);
      setMessage(
        resp.data.already_draft
          ? "El enfrentamiento ya estaba en borrador."
          : "Enfrentamiento regresado a borrador. Los partidos en Calendario siguen ahí.",
      );
      await onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo regresar el enfrentamiento a borrador.");
      } else {
        setError("Error de red.");
      }
    } finally {
      setReverting(false);
    }
  }

  const draftHasEditableMatchups =
    round.status === "draft"
    && round.matchups.some((m) => m.status !== "published");

  const hasSavableWork = pendingChangeCount > 0 || emptyDraftCount > 0;
  const saveLabel = savingBulk
    ? "Guardando…"
    : !hasSavableWork
      ? "Guardar cambios"
      : pendingChangeCount > 0
        ? `Guardar ${pendingChangeCount} cambio${pendingChangeCount === 1 ? "" : "s"}`
        : `Limpiar ${emptyDraftCount} reto${emptyDraftCount === 1 ? "" : "s"} vacío${emptyDraftCount === 1 ? "" : "s"}`;

  return (
    <section className="card drw-numbered">
      <div className="card__header-row">
        <div>
          <h2 className="card__title">{displayWeekName(round.name)}</h2>
          {/* Detail metadata hidden on mobile/admin view; the selector already carries status context. */}
        </div>
        <div className="form__actions rounds-toolbar">
          {round.status === "draft" && draftHasEditableMatchups && (
            <>
              {pendingChangeCount > 0 && (
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={handleResetPending}
                  disabled={savingBulk || rowBusy}
                  title="Descartar los cambios sin guardar"
                >
                  Cancelar
                </button>
              )}
              <button
                type="button"
                className="btn"
                onClick={() => void handleBulkSave()}
                disabled={savingBulk || rowBusy || !hasSavableWork}
                title={
                  !hasSavableWork
                    ? "No hay cambios pendientes"
                    : pendingChangeCount > 0
                      ? "Guardar todos los cambios pendientes"
                      : "Limpiar los retos vacíos del borrador"
                }
              >
                {saveLabel}
              </button>
            </>
          )}
          {round.status === "draft" && (
            <button
              type="button"
              className="btn"
              onClick={() => void handlePublish()}
              disabled={publishing || savingBulk || rowBusy || pendingChangeCount > 0}
              title={
                pendingChangeCount > 0
                  ? "Guarda o cancela los cambios pendientes antes de publicar"
                  : "Publicar el enfrentamiento al Calendario"
              }
            >
              {publishing ? "Publicando…" : "Publicar a Calendario"}
            </button>
          )}
          {round.status === "published" && (
            <>
              <Link
                className="btn btn--ghost"
                to={`/admin/tournaments/${tournamentId}/schedule`}
              >
                Ver en Calendario
              </Link>
              <span className="hint-label">
                <button
                  type="button"
                  className="btn btn--ghost btn--danger"
                  onClick={() => void handleBackToDraft()}
                  disabled={reverting || savingBulk}
                  title="Desbloquear este enfrentamiento"
                >
                  {reverting ? "Regresando…" : "Volver a borrador"}
                </button>
                <FieldHint label="¿Qué hace volver a borrador?">
                  No elimina partidos ni resultados ya existentes en Calendario.
                </FieldHint>
              </span>
            </>
          )}
        </div>
      </div>

      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
      {message && <p className="form-message">{message}</p>}

      {validationErrors.length > 0 && (
        <div role="alert" className="rounds-validation">
          <p className="form-error" style={{ marginBottom: "0.25rem" }}>
            No se puede guardar:
          </p>
          <ul className="list list--compact rounds-validation__list">
            {validationErrors.map((err, idx) => (
              <li key={`${err.type}-${idx}`}>{err.message ?? err.type}</li>
            ))}
          </ul>
        </div>
      )}

      {groups.length === 0 && (
        <p className="muted">
          {ungroupedMatchups.length > 0
            ? "Estos cruces no pertenecen a un grupo porque fueron generados como playoffs desde el ranking final."
            : "Crea grupos y asigna jugadores para que aparezcan retos en este matchup."}
        </p>
      )}

      {ungroupedMatchups.length > 0 && (
        <PlayoffDraftBracket
          matchups={ungroupedMatchups}
          participants={participants}
          pendingChanges={pendingChanges}
          invalidMatchupIds={invalidMatchupIds}
          onPlayerChange={handlePlayerChange}
          rowBusy={rowBusy || savingBulk}
          isLocked={round.status !== "draft"}
          tournamentName={tournamentName}
          divisionName={divisionName}
          onPrintError={setError}
        />
      )}

      {ungroupedMatchups.length === 0 && groups.map((group) => {
        const matchups = matchupsByGroup.get(group.id) || [];
        return (
          <GroupAccordion
            key={group.id}
            tournamentId={tournamentId}
            round={round}
            group={group}
            matchups={matchups}
            groupParticipants={groupParticipants[group.id] || []}
            rankingScope={rankingScope}
            pendingChanges={pendingChanges}
            invalidMatchupIds={invalidMatchupIds}
            emptyDraftMatchupIds={emptyDraftMatchupIds}
            onPlayerChange={handlePlayerChange}
            onDeletePendingChange={handleDeletePendingChange}
            onAddMatchup={handleAddMatchup}
            onChanged={onChanged}
            token={token}
            rowBusy={rowBusy || savingBulk}
            setRowBusy={setRowBusy}
            pendingChangeCount={pendingChangeCount}
            addingGroupId={addingGroupId}
            defaultOpen={group.id === firstGroupWithMatchups}
            isLocked={round.status !== "draft"}
          />
        );
      })}
    </section>
  );
}

interface AdminTournamentRoundsPageProps {
  setupMode?: boolean;
}

export default function AdminTournamentRoundsPage({
  setupMode: forcedSetupMode = false,
}: AdminTournamentRoundsPageProps = {}) {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const setupMode = forcedSetupMode || searchParams.get("setup") === "1";
  // Draw simple (Eduardo 2026-09-11): "Rearmar cuadro" es un solo botón con
  // confirmación; "Generar siguiente ronda" vive en "Más opciones".
  const [rearmMenuOpen, setRearmMenuOpen] = useState(false);

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [formatConfig, setFormatConfig] =
    useState<TournamentFormatConfig | null>(null);
  const [rounds, setRounds] = useState<TournamentRound[]>([]);
  const [tournamentMatches, setTournamentMatches] = useState<TournamentMatch[]>([]);
  // Eduardo 2026-10-05: fechas guardadas y descansos de cada jornada para la
  // hoja del Round Robin (PDF). Si falla, la hoja sale como antes.
  const [jornadaOverview, setJornadaOverview] = useState<{
    tournamentId: number;
    data: JornadaOverviewResponse["data"];
  } | null>(null);
  // Dueño de tournamentMatches, estampado AL RESOLVER el fetch (8º gate de
  // #135): la tarjeta del sorteo solo combina ceremonia+partidos cuando
  // ambos pertenecen al mismo scope (sesión+torneo+división) — sin esto, en
  // el frame transicional A→B podía imprimirse un acta mixta.
  const [tournamentMatchesOwner, setTournamentMatchesOwner] = useState<{
    token: string;
    tournamentId: number;
    divisionId: number | null;
  } | null>(null);
  // Lifecycle del productor (lib/matchesOwnerLifecycle, 10º-11º gates de
  // #135): guard latest-only + estampado del dueño en una sola pieza
  // regresionada. invalidate() CADUCA cualquier load() en vuelo y cierra el
  // snapshot (owner=null) EN EL MISMO ACTO — la tarjeta del sorteo oculta
  // cuadro e impresión sin esperar al fingerprint; solo un load fresco
  // re-estampa. Toda mutación del cuadro (regenerar, swap, editar matchup)
  // invalida ANTES de tocar la API y recarga en TODOS los caminos.
  // Instancia VIVA por lifetime real del effect (21º gate, StrictMode): el
  // probe setup→cleanup→setup de desarrollo dispone la instancia del primer
  // setup y el segundo CREA UNA NUEVA — dispose sigue irreversible para
  // cada instancia (unmount real intacto). Nada se crea ni muta durante el
  // render: los handlers leen la ref al ejecutarse.
  const matchesLifecycleRef = useRef<MatchesOwnerLifecycle | null>(null);
  useEffect(() => {
    const lifecycle = makeMatchesOwnerLifecycle(setTournamentMatchesOwner);
    matchesLifecycleRef.current = lifecycle;
    return () => lifecycle.dispose();
  }, []);
  // Los callers corren solo montados (handlers/effects declarados DESPUÉS
  // de este effect). Fallback fail-closed: instancia inerte ya dispuesta —
  // jamás estampa ni escribe.
  const getMatchesLifecycle = (): MatchesOwnerLifecycle => {
    if (matchesLifecycleRef.current === null) {
      const inerte = makeMatchesOwnerLifecycle(() => undefined);
      inerte.dispose();
      matchesLifecycleRef.current = inerte;
    }
    return matchesLifecycleRef.current;
  };
  // Toda mutación del cuadro abre/cierra su mutación en el lifecycle: abrir
  // caduca loads y cierra el snapshot; mientras haya UNA abierta, ningún
  // load re-estampa (12º gate: mutaciones solapadas). El reload de cada
  // camino va en el finally, DESPUÉS de cerrar la mutación.
  // (El dispose del unmount vive en el cleanup del effect de creación de la
  // instancia — 13º gate intacto, ahora por-instancia.)
  const [roundRobinMatches, setRoundRobinMatches] = useState<TournamentMatch[]>([]);
  const [qualificationPreview, setQualificationPreview] = useState<RoundRobinQualificationPreviewPayload | null>(null);
  const [seedOverrideDrafts, setSeedOverrideDrafts] = useState<Record<number, string>>({});
  const [seedOverrideReason, setSeedOverrideReason] = useState("");
  const [savingSeedOverrides, setSavingSeedOverrides] = useState(false);
  const [groups, setGroups] = useState<TournamentGroup[]>([]);
  const [participants, setParticipants] = useState<TournamentParticipant[]>([]);
  // Cowork 2026-09-19 (torneo 138): el Draw de eliminatoria contaba a los
  // dados de baja ("JUGADORES 12" con 6 activos) y los tomaba como pases
  // directos. `participants` se conserva completo para resolver nombres de
  // partidos ya jugados; el armado y los contadores usan solo a los vigentes.
  const drawParticipants = useMemo(
    () => participants.filter((participant) => participant.current_status !== "dropped"),
    [participants],
  );
  const [divisions, setDivisions] = useState<TournamentDivision[]>([]);
  const divisionFromQuery = searchParams.get("division");
  const initialDivisionId = divisionFromQuery && Number.isInteger(Number(divisionFromQuery)) ? Number(divisionFromQuery) : null;
  const [selectedDivisionId, setSelectedDivisionId] = useState<number | null>(initialDivisionId);
  const { askConfirm, currentSignal } = useAbortableConfirm([tournamentId, selectedDivisionId]);
  const [loading, setLoading] = useState(true);
  // El spinner de página completa solo aplica a la PRIMERA carga: el load se
  // re-dispara al resolver la división (doble load de montaje) y tras cada
  // mutación, y el remonte de todo el árbol se tragaba el primer click en
  // los botones del Draw (QA Cowork #12). En recargas se conserva el DOM.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Spinner de generación con OWNERSHIP por operación (18º gate de #135):
  // contador de operaciones en curso — una operación vieja al cerrar solo
  // DECREMENTA y jamás apaga el spinner de una nueva; el reset por cambio
  // de scope vive en el effect del load.
  const [generatingOps, setGeneratingOps] = useState(0);
  // QA Cowork #5: el botón "Rearmar draw incluyéndolos" del aviso de fuera
  // del draw navega aquí con ?regen=1. Round Robin se dispara solo (la
  // generación es ADITIVA: crea lo que falta y no toca lo existente); en
  // eliminación/escalera solo se guía — rearmar un cuadro implica elegir
  // siembra y eso es decisión del admin.
  const [pendingRegen, setPendingRegen] = useState(false);
  const generating = generatingOps > 0;
  const beginGeneratingOp = () => setGeneratingOps((n) => n + 1);
  const endGeneratingOp = () => setGeneratingOps((n) => Math.max(0, n - 1));
  const [message, setMessage] = useState<string | null>(null);
  const [selectedRoundNumber, setSelectedRoundNumber] = useState<number | null>(
    null,
  );
  const [matchSearchQuery, setMatchSearchQuery] = useState("");
  const [roundRobinEdits, setRoundRobinEdits] = useState<RoundRobinEdits>({});
  // «Acomodar grupos» (Eduardo 2026-10-01): subir/bajar en la hoja del
  // Round Robin y cambiar de grupo ANTES de que empiece la categoría. El
  // estado (candado, permiso del perfil) llega con cada load.
  const [arrangementStatus, setArrangementStatus] = useState<GroupArrangementCategory | null>(null);
  const [arranging, setArranging] = useState(false);
  /** Borradores por grupo (ids en el orden nuevo); sin entrada = orden guardado. */
  const [arrangeDrafts, setArrangeDrafts] = useState<Record<number, number[]>>({});
  /** Revisión «Así quedarían las jornadas» por grupo (solo con partidos que cambian). */
  const [arrangePreview, setArrangePreview] = useState<Record<number, GroupOrderPreview | null>>({});
  const [arrangeBusyGroupId, setArrangeBusyGroupId] = useState<number | null>(null);
  const [changeGroupFor, setChangeGroupFor] = useState<{ participantId: number; groupId: number } | null>(null);
  // Época del scope (sesión/torneo/categoría): una revisión que regresa
  // después de cambiar de categoría ya no abre confirmaciones ni pinta nada.
  const arrangeEpochRef = useRef(0);
  // Foco en «Acomodar grupos» (teclado y lector de pantalla): las flechas se
  // deshabilitan en los extremos y React mueve el renglón al reordenar, así
  // que el foco se perdía en <body>. Se registra cada control por llave y,
  // después de pintar, se enfoca el que toca.
  const arrangeFocusTargetsRef = useRef(new Map<string, HTMLElement>());
  const arrangePendingFocusRef = useRef<string | null>(null);
  const [eliminationEdits, setEliminationEdits] = useState<EliminationEdits>({});
  const [manualDrawParticipantIds, setManualDrawParticipantIds] = useState<number[]>([]);
  const [editingEliminationMatchIds, setEditingEliminationMatchIds] = useState<Set<number>>(() => new Set());
  const [savingRoundRobinMatchId, setSavingRoundRobinMatchId] = useState<number | null>(null);
  // Spinner de fila con OWNERSHIP por operación (20º gate de #135): la
  // identidad es {matchId, opId} — dos guardados de la MISMA fila son
  // operaciones distintas, y un finish stale solo limpia SU opId. El render
  // sigue comparando matchId vía la const derivada.
  const [savingEliminationOp, setSavingEliminationOp] = useState<{ matchId: number; opId: number } | null>(null);
  const savingEliminationMatchId = savingEliminationOp?.matchId ?? null;
  const savingEliminationOpSeqRef = useRef(0);
  const [eliminationVisualZoom, setEliminationVisualZoom] = useState(1);
  const [bracketSwapMode, setBracketSwapMode] = useState(false);
  const [swapSelectionId, setSwapSelectionId] = useState<number | null>(null);
  const autoSelectedRoundKeyRef = useRef<string | null>(null);
  const eliminationVisualRef = useRef<HTMLElement | null>(null);
  const eliminationPreviewVisualRef = useRef<HTMLDivElement | null>(null);
  const roundRobinVisualRef = useRef<HTMLElement | null>(null);
  const playoffBracketPreviewVisualRef = useRef<HTMLElement | null>(null);
  const roundRobinPlayoffVisualRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setSelectedDivisionId((current) => (current === initialDivisionId ? current : initialDivisionId));
  }, [initialDivisionId]);

  // Cambiar de torneo sí vuelve al spinner completo; cambiar de división NO
  // (el DOM se conserva para no desmontar botones bajo el cursor).
  useEffect(() => {
    setHasLoadedOnce(false);
  }, [tournamentId]);

  // Semáforo de preparación por categoría: que ninguna llegue al día del
  // torneo sin formato o sin draw (el error de la categoría D). Se refresca
  // junto con los matchups cargados.
  const [readinessRows, setReadinessRows] = useState<ScheduleReadinessRow[]>([]);
  const [readinessState, setReadinessState] = useState<"loading" | "loaded" | "failed">("loading");
  // «¿Este torneo usa calendario de canchas?» (se decide en Calendario, #449).
  // Con «No» el calendario no es un pendiente (Eduardo 2026-10-05): la
  // pastilla del checklist dice «Sin calendario de canchas» y los partidos
  // no salen «Sin programar». Mientras carga se queda como «Sí».
  const [usesCourtCalendar, setUsesCourtCalendar] = useState(true);
  const readinessMutationTick = useTournamentMutationTick(tournamentId);
  useEffect(() => {
    let cancelled = false;
    async function loadReadiness() {
      if (!token || Number.isNaN(tournamentId)) return;
      if (readinessMutationTick === 0) setReadinessState("loading");
      try {
        const resp = await api.matches.scheduleReadiness(token, tournamentId);
        if (!cancelled) {
          setReadinessRows(resp.data.divisions);
          setReadinessState("loaded");
        }
      } catch {
        if (!cancelled) {
          setReadinessRows([]);
          setReadinessState("failed");
        }
      }
    }
    void loadReadiness();
    return () => {
      cancelled = true;
    };
  }, [token, tournamentId, tournamentMatches, readinessMutationTick]);

  const groupParticipants = useMemo<Record<number, TournamentParticipant[]>>(
    () => {
      const map: Record<number, TournamentParticipant[]> = {};
      for (const group of groups) {
        const list = (group.participants || [])
          .map((a) => a.participant)
          .filter((p): p is TournamentParticipant => p !== null);
        map[group.id] = list;
      }
      void participants;
      return map;
    },
    [groups, participants],
  );

  const load = useCallback(async (opts?: { keepError?: boolean }) => {
    if (!token || Number.isNaN(tournamentId)) return;
    // Wiring del productor extraído a lib/matchesProducer (13º gate de
    // #135): loading/error/keepError/checkpoints/finally viven en
    // runProducerLoad — regresionado ejecutando este flujo real.
    await runProducerLoad(getMatchesLifecycle(), {
      // Scope exacto del load (16º gate): sesión (token exacto) + torneo +
      // división — cambiarlo caduca los boletos de mutación de scopes
      // anteriores (incluido A→B→A por época). Solo en memoria.
      scope: `${token}|${tournamentId}|${selectedDivisionId ?? "auto"}`,
      keepError: opts?.keepError,
      setLoading,
      clearError: () => setError(null),
      onError: (err) => {
        if (err instanceof ApiError) {
          setError(err.message || "No pudimos cargar los matchups.");
        } else {
          setError("Error de red. Intenta otra vez.");
        }
      },
      run: async (checkpoint, stampOwner) => {
        const detail = await api.tournaments.get(token, tournamentId);
        checkpoint();
        const divisionsResp = await api.tournaments.listDivisions(token, tournamentId);
        checkpoint();
        const nextDivisions = divisionsResp.data.divisions;
        setTournament(detail.data.tournament);
        // Sin el dato (respuesta vieja) se queda como hoy: con calendario.
        setUsesCourtCalendar(detail.data.settings?.uses_court_calendar !== false);
        setDivisions(nextDivisions);
        const selectedDivision = initialDivisionId
          ? nextDivisions.find((division) => division.id === initialDivisionId) ?? null
          : nextDivisions[0] ?? null;
        const effectiveDivisionId = selectedDivision?.id ?? null;
        if (effectiveDivisionId !== selectedDivisionId) setSelectedDivisionId(effectiveDivisionId);
        const effectiveFormatConfig: TournamentFormatConfig = selectedDivision
          ? {
              tournament_id: tournamentId,
              format_type: selectedDivision.format_type ?? detail.data.format_config.format_type,
              config_json: (selectedDivision.format_config_json ?? detail.data.format_config.config_json) as TournamentFormatConfig["config_json"],
              created_at: selectedDivision.created_at ?? detail.data.format_config.created_at,
              updated_at: selectedDivision.updated_at ?? detail.data.format_config.updated_at,
            }
          : detail.data.format_config;
        setFormatConfig(effectiveFormatConfig);
        const divisionParam = effectiveDivisionId ?? undefined;

        if (effectiveFormatConfig?.format_type !== "ladder") {
          const [groupsResp, participantsResp, matchesResp, qualificationResp, arrangementResp] = await Promise.all([
            api.groups.list(token, tournamentId, divisionParam !== undefined ? { division_id: divisionParam } : {}),
            api.participants.list(token, tournamentId, divisionParam !== undefined ? { division_id: divisionParam } : {}),
            api.matches.list(token, tournamentId, divisionParam !== undefined ? { division_id: divisionParam } : {}),
            effectiveFormatConfig?.format_type === "round_robin"
              ? api.standings.qualificationPreview(token, tournamentId, divisionParam !== undefined ? { division_id: divisionParam } : {})
              : Promise.resolve(null),
            // «Acomodar grupos»: candado de la categoría. Si falla, el botón
            // simplemente no aparece (el resto de la pantalla sigue igual).
            effectiveFormatConfig?.format_type === "round_robin"
              ? api.groups
                  .arrangementStatus(token, tournamentId, divisionParam !== undefined ? { division_id: divisionParam } : {})
                  .catch(() => null)
              : Promise.resolve(null),
          ]);
          // Latest-only: checkpoint DESPUÉS de cada await y antes de
          // cualquier setter — un load stale corta aquí mismo.
          checkpoint();
          // Estampar PRIMERO (15º gate): si el dueño no puede estamparse
          // (mutación abierta / stale / dispose), stampOwner ABORTA el load
          // completo — cero snapshots intermedios de setters/refs.
          stampOwner({ token, tournamentId, divisionId: effectiveDivisionId });
          setRounds([]);
          setTournamentMatches(matchesResp.data.matches);
          setRoundRobinMatches(
            effectiveFormatConfig?.format_type === "round_robin"
              ? matchesResp.data.matches
              : [],
          );
          setQualificationPreview(qualificationResp?.data ?? null);
          setRoundRobinEdits({});
          setEliminationEdits({});
          setEditingEliminationMatchIds(new Set());
          setGroups(divisionParam !== undefined ? groupsResp.data.groups.filter((g) => g.division_id === divisionParam) : groupsResp.data.groups);
          setParticipants(participantsResp.data.participants);
          const arrangementCategories = arrangementResp?.data.categories ?? [];
          setArrangementStatus(
            arrangementCategories.find((category) => (category.division_id ?? null) === effectiveDivisionId)
              ?? arrangementCategories[0]
              ?? null,
          );
          return;
        }

        const [roundsResp, groupsResp, participantsResp, matchesResp, qualificationResp] = await Promise.all([
          api.rounds.list(token, tournamentId, divisionParam !== undefined ? { division_id: divisionParam } : {}),
          api.groups.list(token, tournamentId, divisionParam !== undefined ? { division_id: divisionParam } : {}),
          api.participants.list(token, tournamentId, divisionParam !== undefined ? { division_id: divisionParam } : {}),
          api.matches.list(token, tournamentId, divisionParam !== undefined ? { division_id: divisionParam } : {}),
          isLadderPlayoffPhaseConfig(effectiveFormatConfig)
            ? api.standings.qualificationPreview(token, tournamentId, divisionParam !== undefined ? { division_id: divisionParam } : {})
            : Promise.resolve(null),
        ]);
        // Latest-only: mismo checkpoint post-await de la otra rama.
        checkpoint();
        // Estampar PRIMERO (15º gate): aborta el load si no puede — ningún
        // setter ni ref (auto-selección) aplica un snapshot intermedio.
        stampOwner({ token, tournamentId, divisionId: effectiveDivisionId });
        setRounds(roundsResp.data.rounds);
        const phasePlayoffMatches = isLadderPlayoffPhaseConfig(effectiveFormatConfig)
          ? matchesResp.data.matches.filter(isPhasePlayoffMatch)
          : [];
        const firstPlayoffRound = phasePlayoffMatches
          .map((match) => parseJornadaNumber(match.round_label))
          .filter((roundNumber): roundNumber is number => roundNumber !== null)
          .sort((a, b) => a - b)[0] ?? null;
        const autoSelectKey = "ladder:" + tournamentId + ":" + (effectiveDivisionId ?? "all") + ":" + (firstPlayoffRound ?? "regular");
        if (autoSelectedRoundKeyRef.current !== autoSelectKey) {
          const availableForAutoSelect = Array.from(
            new Set([
              ...roundsResp.data.rounds.map((round) => round.round_number),
              ...matchesResp.data.matches
                .map((match) => parseJornadaNumber(match.round_label))
                .filter((roundNumber): roundNumber is number => roundNumber !== null),
            ]),
          );
          const cfg = (effectiveFormatConfig.config_json ?? {}) as { start_date?: string; round_duration_days?: number };
          setSelectedRoundNumber(
            firstPlayoffRound ??
              currentRoundFromCalendar(cfg.start_date ?? detail.data.tournament.start_date, cfg.round_duration_days, availableForAutoSelect) ??
              computeOperationalRoundNumber(roundsResp.data.rounds),
          );
          autoSelectedRoundKeyRef.current = autoSelectKey;
        }
        setTournamentMatches(matchesResp.data.matches);
        setRoundRobinMatches(matchesResp.data.matches);
        setQualificationPreview(qualificationResp?.data ?? null);
        setGroups(divisionParam !== undefined ? groupsResp.data.groups.filter((g) => g.division_id === divisionParam) : groupsResp.data.groups);
        setParticipants(participantsResp.data.participants);
        setArrangementStatus(null);
      },
    });
    setHasLoadedOnce(true);
  }, [token, tournamentId, selectedDivisionId, initialDivisionId]);

  useEffect(() => {
    // Cambio de scope (token/torneo/división): los spinners de operaciones
    // del scope anterior se reinician aquí — una mutación stale cross-scope
    // no corre finish (16º gate), así que su liberación vive en este reset
    // (18º gate: ningún spinner queda colgado tras cambiar de scope).
    setGeneratingOps(0);
    setSavingEliminationOp(null);
    // «Acomodar grupos» es por categoría: al cambiar de scope se sale del modo.
    arrangeEpochRef.current += 1;
    setArranging(false);
    setArrangeDrafts({});
    setArrangePreview({});
    setArrangeBusyGroupId(null);
    setChangeGroupFor(null);
    void load();
  }, [load]);

  useEffect(() => {
    if (!token || Number.isNaN(tournamentId)) return;
    let cancelled = false;
    void Promise.resolve()
      .then(() => api.rounds.jornadaOverview(token, tournamentId))
      .then((response) => {
        if (!cancelled && response?.data) setJornadaOverview({ tournamentId, data: response.data });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token, tournamentId, tournamentMatches]);

  useEffect(() => {
    if (!qualificationPreview?.enabled) {
      setSeedOverrideDrafts({});
      return;
    }
    const next: Record<number, string> = {};
    for (const row of qualificationPreview.qualifiers) {
      next[row.participant_id] = String(row.seed);
    }
    setSeedOverrideDrafts(next);
  }, [qualificationPreview]);

  // ELIMINADO el redirect que expulsaba del wizard cuando el torneo ya no
  // estaba en draft (reporte Eduardo 2026-07-16: "después del paso Draw el
  // wizard no pasa por Comunicación, da por terminada la configuración").
  // Un torneo activo re-entra al wizard a propósito (mismo contrato que
  // TournamentSetupGuard desde #124: la única autoridad de navegación es el
  // stepper) y el paso 9 debe seguir siendo alcanzable.

  // Orden canónico del armado manual (pedido Eduardo 2026-07-25): por
  // RANKING (initial_rank ascendente); sin rank, por abecedario. Es el
  // orden inicial de los slots Y el orden fijo de las listas desplegables.
  const manualDrawRankSortedParticipants = useMemo(
    () =>
      [...drawParticipants].sort((a, b) => {
        const rankA = a.initial_rank ?? Number.POSITIVE_INFINITY;
        const rankB = b.initial_rank ?? Number.POSITIVE_INFINITY;
        if (rankA !== rankB) return rankA - rankB;
        return participantDisplayName(a).localeCompare(participantDisplayName(b), "es", { sensitivity: "base" });
      }),
    [drawParticipants],
  );

  useEffect(() => {
    setManualDrawParticipantIds((current) => {
      const participantIds = manualDrawRankSortedParticipants.map((participant) => participant.id);
      if (participantIds.length === 0) return [];
      const currentSet = new Set(current);
      const same = current.length === participantIds.length && participantIds.every((id) => currentSet.has(id));
      return same ? current : participantIds;
    });
  }, [manualDrawRankSortedParticipants]);

  const activeDivision = useMemo(
    () => divisions.find((division) => division.id === selectedDivisionId) ?? null,
    [divisions, selectedDivisionId],
  );

  const ladderConfig = isLadderConfig(formatConfig)
    ? formatConfig.config_json
    : null;
  const roundRobinConfig = isRoundRobinConfig(formatConfig)
    ? formatConfig.config_json
    : null;
  const eliminationConfig = isEliminationConfig(formatConfig)
    ? (formatConfig.config_json as EliminationFormatConfig)
    : null;
  const isLadder = !!ladderConfig;
  const isLadderPlayoffs = isLadderPlayoffPhaseConfig(formatConfig);
  const isPhasePlayoffBuilder = formatConfig?.format_type === "round_robin" || isLadderPlayoffs;
  const isTeamPlay = Boolean((formatConfig?.config_json as { team_play?: { enabled?: boolean } | null } | undefined)?.team_play?.enabled);
  const phaseBaseRoundCount = roundRobinConfig?.round_count ?? ladderBaseRoundCount(ladderConfig);
  const bracketPreview = qualificationPreview?.bracket_preview ?? null;
  const hasRoundRobinPlayoffResults = roundRobinMatches.some((match) =>
    isPhasePlayoffMatch(match) &&
    Boolean(match.winner_participant_id || match.score_text || ["completed", "walkover", "no_show", "defaulted", "retired", "double_walkover"].includes(String(match.status)))
  );
  const roundCount = ladderConfig ? ladderBaseRoundCount(ladderConfig) : roundRobinConfig?.round_count ?? rounds.length;

  // Eduardo 2026-09-21: "Generar Round Robin" y "Generar Playoffs" siempre
  // visibles confundían: con los partidos ya generados y los playoffs
  // automáticos, casi nunca hacen falta. Cada botón aparece solo cuando sirve.
  const rrDecidedStatuses = ["completed", "walkover", "no_show", "defaulted", "retired", "double_walkover"];
  const rrGroupMatches = roundRobinMatches.filter(
    (match) => !match.parent_match_id && String(match.status) !== "cancelled" && !isPhasePlayoffMatch(match),
  );
  const rrPlayoffMatchCount = roundRobinMatches.filter(
    (match) => !match.parent_match_id && isPhasePlayoffMatch(match),
  ).length;
  const rrParticipantIdsWithMatch = new Set<number>();
  for (const match of rrGroupMatches) {
    if (match.player1_participant_id != null) rrParticipantIdsWithMatch.add(match.player1_participant_id);
    if (match.player2_participant_id != null) rrParticipantIdsWithMatch.add(match.player2_participant_id);
  }
  // Inscritos tardíos: vigentes que todavía no aparecen en ningún partido de grupos.
  const rrParticipantsWithoutMatches = rrGroupMatches.length > 0
    ? drawParticipants.filter((participant) => !rrParticipantIdsWithMatch.has(participant.id)).length
    : 0;
  const rrGroupStageComplete = rrGroupMatches.length > 0 && rrGroupMatches.every(
    (match) => match.winner_participant_id != null || rrDecidedStatuses.includes(String(match.status)),
  );
  // 1) Generar Round Robin: la primera vez, o cuando hay inscritos nuevos sin partidos.
  const showGenerateRoundRobin = !isLadderPlayoffs && (rrGroupMatches.length === 0 || rrParticipantsWithoutMatches > 0);
  // 2) Generar Playoffs: los playoffs nacen solos; el botón queda como respaldo
  //    cuando la siembra es manual, cuando la fase de grupos ya terminó y aún no
  //    hay cuadro (torneos anteriores al automatismo) o cuando el automático no
  //    pudo generarlos (el motivo se muestra en el preview de clasificación).
  // Encinos (Eduardo 2026-10-09): programar los playoffs antes de que acaben
  // los grupos. El cuadro nace con referencias («1.º Grupo A») y se llena
  // solo al capturar el último resultado de grupos (o con «Llenar…»).
  const showCreatePlayoffPlaceholders = !isLadderPlayoffs
    && Boolean(qualificationPreview?.enabled)
    && rrGroupMatches.length > 0
    && rrPlayoffMatchCount === 0
    && !rrGroupStageComplete
    && roundRobinConfig?.playoff?.bracket_policy !== "manual";
  const rrUnfilledPlaceholderCount = roundRobinMatches.filter(
    (match) => !match.parent_match_id && isPhasePlayoffMatch(match) && isUnfilledPlayoffPlaceholder(match),
  ).length;
  const showGeneratePlayoffs = isLadderPlayoffs || (
    Boolean(qualificationPreview?.enabled)
    && rrPlayoffMatchCount === 0
    && (
      roundRobinConfig?.playoff?.bracket_policy === "manual"
      || rrGroupStageComplete
      || Boolean(qualificationPreview?.summary.playoffs_auto_blocked)
    )
  );

  const allRoundRobinPlayerOptions = useMemo(() => {
    const byId = new Map<number, TournamentParticipant>();
    for (const participant of participants) byId.set(participant.id, participant);
    return [...byId.values()].sort((a, b) => participantDisplayName(a).localeCompare(participantDisplayName(b)));
  }, [participants]);

  // Build the list of jornada numbers to expose in the selector. Use either
  // the configured round_count (so the admin can pick an unbuilt jornada) or
  // the rounds/matches we already have on file, whichever is larger.
  const availableRoundNumbers = useMemo<number[]>(() => {
    const present = new Set<number>();
    for (const r of rounds) present.add(r.round_number);
    for (const match of roundRobinMatches) {
      const parsed = parseJornadaNumber(match.round_label);
      if (parsed !== null) present.add(parsed);
    }
    const total = Math.max(roundCount || 0, ...rounds.map((r) => r.round_number), ...Array.from(present), 0);
    const result: number[] = [];
    for (let i = 1; i <= total; i += 1) result.push(i);
    return result.length > 0 ? result : Array.from(present).sort((a, b) => a - b);
  }, [roundRobinMatches, rounds, roundCount]);

  const roundsByNumber = useMemo(() => {
    const map = new Map<number, TournamentRound>();
    for (const r of rounds) map.set(r.round_number, r);
    return map;
  }, [rounds]);

  // QA Cowork #7: semanas regulares de escalera sin jornada generada. El
  // backend ya genera todas las faltantes en lote (round_number omitido en
  // rounds/generate) como borrador, sin tocar publicadas.
  const missingLadderWeeks = useMemo<number[]>(() => {
    if (!ladderConfig) return [];
    const total = ladderBaseRoundCount(ladderConfig);
    const missing: number[] = [];
    for (let rn = 1; rn <= total; rn += 1) {
      if (!roundsByNumber.has(rn)) missing.push(rn);
    }
    return missing;
  }, [ladderConfig, roundsByNumber]);

  const roundRobinMatchesByNumber = useMemo(() => {
    const map = new Map<number, TournamentMatch[]>();
    for (const match of roundRobinMatches) {
      const edit = roundRobinEdits[match.id];
      const parsed = edit
        ? Number(edit.roundNumber)
        : parseJornadaNumber(match.round_label);
      if (parsed === null || !Number.isFinite(parsed) || parsed <= 0) continue;
      const n = parsed;
      const list = map.get(n) ?? [];
      list.push(match);
      map.set(n, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        const aEdit = roundRobinEdits[a.id];
        const bEdit = roundRobinEdits[b.id];
        const aMatchNumber = aEdit ? Number(aEdit.roundNumber) : a.match_number;
        const bMatchNumber = bEdit ? Number(bEdit.roundNumber) : b.match_number;
        return (
          (a.match_number ?? aMatchNumber ?? 1_000_000)
          - (b.match_number ?? bMatchNumber ?? 1_000_000)
        ) || a.id - b.id;
      });
    }
    return map;
  }, [roundRobinEdits, roundRobinMatches]);

  const roundRobinChangedCount = useMemo(() => {
    let count = 0;
    for (const match of roundRobinMatches) {
      const edit = roundRobinEdits[match.id];
      if (edit && !roundRobinEditMatches(edit, match)) count += 1;
    }
    return count;
  }, [roundRobinEdits, roundRobinMatches]);

  const roundRobinVisualGroups = useMemo<RoundRobinVisualGroup[]>(() => {
    return groups.map((group) => {
      const participantsForGroup = [...(groupParticipants[group.id] || [])];
      const knownParticipantIds = new Set(participantsForGroup.map((participant) => participant.id));
      const ghostIds = new Set<number>();
      const matchesByPair = new Map<string, RoundRobinVisualMatch>();
      // Grupo acomodado (Eduardo 2026-10-01): las filas siguen el lugar en la
      // hoja. Sin lugares, exactamente el orden y las filas de siempre.
      const positions = groupSheetPositions(group);
      // Revisión 2026-10-05: en un grupo acomodado, primero los vigentes (que
      // arman las filas, también la de una baja) y luego los cancelados. Así
      // un «Cancelado por baja» sale en su celda sin importar el orden de los
      // partidos, igual que en el Draw del jugador.
      const matchesInOrder =
        positions === null
          ? roundRobinMatches
          : [
              ...roundRobinMatches.filter((match) => String(match.status) !== "cancelled"),
              ...roundRobinMatches.filter((match) => String(match.status) === "cancelled"),
            ];

      for (const match of matchesInOrder) {
        const edit = roundRobinEdits[match.id];
        const effectiveGroupId = Number(edit?.groupId ?? match.group_id ?? 0);
        const player1Id = Number(edit?.player1 ?? match.player1_participant_id ?? 0);
        const player2Id = Number(edit?.player2 ?? match.player2_participant_id ?? 0);
        if (effectiveGroupId !== group.id || player1Id <= 0 || player2Id <= 0) continue;
        if (player1Id === player2Id) continue;
        const pairKey = roundRobinPairKey(player1Id, player2Id);
        // Solo en un grupo ACOMODADO: un partido cancelado no crea filas
        // fantasma (quien solo sale ahí ya no está en el grupo), no cuenta si
        // uno de los dos no tiene fila y nunca tapa al vigente de la misma
        // pareja. Sin lugares (torneo 59 y los demás en curso), como siempre.
        const cancelled = positions !== null && String(match.status) === "cancelled";
        if (
          cancelled
          && (matchesByPair.has(pairKey) || !knownParticipantIds.has(player1Id) || !knownParticipantIds.has(player2Id))
        ) {
          continue;
        }
        if (!cancelled) {
          // Filas que el listado de grupos ya no trae (bajas): salen del partido.
          for (const [participantId, brief] of [[player1Id, match.player1], [player2Id, match.player2]] as const) {
            if (brief && brief.id === participantId && !knownParticipantIds.has(participantId)) {
              participantsForGroup.push(participantFromMatchBrief(brief));
              knownParticipantIds.add(participantId);
              ghostIds.add(participantId);
            }
          }
        }
        matchesByPair.set(pairKey, {
          id: match.id,
          matchNumber: match.match_number,
          roundNumber: roundRobinEffectiveRoundNumber(match, edit),
          status: match.status,
          scheduledDate: match.scheduled_date,
          scheduledTime: match.scheduled_time,
          court: match.court,
          player1Id,
          player2Id,
          winnerParticipantId: match.winner_participant_id ?? null,
          scoreSets: match.score_sets ?? [],
          scoreText: match.score_text,
          isTie: match.match_kind === "tie",
          drop: match.match_kind === "tie" ? null : dropMatchState(match),
        });
      }

      const ordered = orderMatrixParticipants(participantsForGroup, {
        positions,
        autoCompare: (a, b) => {
          const aRank = a.current_rank ?? a.initial_rank ?? 1_000_000;
          const bRank = b.current_rank ?? b.initial_rank ?? 1_000_000;
          if (aRank !== bRank) return aRank - bRank;
          return participantDisplayName(a).localeCompare(participantDisplayName(b));
        },
        ghostIds,
      });
      return {
        id: group.id,
        name: group.name,
        participants: ordered,
        matchesByPair,
        hasPositions: positions !== null,
        ghostIds,
      };
    }).filter((group) => group.participants.length > 0);
  }, [groups, groupParticipants, roundRobinEdits, roundRobinMatches]);

  /** G-P, games y lugar de cada grupo (Eduardo 2026-10-15, tabla estilo
   * póster): salen de la vista previa de clasificación que la página ya
   * carga; sin ella, las columnas dicen «—». */
  const rrStandingsByGroup = useMemo(() => {
    type Row = RoundRobinQualificationPreviewPayload["standings"]["groups"][number]["standings"][number];
    const map = new Map<number, Map<number, Row>>();
    for (const group of qualificationPreview?.standings?.groups ?? []) {
      if (group.group_id == null) continue;
      map.set(group.group_id, new Map(group.standings.map((row) => [row.participant_id, row])));
    }
    return map;
  }, [qualificationPreview]);

  // ── «Acomodar grupos» (Eduardo 2026-10-01) ──────────────────────────────
  const groupsById = useMemo(() => new Map(groups.map((group) => [group.id, group])), [groups]);
  /** Orden guardado de cada grupo: el punto de partida de su borrador. */
  const arrangeInitialOrders = useMemo<Record<number, number[]>>(() => {
    const out: Record<number, number[]> = {};
    for (const visual of roundRobinVisualGroups) {
      const group = groupsById.get(visual.id);
      if (!group) continue;
      out[visual.id] = initialArrangeDraft(
        group,
        visual.participants.map((participant) => participant.id),
        visual.ghostIds,
      );
    }
    return out;
  }, [groupsById, roundRobinVisualGroups]);
  const arrangeDraftFor = (groupId: number): number[] => {
    const initial = arrangeInitialOrders[groupId] ?? [];
    const draft = arrangeDrafts[groupId];
    // Un borrador viejo (cambió quién está en el grupo) ya no vale.
    return draft && sameMembers(draft, initial) ? draft : initial;
  };
  const arrangeDirtyGroupIds = roundRobinVisualGroups
    .filter((visual) => !sameOrder(arrangeDraftFor(visual.id), arrangeInitialOrders[visual.id] ?? []))
    .map((visual) => visual.id);
  // «Generar Round Robin» usaría el orden GUARDADO, no el que se está
  // acomodando: mientras haya orden sin guardar, primero guardar o deshacer.
  const generateBlockedByArrange = arranging && arrangeDirtyGroupIds.length > 0;
  function generateBlockedByArrangeMessage(groupIds: number[]): string {
    const names = groupIds.map((groupId) => groupsById.get(groupId)?.name ?? `Grupo ${groupId}`);
    return `Tienes un orden sin guardar en el ${joinSpanishList(names)}: guárdalo o deshazlo antes de generar las jornadas.`;
  }
  // Cambios reales sin guardar en la tabla de partidos (una edición que
  // regresó a su valor original no cuenta): guardar el orden recarga la
  // pantalla y los perdería.
  const roundRobinHasPendingEdits = roundRobinChangedCount > 0;
  const arrangeDisabledReason: string | null = !arrangementStatus
    ? null
    : arrangementStatus.locked
      ? arrangementStatus.message || ARRANGE_LOCK_FALLBACK
      : !arrangementStatus.can_edit
        ? "Tu perfil no permite acomodar grupos."
        : roundRobinHasPendingEdits
          ? "Guarda o cancela primero los cambios de la tabla de partidos."
          : null;

  // Foco pendiente de «Acomodar grupos» (ver arrangeFocusTargetsRef): se
  // intenta una vez después de cada pintada y se olvida.
  useEffect(() => {
    const key = arrangePendingFocusRef.current;
    if (!key) return;
    arrangePendingFocusRef.current = null;
    const target = arrangeFocusTargetsRef.current.get(key);
    if (target && !(target as HTMLButtonElement).disabled) target.focus();
  });

  // Si la categoría empieza (o el perfil ya no puede) mientras se acomoda,
  // se sale del modo: ya no hay nada que guardar y el aviso explica por qué.
  useEffect(() => {
    if (!arrangementStatus || arrangementStatus.locked || !arrangementStatus.can_edit) {
      setArranging(false);
      setArrangeDrafts({});
      setArrangePreview({});
      setChangeGroupFor(null);
    }
  }, [arrangementStatus]);

  const roundRobinPlayoffVisualRounds = useMemo<RoundRobinPlayoffVisualRound[]>(() => {
    if (!isPhasePlayoffBuilder) return [];
    // La pertenencia al cuadro la decide el predicado AUTORITATIVO compartido
    // (notes de playoff / etiqueta canónica legada), igual que
    // hasRoundRobinPlayoffResults aquí mismo y playoff_labels.py en backend.
    // El número de ronda solo agrupa y ordena — ver groupPlayoffVisualRounds.
    return groupPlayoffVisualRounds(roundRobinMatches, {
      isPlayoff: isPhasePlayoffMatch,
      roundNumberFor: (match) => {
        const legacyOrder = legacyPlayoffRoundOrder(match.round_label);
        if (legacyOrder !== null) return phaseBaseRoundCount + legacyOrder;
        return roundRobinEffectiveRoundNumber(match, roundRobinEdits[match.id]);
      },
    });
  }, [isPhasePlayoffBuilder, phaseBaseRoundCount, roundRobinEdits, roundRobinMatches]);

  // Eduardo 2026-10-02: sin resultados de grupos el preview dice el lugar
  // que clasifica («1.º Grupo 1», «Mejor 3.º»), no el nombre proyectado (el
  // admin creía que los playoffs ya estaban programados).
  const playoffPreviewNamesPending = playoffNamesPending(qualificationPreview);
  const playoffPreviewPlaceholders = useMemo(
    () => (playoffPreviewNamesPending && qualificationPreview ? qualifierPlaceholderLabels(qualificationPreview.qualifiers) : null),
    [playoffPreviewNamesPending, qualificationPreview],
  );
  const previewBracketRounds = useMemo<BracketRound[]>(
    () => buildPlayoffPreviewRounds(bracketPreview, playoffPreviewPlaceholders),
    [bracketPreview, playoffPreviewPlaceholders],
  );
  // Los seeds manuales se guardan por jugador: sin resultados de grupos (la
  // tabla dice «1.º Grupo 2») se bloquean, para no sembrar a quien solo va
  // primero en la proyección creyendo que se siembra el lugar.
  const manualSeedLock = manualSeedLockReason({
    hasPlayoffResults: hasRoundRobinPlayoffResults,
    namesPending: playoffPreviewNamesPending,
  });

  // Seed de playoffs por participante (preview de clasificación): el bracket
  // visual lo muestra junto al nombre, igual que el cuadro de eliminación.
  // Seeds/lugares GUARDADOS al generar el cuadro ([pN_seed]/[pN_q] en notes).
  // Si existen mandan sobre el preview en vivo (que puede haber cambiado).
  const storedPlayoffSeedInfo = useMemo(
    () => playoffSeedInfoByParticipant(roundRobinMatches.filter((match) => match.group_id === null && isPhasePlayoffMatch(match))),
    [roundRobinMatches],
  );
  const playoffSeedByParticipantId = useMemo<Record<number, number>>(() => {
    const map: Record<number, number> = {};
    if (storedPlayoffSeedInfo.size > 0) {
      for (const [participantId, info] of storedPlayoffSeedInfo) {
        if (info.seed != null) map[participantId] = info.seed;
      }
      return map;
    }
    if (qualificationPreview?.enabled) {
      for (const row of qualificationPreview.qualifiers) {
        if (row.participant_id != null) map[row.participant_id] = row.seed;
      }
    }
    return map;
  }, [qualificationPreview, storedPlayoffSeedInfo]);

  // «Editar partidos» de playoffs: seed + lugar de grupo de cada jugador
  // («S3 · 1.º Grupo A · Nombre»). El lugar sale de lo guardado al generar o,
  // para quien no clasificó, de la tabla de su grupo.
  const playoffOptionInfoById = useMemo<Map<number, PlayoffOptionInfo>>(() => {
    const map = new Map<number, PlayoffOptionInfo>();
    for (const group of qualificationPreview?.standings?.groups ?? []) {
      if (group.group_id == null) continue;
      for (const row of group.standings ?? []) {
        if (row.participant_id == null || !row.position) continue;
        map.set(row.participant_id, { seed: null, place: `${row.position}.º ${group.group_name}` });
      }
    }
    for (const [participantId, seed] of Object.entries(playoffSeedByParticipantId)) {
      const id = Number(participantId);
      map.set(id, { seed, place: map.get(id)?.place ?? null });
    }
    for (const [participantId, info] of storedPlayoffSeedInfo) {
      map.set(participantId, { seed: info.seed ?? map.get(participantId)?.seed ?? null, place: info.qual ?? map.get(participantId)?.place ?? null });
    }
    return map;
  }, [playoffSeedByParticipantId, qualificationPreview, storedPlayoffSeedInfo]);
  const playoffPlayerOptions = useMemo(
    () => sortPlayoffPlayerOptions(allRoundRobinPlayerOptions, (id) => playoffOptionInfoById.get(id), compactParticipantDisplayName),
    [allRoundRobinPlayerOptions, playoffOptionInfoById],
  );
  const playoffMatchCount = roundRobinMatches.filter((match) => match.group_id === null && isPhasePlayoffMatch(match)).length;
  const regenerateState = regeneratePlayoffsState({
    playoffMatchCount,
    hasPlayoffResults: hasRoundRobinPlayoffResults,
    busy: generating,
  });

  const roundRobinPlayoffBracketRounds = useMemo<BracketRound[]>(() => {
    const deadSides = deadBracketSides(roundRobinMatches);
    const gamesFor = (match: TournamentMatch, side: "p1" | "p2"): number[] =>
      (match.score_sets ?? []).map((set) => (side === "p1" ? set.player1_games : set.player2_games));
    const competitorFor = (
      match: TournamentMatch,
      side: "p1" | "p2",
      roundNumber: number,
    ): BracketCompetitor | null => {
      const participantId = side === "p1" ? match.player1_participant_id : match.player2_participant_id;
      const brief = side === "p1" ? match.player1 : match.player2;
      const fallback = participantId
        ? `#${participantId}`
        : isDeadSide(deadSides, match, side === "p1" ? 1 : 2)
          ? DEAD_SIDE_LABEL
        : side === "p2"
          ? ladderSemifinalPlaceholderName(match, roundNumber, phaseBaseRoundCount)
          : "Por definir";
      return {
        name: matchParticipantName(brief, fallback),
        seed: participantId != null ? playoffSeedByParticipantId[participantId] ?? null : null,
        games: gamesFor(match, side),
        isWinner: match.winner_participant_id != null && match.winner_participant_id === participantId,
        tag: competitorLoserTag(match, participantId),
      };
    };
    return roundRobinPlayoffVisualRounds.map((round) => ({
      label: eliminationRoundDisplayLabel(round.label),
      matches: round.matches.map((match) => ({
        id: `rr-playoff-${match.id}`,
        matchId: match.id,
        top: competitorFor(match, "p1", round.roundNumber),
        bottom: competitorFor(match, "p2", round.roundNumber),
        pending: !match.player1_participant_id || !match.player2_participant_id,
      })),
    }));
  }, [phaseBaseRoundCount, playoffSeedByParticipantId, roundRobinMatches, roundRobinPlayoffVisualRounds]);

  const openRoundRobinPlayoffMatch = useCallback((matchId: number) => {
    const round = roundRobinPlayoffVisualRounds.find((candidate) =>
      candidate.matches.some((match) => match.id === matchId),
    );
    if (round) {
      setSelectedRoundNumber(round.roundNumber);
    }
  }, [roundRobinPlayoffVisualRounds]);

  // "3er Lugar" playoff match — excluded from the round columns, shown as a
  // mini-box under the final instead.
  const roundRobinPlayoffThirdPlace = useMemo<BracketMatch | null>(() => {
    const match = roundRobinMatches.find((candidate) =>
      candidate.group_id === null && isThirdPlaceRoundLabel(candidate.round_label));
    if (!match) return null;
    const deadSides = deadBracketSides(roundRobinMatches);
    const competitorFor = (side: "p1" | "p2"): BracketCompetitor | null => {
      const participantId = side === "p1" ? match.player1_participant_id : match.player2_participant_id;
      const brief = side === "p1" ? match.player1 : match.player2;
      const empty = isDeadSide(deadSides, match, side === "p1" ? 1 : 2) ? DEAD_SIDE_LABEL : "Por definir";
      return {
        name: matchParticipantName(brief, participantId ? `#${participantId}` : empty),
        seed: participantId != null ? playoffSeedByParticipantId[participantId] ?? null : null,
        games: (match.score_sets ?? []).map((set) => (side === "p1" ? set.player1_games : set.player2_games)),
        isWinner: match.winner_participant_id != null && match.winner_participant_id === participantId,
        tag: competitorLoserTag(match, participantId),
      };
    };
    return {
      id: `rr-playoff-third-${match.id}`,
      matchId: match.id,
      top: competitorFor("p1"),
      bottom: competitorFor("p2"),
      pending: !match.player1_participant_id || !match.player2_participant_id,
    };
  }, [playoffSeedByParticipantId, roundRobinMatches]);

  const roundResultSummaries = useMemo(() => {
    const map = new Map<number, RoundResultSummary>();
    for (const match of tournamentMatches) {
      const rn = parseJornadaNumber(match.round_label);
      if (rn === null) continue;
      const listSummary = map.get(rn) ?? { total: 0, published: 0, pending: 0 };
      listSummary.total += 1;
      if (isPublishedResultStatus(match.status) && !!match.winner_participant_id) {
        listSummary.published += 1;
      }
      map.set(rn, listSummary);
    }
    for (const [rn, summary] of map) {
      map.set(rn, { ...summary, pending: Math.max(summary.total - summary.published, 0) });
    }
    return map;
  }, [tournamentMatches]);

  const isRoundResultPublished = useCallback((roundNumber: number): boolean => {
    if (roundNumber <= 0) return true;
    const summary = roundResultSummaries.get(roundNumber);
    return !!summary && summary.total > 0 && summary.pending === 0;
  }, [roundResultSummaries]);

  const latestPublishedRoundNumber = useMemo(() => {
    return [...rounds]
      .filter((round) => round.status === "published")
      .sort((a, b) => b.round_number - a.round_number)[0]?.round_number ?? null;
  }, [rounds]);

  const nextDraftRoundNumber = useMemo(() => {
    if (latestPublishedRoundNumber === null) return null;
    const next = latestPublishedRoundNumber + 1;
    return availableRoundNumbers.includes(next) ? next : null;
  }, [availableRoundNumbers, latestPublishedRoundNumber]);

  const currentCalendarRoundNumber = useMemo(() => {
    const cfg = (formatConfig?.config_json ?? {}) as { start_date?: string; round_duration_days?: number };
    return currentRoundFromCalendar(
      cfg.start_date ?? tournament?.start_date,
      cfg.round_duration_days,
      availableRoundNumbers,
    );
  }, [availableRoundNumbers, formatConfig, tournament?.start_date]);

  const defaultRoundNumber = useMemo(() => {
    if (currentCalendarRoundNumber !== null) return currentCalendarRoundNumber;
    if (latestPublishedRoundNumber !== null) return latestPublishedRoundNumber;

    const firstDraft = [...rounds]
      .filter((round) => round.status === "draft")
      .sort((a, b) => a.round_number - b.round_number)[0];
    if (firstDraft) return firstDraft.round_number;

    return availableRoundNumbers[0] ?? null;
  }, [availableRoundNumbers, currentCalendarRoundNumber, latestPublishedRoundNumber, rounds]);

  // Pick the calendar-current matchup on load, so Matchups opens on the week
  // the tournament is currently running instead of defaulting to the latest one.
  useEffect(() => {
    if (selectedRoundNumber !== null) return;
    if (defaultRoundNumber !== null) setSelectedRoundNumber(defaultRoundNumber);
  }, [defaultRoundNumber, selectedRoundNumber]);

  // If the selection points to a round that no longer exists in the data,
  // snap it back to the same operational default.
  useEffect(() => {
    if (selectedRoundNumber === null) return;
    if (availableRoundNumbers.length === 0) return;
    if (!availableRoundNumbers.includes(selectedRoundNumber)) {
      setSelectedRoundNumber(defaultRoundNumber ?? availableRoundNumbers[0]);
    }
  }, [availableRoundNumbers, defaultRoundNumber, selectedRoundNumber]);

  useEffect(() => {
    if (selectedRoundNumber === null) return;
    const activeButton = document.querySelector<HTMLButtonElement>(
      `[data-round-selector-button="${selectedRoundNumber}"]`,
    );
    activeButton?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [selectedRoundNumber]);

  async function handleGenerate(opts: {
    regenerate_drafts?: boolean;
    round_number?: number;
  } = {}) {
    if (!token) return;
    if (
      opts.regenerate_drafts &&
      !(await askConfirm(
        "Regenerar descarta los ajustes manuales del borrador de esta semana. ¿Continuar?", { acceptLabel: "Sí, regenerar semana", tone: "danger" }))
    ) {
      return;
    }
    beginGeneratingOp();
    setError(null);
    setMessage(null);
    // Ruta ladder al MISMO lifecycle scoped que las demás (19º gate): sin
    // esto, un handleGenerate de A resolviendo tarde escribía
    // rounds/mensajes bajo B y decrementaba el contador de B.
    await runMatchesMutation(getMatchesLifecycle(), {
      mutate: async (checkpoint) => {
        const resp = await api.rounds.generate(token, tournamentId, { ...opts, division_id: selectedDivisionId });
        checkpoint();
        const summary = resp.data.summary;
        const created = summary.created.length;
        const regen = summary.regenerated.length;
        const skippedPub = summary.skipped_published.length;
        setRounds(resp.data.rounds);
        if (opts.round_number) setSelectedRoundNumber(opts.round_number);
        setMessage(
          `Generadas: ${created} · Regeneradas: ${regen}${
            skippedPub > 0
              ? ` · Publicadas no tocadas: ${skippedPub}`
              : ""
          }.`,
        );
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          setError(err.message || "No se pudo generar los matchups.");
        } else {
          setError("Error de red.");
        }
      },
      reload: (reloadOpts) => load({ keepError: reloadOpts.keepError }),
      finish: () => endGeneratingOp(),
    });
  }

  async function handleGenerateAllLadderWeeks() {
    const pending = missingLadderWeeks.length;
    if (pending === 0) return;
    if (
      !(await askConfirm(
        `Se crearán como BORRADOR las ${pending} semana(s) que faltan, emparejadas con el orden actual de la escalera. Las semanas publicadas no se tocan. Si el orden cambia con los resultados, usa "Regenerar Semana N" antes de publicar cada una. ¿Continuar?`, { acceptLabel: "Sí, crear borradores" }))
    ) {
      return;
    }
    void handleGenerate({});
  }

  async function handleGeneratePlayoffs() {
    if (!token) return;
    beginGeneratingOp();
    setError(null);
    setMessage(null);
    // Ruta ladder al MISMO lifecycle scoped que las demás (19º gate).
    await runMatchesMutation(getMatchesLifecycle(), {
      mutate: async (checkpoint) => {
        const resp = await api.rounds.generatePlayoffs(token, tournamentId, selectedDivisionId ?? null);
        checkpoint();
        const summary = resp.data.summary;
        setRounds(resp.data.rounds);
        const roundNumber = summary.round_numbers[0] ?? null;
        if (roundNumber !== null) setSelectedRoundNumber(roundNumber);
        if (summary.created > 0) {
          const label = roundNumber !== null
            ? ladderRoundButtonLabel(roundNumber, ladderConfig?.round_count ?? 0)
            : "Playoff";
          setMessage(
            `${label} generado: ${summary.qualified_participant_ids.length} clasificado(s) · ${summary.playoff_players_count} jugadores en cuadro.`,
          );
        } else {
          setMessage(`Playoffs ya estaban generados: ${summary.skipped} ronda(s).`);
        }
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          setError(err.message || "No se pudieron armar los playoffs.");
        } else {
          setError("Error de red.");
        }
      },
      reload: (reloadOpts) => load({ keepError: reloadOpts.keepError }),
      finish: () => endGeneratingOp(),
    });
  }

  useEffect(() => {
    if (searchParams.get("regen") !== "1") return;
    const next = new URLSearchParams(searchParams);
    next.delete("regen");
    setSearchParams(next, { replace: true });
    setPendingRegen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!pendingRegen || generating || !formatConfig) return;
    setPendingRegen(false);
    if (formatConfig.format_type === "round_robin") {
      void handleGenerateRoundRobin();
      return;
    }
    // Alta tardía en ELIMINACIÓN (hueco #2-#3 de la auditoría de
    // altas/bajas): el texto viejo prometía "el cuadro se rearma con la
    // siembra que elijas" aunque el backend lo bloquee (correctamente) con
    // calendario o resultados. Guía honesta según el estado real del
    // bracket, con el MISMO predicado del backend (fecha/hora/cancha
    // replicado en eliminationScheduledCount + resultados).
    if (formatConfig.format_type === "elimination") {
      const bracketLocked = eliminationScheduledCount > 0 || eliminationResolvedCount > 0;
      setMessage(
        bracketLocked
          ? "Esta eliminatoria ya tiene calendario o resultados: el cuadro NO se puede rearmar, así que el inscrito nuevo no puede entrar al bracket. Opciones reales: dejarlo fuera de esta eliminatoria, o — si apenas empieza — quitar fechas y resultados en Calendario para poder rearmar (se pierde el avance del cuadro)."
          : "El cuadro aún no tiene calendario ni resultados: usa “Rearmar cuadro” (junto al bracket) para regenerarlo incluyendo a los inscritos nuevos.",
      );
      return;
    }
    setMessage(
      "Para incluir a los inscritos nuevos en escalera: entran en la siguiente semana que generes (necesitan grupo asignado).",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRegen, generating, formatConfig]);

  async function handleGenerateRoundRobin() {
    if (!token) return;
    if (arranging && arrangeDirtyGroupIds.length > 0) {
      // Generaría con el orden guardado, no con el que se ve en pantalla.
      setError(generateBlockedByArrangeMessage(arrangeDirtyGroupIds));
      return;
    }
    beginGeneratingOp();
    setError(null);
    setMessage(null);
    await runMatchesMutation(getMatchesLifecycle(), {
      mutate: async (checkpoint) => {
          const resp = await api.matches.generateRoundRobin(token, tournamentId, {
            division_id: selectedDivisionId,
            round_count: roundRobinConfig?.round_count ?? undefined,
          });
          checkpoint();
          const summary = resp.data;
          setMessage(
            `Round Robin generado: ${summary.created} partidos nuevos · ${summary.updated ?? 0} acomodados por jornada · ${summary.skipped} existentes.`,
          );
          if (summary.created === 0 && summary.skipped === 0 && (summary.updated ?? 0) === 0) {
            setError(
              "No se generaron partidos. Revisa que cada grupo tenga al menos 2 jugadores asignados.",
            );
            return { keepErrorOnReload: true };
          }
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          setError(err.message || "No se pudo generar el Round Robin.");
        } else {
          setError("Error de red.");
        }
      },
      reload: (reloadOpts) => load({ keepError: reloadOpts.keepError }),
      finish: () => endGeneratingOp(),
    });
  }

  async function handleSaveSeedOverrides() {
    if (!token || !qualificationPreview || manualSeedLock) return;
    const reason = seedOverrideReason.trim();
    if (!reason) {
      setError("Escribe la razón del ajuste manual de sembrados.");
      return;
    }
    const seedOverrides = qualificationPreview.qualifiers.map((row) => {
      const parsed = Number(seedOverrideDrafts[row.participant_id]);
      return { participant_id: row.participant_id, seed: parsed };
    });
    if (seedOverrides.some((item) => !Number.isInteger(item.seed) || item.seed < 1 || item.seed > qualificationPreview.qualifiers.length)) {
      setError(`Usa seeds del 1 al ${qualificationPreview.qualifiers.length}.`);
      return;
    }
    if (new Set(seedOverrides.map((item) => item.seed)).size !== seedOverrides.length) {
      setError("No repitas seeds; cada clasificado debe tener un número único.");
      return;
    }
    setSavingSeedOverrides(true);
    setError(null);
    setMessage(null);
    try {
      const resp = await api.standings.saveQualificationOverrides(token, tournamentId, {
        division_id: selectedDivisionId,
        reason,
        seed_overrides: seedOverrides,
      });
      setQualificationPreview(resp.data);
      setSeedOverrideReason("");
      setMessage("Sembrados manuales guardados con auditoría. El preview y la generación de playoffs usarán este orden.");
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudieron guardar los overrides manuales.");
      } else {
        setError("Error de red.");
      }
    } finally {
      setSavingSeedOverrides(false);
    }
  }

  async function handleCreatePlayoffPlaceholders() {
    if (!token) return;
    beginGeneratingOp();
    setError(null);
    setMessage(null);
    await runMatchesMutation(getMatchesLifecycle(), {
      mutate: async (checkpoint) => {
        const resp = await api.matches.createRoundRobinPlayoffPlaceholders(token, tournamentId, {
          division_id: selectedDivisionId,
        });
        checkpoint();
        setMessage(
          `Cuadro de playoffs por definir creado: ${resp.data.created} partido(s) para ${resp.data.playoff_slots_count} lugares. Ya puedes darles fecha, hora y cancha en Calendario; se llenan solos con los clasificados al terminar los grupos.`,
        );
      },
      onError: (err) => {
        setError(err instanceof ApiError ? err.message || "No se pudo crear el cuadro por definir." : "Error de red.");
      },
      reload: (reloadOpts) => load({ keepError: reloadOpts.keepError }),
      finish: () => endGeneratingOp(),
    });
  }

  async function handleRegeneratePlayoffs() {
    if (!token) return;
    const publishedCount = roundRobinMatches.filter(
      (match) => match.group_id === null && isPhasePlayoffMatch(match) && Boolean(match.schedule_published_at),
    ).length;
    const confirmed = await askConfirm(
      publishedCount > 0
        ? `Se borrará el cuadro de playoffs y se armará de nuevo con la configuración actual. ${publishedCount} partido(s) ya tienen horario publicado: se quitarán y tendrás que volver a programarlos y publicarlos. ¿Rehacer el cuadro?`
        : "Se borrará el cuadro de playoffs y se armará de nuevo con la configuración actual (cruces, seeds y 3er lugar). Las fechas y canchas que hayas puesto a los playoffs se pierden. ¿Rehacer el cuadro?",
      { acceptLabel: "Sí, rehacer cuadro", dismissLabel: "No, dejarlo como está" },
    );
    if (!confirmed) return;
    beginGeneratingOp();
    setError(null);
    setMessage(null);
    await runMatchesMutation(getMatchesLifecycle(), {
      mutate: async (checkpoint) => {
        const resp = await api.matches.regenerateRoundRobinPlayoffs(token, tournamentId, {
          division_id: selectedDivisionId,
          confirm_published: publishedCount > 0,
        });
        checkpoint();
        setMessage(`Cuadro de playoffs rehecho: ${resp.data.created} partido(s) con la configuración actual.`);
      },
      onError: (err) => {
        setError(err instanceof ApiError ? err.message || "No se pudo rehacer el cuadro de playoffs." : "Error de red.");
      },
      reload: (reloadOpts) => load({ keepError: reloadOpts.keepError }),
      finish: () => endGeneratingOp(),
    });
  }

  async function handleFillPlayoffPlaceholders() {
    if (!token) return;
    beginGeneratingOp();
    setError(null);
    setMessage(null);
    await runMatchesMutation(getMatchesLifecycle(), {
      mutate: async (checkpoint) => {
        const resp = await api.matches.fillRoundRobinPlayoffPlaceholders(token, tournamentId, {
          division_id: selectedDivisionId,
        });
        checkpoint();
        setMessage(
          resp.data.filled > 0
            ? `Playoffs llenados con los clasificados: ${resp.data.filled} partido(s). Fechas, horas y canchas se conservaron.`
            : "Los playoffs ya tenían a sus clasificados.",
        );
      },
      onError: (err) => {
        setError(err instanceof ApiError ? err.message || "No se pudieron llenar los playoffs." : "Error de red.");
      },
      reload: (reloadOpts) => load({ keepError: reloadOpts.keepError }),
      finish: () => endGeneratingOp(),
    });
  }

  async function handleGenerateRoundRobinPlayoffs() {
    if (!token) return;
    beginGeneratingOp();
    setError(null);
    setMessage(null);
    await runMatchesMutation(getMatchesLifecycle(), {
      mutate: async (checkpoint) => {
          const resp = await api.matches.generateRoundRobinPlayoffs(token, tournamentId, {
            division_id: selectedDivisionId,
          });
          checkpoint();
          const summary = resp.data;
          const labels = summary.round_labels?.join(", ") || `Jornada ${summary.start_jornada}`;
          if (summary.created > 0) {
            setMessage(
              `Playoffs generados: ${summary.playoff_players_count} clasificados · ${summary.created} enfrentamiento(s) en ${labels}. Semifinales sugeridas por resultados y final preparada para ganadores.`,
            );
          } else if ((summary.updated ?? 0) > 0) {
            setMessage(`Playoffs actualizados: ${summary.updated} final(es) enlazada(s) con ganadores.`);
          } else {
            setMessage(`Playoffs ya estaban generados: ${summary.skipped} enfrentamiento(s) en ${labels}.`);
          }
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          setError(err.message || "No se pudieron armar los playoffs.");
        } else {
          setError("Error de red.");
        }
      },
      reload: (reloadOpts) => load({ keepError: reloadOpts.keepError }),
      finish: () => endGeneratingOp(),
    });
  }

  async function handleGenerateElimination(
    generationMode: "ranking" | "random" | "manual" = eliminationConfig?.default_generation_mode ?? "ranking",
    regenerateEmpty = false,
    participantIdsOrder?: number[],
  ) {
    if (!token) return;
    beginGeneratingOp();
    setError(null);
    setMessage(null);
    await runMatchesMutation(getMatchesLifecycle(), {
      mutate: async (checkpoint) => {
          const resp = await api.matches.generateElimination(token, tournamentId, {
            division_id: selectedDivisionId,
            generation_mode: generationMode,
            regenerate_empty: regenerateEmpty,
            participant_ids_order: participantIdsOrder,
          });
          checkpoint();
          const summary = resp.data;
          let keepWarningOnReload = false;
          if (summary.already_had_matches) {
            setMessage(`La eliminatoria ya tiene ${summary.skipped} enfrentamiento(s) generado(s).`);
          } else {
            const advancers = summary.direct_advancer_count ?? 0;
            const advanceText = advancers > 0
              ? ` · ${advancers} jugador(es) pasan directo al cuadro de ${summary.next_round_size ?? summary.draw_size}`
              : "";
            const adjustText = summary.draw_size_auto_adjusted
              ? ` (ajustamos el cuadro a Ronda de ${summary.draw_size} porque el tamaño configurado, ${summary.requested_draw_size}, era muy grande para ${summary.created * 2} jugador(es))`
              : "";
            setMessage(
              `Eliminatoria armada ${summary.generation_mode === "random" ? "random" : summary.generation_mode === "manual" ? "manual" : "por ranking"}: ${summary.created} enfrentamiento(s) en ${summary.round_label ?? "primera ronda"}${advanceText}${adjustText}. Ahora sí puedes calendarizar los partidos reales.`,
            );
            // Guard anti-silencio (caso dobles t52 2026-07-30): pediste
            // sorteo POR RANKING pero nadie tenía rank al generar — el
            // cuadro salió 100% al azar (el 3 vs 4 en primera ronda) y no
            // había ninguna señal. Avisar en grande para rearmar a tiempo.
            if (
              summary.generation_mode === "ranking"
              && summary.draw_ceremony
              // Estricto: sin dato (null) NO es "cero sembrados".
              && summary.draw_ceremony.seed_count === 0
            ) {
              const fuente = summary.seeding_source ?? "initial_rank";
              const fuenteLabel =
                fuente === "manual" ? "Manual" : fuente === "current_rank" ? "Ranking actual" : "Ranking inicial";
              const consejo =
                fuente === "manual"
                  ? "La fuente Manual toma las posiciones del acomodo manual y nadie tiene posición asignada — cambia el Seeding a Ranking inicial en Formato (o asigna posiciones) y rearma."
                  : "Captura los seeds en el paso Ranking y usa Rearmar draw antes de calendarizar.";
              setError(
                `⚠️ El draw se sorteó SIN sembrados (fuente de seeding configurada: ${fuenteLabel}): todas las líneas salieron al azar. ${consejo}`,
              );
              // El aviso debe SOBREVIVIR al reload de la mutación — sin esto,
              // load({keepError:false}) lo borra al instante y el admin no lo
              // ve (bloqueo del gate #154; patrón keepErrorOnReload
              // documentado en matchesProducer.test).
              keepWarningOnReload = true;
            }
            // Esqueleto del cuadro: crea también las rondas futuras como
            // placeholders para poder programarlas desde el día 1. Best-effort:
            // si el cuadro no es potencia de dos se completa manualmente.
            try {
              await api.matches.materializeEliminationSkeleton(token, tournamentId, {
                division_id: selectedDivisionId,
              });
            } catch {
              // Sin esqueleto el flujo clásico (crear ronda por ronda) sigue igual.
            }
          }
          if (keepWarningOnReload) return { keepErrorOnReload: true };
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          setError(err.message || "No se pudo armar la eliminatoria.");
        } else {
          setError("Error de red.");
        }
      },
      reload: (reloadOpts) => load({ keepError: reloadOpts.keepError }),
      finish: () => endGeneratingOp(),
    });
  }

  async function handleGenerateEliminationNextRound() {
    if (!token) return;
    beginGeneratingOp();
    setError(null);
    setMessage(null);
    await runMatchesMutation(getMatchesLifecycle(), {
      mutate: async (checkpoint) => {
          const resp = await api.matches.generateEliminationNextRound(token, tournamentId, {
            division_id: selectedDivisionId,
          });
          checkpoint();
          const summary = resp.data;
          const labels = summary.round_labels?.map(eliminationRoundDisplayLabel).join(", ") || "siguiente ronda";
          if (summary.created > 0) {
            setMessage(
              `Siguiente ronda generada desde ${eliminationRoundDisplayLabel(summary.from_round_label)}: ${summary.created} enfrentamiento(s) en ${labels}.`,
            );
          } else if ((summary.updated ?? 0) > 0) {
            setMessage(
              `Cuadro avanzado desde ${eliminationRoundDisplayLabel(summary.from_round_label)}: ${summary.updated} enfrentamiento(s) de ${labels} rellenado(s) con los clasificados (la programación previa se conserva).`,
            );
          } else {
            setMessage(`La siguiente ronda ya estaba armada: ${summary.skipped} enfrentamiento(s) en ${labels}.`);
          }
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          setError(err.message || "No se pudo generar la siguiente ronda.");
        } else {
          setError("Error de red.");
        }
      },
      reload: (reloadOpts) => load({ keepError: reloadOpts.keepError }),
      finish: () => endGeneratingOp(),
    });
  }

  async function handleMaterializeEliminationSkeleton() {
    if (!token) return;
    beginGeneratingOp();
    setError(null);
    setMessage(null);
    await runMatchesMutation(getMatchesLifecycle(), {
      mutate: async (checkpoint) => {
          const resp = await api.matches.materializeEliminationSkeleton(token, tournamentId, {
            division_id: selectedDivisionId,
          });
          checkpoint();
          const summary = resp.data;
          if (summary.created > 0) {
            const labels = summary.round_labels.map(eliminationRoundDisplayLabel).join(", ");
            setMessage(
              `Cuadro completado: ${summary.created} partido(s) creados en ${labels}. Ya puedes programar fecha, hora y cancha de cualquier ronda en Calendario; los nombres se rellenan solos al avanzar el bracket.`,
            );
          } else {
            setMessage("El cuadro ya tiene todas sus rondas creadas.");
          }
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          setError(err.message || "No se pudo completar el cuadro.");
        } else {
          setError("Error de red.");
        }
      },
      reload: (reloadOpts) => load({ keepError: reloadOpts.keepError }),
      finish: () => endGeneratingOp(),
    });
  }

  function setRoundRobinEdit(match: TournamentMatch, field: keyof RoundRobinEdit, value: string) {
    setRoundRobinEdits((current) => {
      const existing = current[match.id] ?? editFromRoundRobinMatch(match);
      const next = { ...existing, [field]: value };
      if (field === "groupId") {
        next.player1 = "";
        next.player2 = "";
      }
      return { ...current, [match.id]: next };
    });
  }

  async function saveRoundRobinMatch(match: TournamentMatch) {
    if (!token) return;
    const edit = roundRobinEdits[match.id] ?? editFromRoundRobinMatch(match);
    const nextRoundNumber = Number(edit.roundNumber);
    const nextGroupId = Number(edit.groupId);
    const nextPlayer1 = Number(edit.player1);
    const nextPlayer2 = Number(edit.player2);

    const isPlayoffRound = roundRobinConfig !== null && nextRoundNumber > roundRobinConfig.round_count;

    if (!Number.isFinite(nextRoundNumber) || nextRoundNumber <= 0) {
      setError("Selecciona una jornada válida para el matchup.");
      return;
    }
    if ((!Number.isFinite(nextGroupId) || nextGroupId <= 0) && !isPlayoffRound) {
      setError("Selecciona un grupo válido para el matchup.");
      return;
    }
    if (!Number.isFinite(nextPlayer1) || !Number.isFinite(nextPlayer2) || nextPlayer1 <= 0 || nextPlayer2 <= 0) {
      setError("Selecciona ambos jugadores para guardar el matchup.");
      return;
    }
    if (nextPlayer1 === nextPlayer2) {
      setError("Un jugador no puede enfrentarse contra sí mismo.");
      return;
    }

    setSavingRoundRobinMatchId(match.id);
    setError(null);
    setMessage(null);
    try {
      await api.matches.update(token, tournamentId, match.id, {
        round_label: `Jornada ${nextRoundNumber}`,
        group_id: nextGroupId > 0 ? nextGroupId : null,
        player1_participant_id: nextPlayer1,
        player2_participant_id: nextPlayer2,
      });
      setMessage(`Partido #${match.match_number ?? match.id} guardado en Jornada ${nextRoundNumber}.`);
      await load();
      setSelectedRoundNumber(nextRoundNumber);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo guardar el matchup.");
      } else {
        setError("Error de red.");
      }
    } finally {
      setSavingRoundRobinMatchId(null);
    }
  }

  async function handleBracketSwapClick(matchId: number) {
    if (!token) return;
    if (swapSelectionId === null) {
      setSwapSelectionId(matchId);
      setMessage("Enfrentamiento seleccionado. Ahora toca el enfrentamiento de la MISMA ronda con el que quieres intercambiar posición.");
      return;
    }
    if (swapSelectionId === matchId) {
      setSwapSelectionId(null);
      setMessage(null);
      return;
    }
    const firstId = swapSelectionId;
    const signal = currentSignal();
    setSwapSelectionId(null);
    setError(null);
    setMessage(null);
    // Orden de rondas (revisión 2026-10-03): si con el intercambio un partido
    // de la siguiente ronda ya programado empezaría antes de que termine su
    // nuevo previo, el servidor responde 409 `round_order_conflict` y no
    // cambia nada. Se pregunta (tras el clic del admin) y, si confirma, se
    // reenvía «de todos modos», como «Confirmar de todos modos» del calendario.
    let roundOrderMessage: string | null = null;
    const swap = async (allowRoundOrderConflict: boolean) =>
      // El swap muta el cuadro (11º-13º gates): la mutación abre ANTES de la
      // API — el acta no puede imprimirse con las líneas previas al
      // intercambio, ni re-estamparse mientras otra mutación siga abierta.
      runMatchesMutation(getMatchesLifecycle(), {
        mutate: async (checkpoint) => {
          try {
            await api.matches.swapMatchups(token, tournamentId, {
              match_a_id: firstId,
              match_b_id: matchId,
              ...(allowRoundOrderConflict ? { allow_round_order_conflict: true } : {}),
            });
          } catch (err) {
            if (!allowRoundOrderConflict && err instanceof ApiError && err.code === "round_order_conflict") {
              checkpoint();
              roundOrderMessage = err.message;
              return;
            }
            throw err;
          }
          checkpoint();
          setMessage("Posiciones intercambiadas en el cuadro.");
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            setError(err.message || "No se pudieron intercambiar los matchups.");
          } else {
            setError("Error de red. Intenta otra vez.");
          }
        },
        reload: (reloadOpts) => load({ keepError: reloadOpts.keepError }),
      });
    await swap(false);
    if (roundOrderMessage === null) return;
    const confirmed = await askConfirm(
      `${roundOrderMessage}\n¿Intercambiar de todos modos? Después puedes mover sus horarios en el calendario.`,
      { acceptLabel: "Sí, intercambiar de todos modos", dismissLabel: "No, dejarlos como están", signal },
    );
    if (!confirmed || signal?.aborted) return;
    await swap(true);
  }

  // Captura desde el cuadro (Eduardo 2026-09-12): Resultados abre la hoja
  // de captura de ese partido (móvil) o lo enfoca (escritorio).
  function captureResultPath(matchId: number): string {
    const division = activeDivision ? `division=${activeDivision.id}&` : "";
    return `/admin/tournaments/${tournamentId}/results?${division}capturar=${matchId}`;
  }

  function openEliminationMatchEditor(matchId: number) {
    setEditingEliminationMatchIds((current) => new Set(current).add(matchId));
    window.setTimeout(() => {
      const element = document.getElementById(`elimination-match-${matchId}`);
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
      element?.focus({ preventScroll: true });
    }, 0);
  }

  function updateEliminationVisualZoom(delta: number) {
    setEliminationVisualZoom((current) => Math.max(0.5, Math.min(1.4, Number((current + delta).toFixed(2)))));
  }

  function printEliminationVisual(targetNode = eliminationVisualRef.current, label = "Draw eliminatoria") {
    const node = targetNode;
    if (!node) {
      setError("No encontré el draw para imprimir. Vuelve a abrir la sección de Draw e inténtalo de nuevo.");
      return;
    }
    try {
      openPrintWindow({
        title: `${label} · ${tournament?.name ?? "Torneos123"}`,
        eyebrow: label,
        heading: tournament?.name ?? "Torneos123",
        subtitle: activeDivision?.name,
        bodyHtml: node.outerHTML,
        landscape: true,
        fitToPage: true,
        includeAppStyles: true,
        organizerSlot: true,
        // Eduardo 2026-09-24: aquí va el LOGO DEL ORGANIZADOR, no el del torneo.
        organizerLogoUrl: uploadAssetHref(tournament?.organizer_logo_url),
        extraCss: `
          /* Strip the on-screen card chrome / tools; printTemplate scales the
             draw to fit one letter-landscape page (by width AND height). */
          .elimination-visual { width: max-content !important; max-width: none !important; overflow: visible !important; margin: 0 !important; padding: 0 !important; border: 0 !important; background: #fff !important; box-shadow: none !important; }
          .elimination-visual__tools,
          .elimination-visual__header,
          .elimination-visual__legend { display: none !important; }
          .elimination-visual__viewport { width: max-content !important; max-width: none !important; overflow: visible !important; max-height: none !important; }
          .elimination-visual__bracket { width: max-content !important; min-width: 0 !important; max-width: none !important; overflow: visible !important; zoom: 1 !important; transform: none !important; padding: 0 !important; margin: 0 !important; }
          .bracket-pro,
          .elimination-draw-sheet { max-width: none !important; overflow: visible !important; margin: 0 !important; }
          /* Ronda 32: each first-round slot occupies one grid row. The on-screen
             compact/mobile row height can be shorter than the two-line card,
             so Safari/print lets boxes visually overlap. In the print window,
             make the grid row at least as tall as a match card, then let
             printTemplate scale the whole sheet to one landscape letter page. */
          .elimination-draw-sheet {
            --elimination-draw-column-width: 9.2rem !important;
            --elimination-draw-row-height: 2.12rem !important;
            --elimination-draw-gap: 0.62rem !important;
            --elimination-draw-left-gutter: 0.42rem !important;
            padding: 0.45rem 0.5rem 0.55rem !important;
          }
          .elimination-draw-sheet__round-headings { margin-bottom: 0.28rem !important; font-size: 0.58rem !important; }
          .elimination-draw-slot,
          .elimination-draw-slot.is-placeholder {
            box-sizing: border-box !important;
            min-height: 0 !important;
            height: calc(var(--elimination-slot-span) * var(--elimination-draw-row-height) - 0.14rem) !important;
            padding: 0.2rem 0.28rem !important;
            border-radius: 0.24rem !important;
            gap: 0.04rem !important;
          }
          .elimination-draw-slot__line { min-height: 0.66rem !important; font-size: 0.58rem !important; }
          .elimination-draw-slot__meta,
          .elimination-draw-slot__result { font-size: 0.42rem !important; }
          .elimination-draw-slot__wl { width: 0.76rem !important; height: 0.76rem !important; font-size: 0.42rem !important; }
        `,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo abrir la impresión.");
    }
  }

  // ── «Acomodar grupos»: subir/bajar y cambiar de grupo (Eduardo 2026-10-01) ──

  async function handleToggleArrange() {
    if (!arranging) {
      if (arrangeDisabledReason !== null || generating || !arrangementStatus) return;
      setArrangeDrafts({});
      setArrangePreview({});
      setArranging(true);
      return;
    }
    if (arrangeBusyGroupId !== null) return;
    if (arrangeDirtyGroupIds.length > 0) {
      const names = arrangeDirtyGroupIds.map((groupId) => groupsById.get(groupId)?.name ?? `Grupo ${groupId}`);
      const accepted = await askConfirm(
        `Tienes cambios sin guardar en el orden del ${joinSpanishList(names)}. ¿Salir sin guardar?`,
        { acceptLabel: "Sí, salir sin guardar" },
      );
      if (!accepted) return;
    }
    setArranging(false);
    setArrangeDrafts({});
    setArrangePreview({});
  }

  /** Registra un control de «Acomodar grupos» para devolverle el foco. */
  function arrangeFocusRef(key: string) {
    return (element: HTMLElement | null) => {
      if (element) arrangeFocusTargetsRef.current.set(key, element);
      else arrangeFocusTargetsRef.current.delete(key);
    };
  }

  function handleArrangeMove(groupId: number, index: number, delta: -1 | 1) {
    if (arrangeBusyGroupId !== null) return;
    const current = arrangeDraftFor(groupId);
    const next = moveInList(current, index, delta);
    setArrangeDrafts((prev) => ({ ...prev, [groupId]: next }));
    // Un orden distinto invalida la revisión de jornadas de ese grupo.
    setArrangePreview((prev) => ({ ...prev, [groupId]: null }));
    // El foco sigue al jugador: la misma flecha, o la otra si ya llegó al
    // extremo (la que apretó queda deshabilitada).
    const participantId = current[index];
    if (participantId === undefined) return;
    const target = index + delta;
    const atEnd = delta < 0 ? target <= 0 : target >= next.length - 1;
    const direction = (delta < 0) !== atEnd ? "up" : "down";
    arrangePendingFocusRef.current = `move:${groupId}:${participantId}:${direction}`;
  }

  function handleArrangeUndo(groupId: number) {
    if (arrangeBusyGroupId !== null) return;
    setArrangeDrafts((prev) => {
      const next = { ...prev };
      delete next[groupId];
      return next;
    });
    setArrangePreview((prev) => ({ ...prev, [groupId]: null }));
  }

  function showArrangeError(err: unknown, groupId: number, fallback: string) {
    // Cualquier rechazo (revisión vieja, lista distinta, categoría ya
    // empezada…) obliga a volver a revisar antes de guardar.
    setArrangePreview((prev) => ({ ...prev, [groupId]: null }));
    if (err instanceof ApiError) {
      setError(err.message || fallback);
    } else {
      setError("Error de red. Intenta otra vez.");
    }
  }

  /** «Guardar orden»: primero la revisión del backend. Sin partidos (o sin
   * cambios en ellos) basta confirmar; si cambian rivales, se muestra cómo
   * quedarían las jornadas antes de guardar. */
  async function handleArrangeSave(groupId: number) {
    if (!token || arrangeBusyGroupId !== null) return;
    if (roundRobinHasPendingEdits) {
      setError("Guarda o cancela primero los cambios de la tabla de partidos.");
      return;
    }
    const draft = arrangeDraftFor(groupId);
    const epoch = arrangeEpochRef.current;
    const signal = currentSignal();
    setArrangeBusyGroupId(groupId);
    setError(null);
    setMessage(null);
    let preview: GroupOrderPreview;
    try {
      preview = (await api.groups.orderPreview(token, tournamentId, groupId, draft)).data;
    } catch (err) {
      if (epoch !== arrangeEpochRef.current) return;
      setArrangeBusyGroupId(null);
      showArrangeError(err, groupId, "No se pudo revisar el orden.");
      // Candado: se recarga para que el aviso y los botones lo reflejen.
      if (err instanceof ApiError && err.code === "rr_arrangement_locked") void load({ keepError: true });
      return;
    }
    if (epoch !== arrangeEpochRef.current) return;
    if (preview.locked || !preview.plan_token) {
      setArrangeBusyGroupId(null);
      setError(preview.lock?.message || ARRANGE_LOCK_FALLBACK);
      void load({ keepError: true });
      return;
    }
    if (!preview.has_matches || !preview.changes_matches || preview.manual_schedule) {
      const accepted = await askConfirm(preview.summary, { acceptLabel: "Sí, guardar orden", signal });
      if (epoch !== arrangeEpochRef.current) return;
      if (!accepted) {
        setArrangeBusyGroupId(null);
        return;
      }
      await applyArrangeOrder(groupId, draft, preview.plan_token);
      return;
    }
    setArrangeBusyGroupId(null);
    setArrangePreview((prev) => ({ ...prev, [groupId]: preview }));
    // La revisión sustituye al pie con «Guardar orden»: el foco va a su título.
    arrangePendingFocusRef.current = `preview:${groupId}`;
  }

  /** «Guardar este orden» desde la revisión de jornadas. */
  async function handleArrangeConfirmPreview(groupId: number) {
    const preview = arrangePreview[groupId];
    if (!preview?.plan_token || arrangeBusyGroupId !== null) return;
    const epoch = arrangeEpochRef.current;
    const accepted = await askConfirm(preview.summary, {
      acceptLabel: "Sí, guardar orden",
      tone: preview.counts.removed > 0 ? "danger" : "default",
    });
    if (!accepted || epoch !== arrangeEpochRef.current) return;
    await applyArrangeOrder(groupId, preview.participant_ids, preview.plan_token);
  }

  async function applyArrangeOrder(groupId: number, participantIds: number[], planToken: string) {
    if (!token) return;
    setArrangeBusyGroupId(groupId);
    setError(null);
    setMessage(null);
    let saved = false;
    await runMatchesMutation(getMatchesLifecycle(), {
      mutate: async (checkpoint) => {
        await api.groups.saveOrder(token, tournamentId, groupId, {
          participant_ids: participantIds,
          plan_token: planToken,
        });
        checkpoint();
        saved = true;
        setArrangePreview((prev) => ({ ...prev, [groupId]: null }));
        setMessage("Orden guardado.");
      },
      onError: (err) => showArrangeError(err, groupId, "No se pudo guardar el orden."),
      // El reload trae el orden nuevo y el candado vigente (un
      // rr_arrangement_locked se refleja en el aviso y los botones).
      reload: (reloadOpts) => load({ keepError: reloadOpts.keepError }),
      finish: () => {
        // Ya con los grupos frescos: el borrador guardado es el orden vigente.
        if (saved) {
          setArrangeDrafts((prev) => {
            const next = { ...prev };
            delete next[groupId];
            return next;
          });
        }
        setArrangeBusyGroupId((current) => (current === groupId ? null : current));
      },
    });
  }

  async function handleChangeGroupDone(fromGroupId: number, toGroupId: number, notice: string | null = null) {
    setChangeGroupFor(null);
    // Solo cambian los dos grupos del cambio: el orden sin guardar de los
    // demás se conserva (sus miembros siguen siendo los mismos).
    const touched = new Set([fromGroupId, toGroupId]);
    setArrangeDrafts((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([groupId]) => !touched.has(Number(groupId)))),
    );
    setArrangePreview((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([groupId]) => !touched.has(Number(groupId)))),
    );
    setError(null);
    // Eduardo 2026-10-13: el cambio rehízo los partidos de los dos grupos y
    // los dejó sin programar; se dice cuáles para volver a programarlos.
    setMessage(notice ? `Cambio de grupo guardado. ${notice}` : "Cambio de grupo guardado.");
    await load({ keepError: false });
  }

  /** Un grupo en modo «Acomodar grupos»: la hoja con su lugar, ↑/↓ y
   * «Cambiar de grupo…» por jugador (sustituye a la matriz mientras dura). */
  function renderArrangeGroup(visual: RoundRobinVisualGroup, group: TournamentGroup) {
    const initial = arrangeInitialOrders[visual.id] ?? [];
    const draft = arrangeDraftFor(visual.id);
    const dirty = !sameOrder(draft, initial);
    // Sin lugares guardados ni partidos: el orden que se ve todavía NO es el
    // del generador (ese va por orden de inscripción). Se puede guardar tal
    // cual, sin moverle nada.
    const unsavedShownOrder = !group.rr_has_positions && !group.rr_sheet_order;
    const canSave = dirty || unsavedShownOrder;
    const busy = arrangeBusyGroupId !== null;
    const savingThis = arrangeBusyGroupId === visual.id;
    const preview = arrangePreview[visual.id] ?? null;
    const participantsById = new Map(visual.participants.map((participant) => [participant.id, participant]));
    const hasOtherActiveGroup = groups.some(
      (candidate) =>
        candidate.is_active
        && candidate.id !== group.id
        && (candidate.division_id ?? null) === (group.division_id ?? null),
    );
    // Por qué «Cambiar de grupo…» está apagado en este grupo, a la vista (en
    // el teléfono no hay «hover» para leer el title).
    const changeGroupReason = !hasOtherActiveGroup
      ? "No hay otro grupo activo en esta categoría para cambiar jugadores."
      : roundRobinHasPendingEdits
        ? "Para cambiar a alguien de grupo, guarda o cancela primero los cambios de la tabla de partidos."
        : dirty
          ? "Para cambiar a alguien de grupo, guarda o deshaz primero el orden."
          : null;
    return (
      <details key={visual.id} className="round-robin-visual__group round-robin-visual__group--arrange" open>
        <summary>
          <span>{visual.name}</span>
          <small>
            {draft.length} participante{draft.length === 1 ? "" : "s"}
            {dirty ? " · orden sin guardar" : ""}
          </small>
        </summary>
        <div className="round-robin-arrange__body">
          {!group.is_active ? (
            <p className="muted" data-no-toast>
              Este grupo está desactivado: no se acomoda.
            </p>
          ) : (
            <>
              <div className="round-robin-arrange__notes" data-no-toast>
                <p className="muted">El lugar 1 y el 2 se enfrentan en la última jornada.</p>
                {!group.rr_has_positions && group.rr_sheet_order ? (
                  <p className="muted">Este es el orden con el que se armaron las jornadas.</p>
                ) : null}
                {unsavedShownOrder ? (
                  <p className="muted">
                    Este orden todavía no está guardado: si no lo guardas, las jornadas se arman por orden de
                    inscripción.
                  </p>
                ) : null}
                {changeGroupReason ? <p className="muted">{changeGroupReason}</p> : null}
              </div>
              <ol className="round-robin-arrange" aria-label={`Orden de la hoja del ${visual.name}`}>
                {draft.map((participantId, index) => {
                  const participant = participantsById.get(participantId);
                  const fullName = participant ? participantDisplayName(participant) : `#${participantId}`;
                  const shortName = participant ? compactParticipantDisplayName(participant) : fullName;
                  return (
                    <li key={participantId} className="round-robin-arrange__item">
                      <span className="round-robin-arrange__place" title={RR_SHEET_PLACE_TOOLTIP}>
                        {index + 1}
                      </span>
                      <span className="round-robin-arrange__name" title={fullName}>
                        {shortName}
                      </span>
                      <span className="round-robin-arrange__moves">
                        <button
                          type="button"
                          className="round-robin-arrange__move"
                          aria-label={`Subir ${fullName}`}
                          ref={arrangeFocusRef(`move:${visual.id}:${participantId}:up`)}
                          disabled={busy || index === 0}
                          onClick={() => handleArrangeMove(visual.id, index, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="round-robin-arrange__move"
                          aria-label={`Bajar ${fullName}`}
                          ref={arrangeFocusRef(`move:${visual.id}:${participantId}:down`)}
                          disabled={busy || index === draft.length - 1}
                          onClick={() => handleArrangeMove(visual.id, index, 1)}
                        >
                          ↓
                        </button>
                      </span>
                      <button
                        type="button"
                        className="btn btn--ghost btn--compact round-robin-arrange__change"
                        aria-label={`Cambiar a ${fullName} de grupo`}
                        disabled={busy || dirty || !hasOtherActiveGroup || roundRobinHasPendingEdits}
                        title={
                          dirty
                            ? "Guarda o deshaz el orden primero"
                            : !hasOtherActiveGroup
                              ? "No hay otro grupo activo en esta categoría."
                              : roundRobinHasPendingEdits
                                ? "Guarda o cancela primero los cambios de la tabla de partidos."
                                : undefined
                        }
                        onClick={() => setChangeGroupFor({ participantId, groupId: group.id })}
                      >
                        Cambiar de grupo…
                      </button>
                    </li>
                  );
                })}
              </ol>
              {preview ? (
                <div className="round-robin-arrange__preview" data-no-toast>
                  <h4 tabIndex={-1} ref={arrangeFocusRef(`preview:${visual.id}`)}>
                    Así quedarían las jornadas
                  </h4>
                  <p className="round-robin-arrange__summary">{preview.summary}</p>
                  {preview.warnings.map((warning) => (
                    <p key={warning} className="field-warning">
                      {warning}
                    </p>
                  ))}
                  <RoundRobinPreviewJornadas jornadas={preview.jornadas} />
                  <div className="round-robin-arrange__footer">
                    <button
                      type="button"
                      className="btn btn--small"
                      disabled={busy}
                      onClick={() => void handleArrangeConfirmPreview(visual.id)}
                    >
                      {savingThis ? "Guardando…" : "Guardar este orden"}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--small"
                      disabled={busy}
                      onClick={() => {
                        setArrangePreview((prev) => ({ ...prev, [visual.id]: null }));
                        // De regreso al pie: el foco vuelve a «Guardar orden».
                        arrangePendingFocusRef.current = `save:${visual.id}`;
                      }}
                    >
                      Seguir acomodando
                    </button>
                  </div>
                </div>
              ) : (
                <div className="round-robin-arrange__footer">
                  <button
                    type="button"
                    className="btn btn--small"
                    ref={arrangeFocusRef(`save:${visual.id}`)}
                    disabled={!canSave || busy}
                    onClick={() => void handleArrangeSave(visual.id)}
                  >
                    {savingThis ? "Revisando…" : dirty || !unsavedShownOrder ? "Guardar orden" : "Guardar este orden"}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    disabled={!dirty || busy}
                    onClick={() => handleArrangeUndo(visual.id)}
                  >
                    Deshacer cambios
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </details>
    );
  }

  /** «Cambiar de grupo…»: mismo diálogo que en Grupos. Los grupos destino
   * son los activos de la categoría, con sus miembros en el orden de la hoja. */
  function renderChangeGroupDialog(target: { participantId: number; groupId: number }) {
    const source = groupsById.get(target.groupId);
    if (!source || !token) return null;
    const participant =
      roundRobinVisualGroups
        .find((visual) => visual.id === source.id)
        ?.participants.find((item) => item.id === target.participantId)
      ?? participants.find((item) => item.id === target.participantId)
      ?? null;
    const targetGroups: RoundRobinChangeGroupTarget[] = groups
      .filter(
        (candidate) =>
          candidate.is_active
          && candidate.id !== source.id
          && (candidate.division_id ?? null) === (source.division_id ?? null),
      )
      .map((candidate) => {
        const namesById = new Map<number, string>();
        for (const assignment of candidate.participants ?? []) {
          if (assignment.participant) namesById.set(assignment.participant_id, participantDisplayName(assignment.participant));
        }
        const order = arrangeInitialOrders[candidate.id] ?? initialArrangeDraft(candidate, [], new Set());
        return {
          id: candidate.id,
          name: candidate.name,
          members: order.map((id) => ({ id, name: namesById.get(id) ?? `#${id}` })),
        };
      });
    return (
      <RoundRobinChangeGroupDialog
        token={token}
        tournamentId={tournamentId}
        participant={{
          id: target.participantId,
          name: participant ? participantDisplayName(participant) : `#${target.participantId}`,
        }}
        fromGroup={{ id: source.id, name: source.name }}
        targetGroups={targetGroups}
        onDone={({ toGroupId, notice }) => void handleChangeGroupDone(source.id, toGroupId, notice)}
        onClose={() => setChangeGroupFor(null)}
      />
    );
  }

  /** Póster del Draw (Eduardo 2026-10-09): doble carta horizontal, una hoja
   * por grupo, para pegar en la pared del club. */
  async function printDrawPoster() {
    if (!token) return;
    let target: ReturnType<typeof openDrawPosterWindow>;
    try {
      target = openDrawPosterWindow();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo abrir la ventana de impresión.");
      return;
    }
    try {
      const divisionId = activeDivision?.id ?? selectedDivisionId ?? null;
      const [detail, standingsResp] = await Promise.all([
        api.tournaments.get(token, tournamentId),
        api.standings.get(token, tournamentId, divisionId !== null ? { division_id: divisionId } : {}),
      ]);
      const standingsByGroup = new Map<number, Map<number, PosterStanding>>();
      for (const group of standingsResp.data.groups ?? []) {
        if (group.group_id == null) continue;
        standingsByGroup.set(
          group.group_id,
          new Map(group.standings.map((row) => [row.participant_id, {
            wins: row.wins,
            losses: row.losses,
            gamesWon: row.games_won,
            gamesLost: row.games_lost,
          }])),
        );
      }
      const overview = jornadaOverview?.tournamentId === tournamentId ? jornadaOverview.data : null;
      const windows = overview?.saved_windows ?? [];
      const posterGroups: PosterGroup[] = roundRobinVisualGroups.map((group) => ({
        name: group.name,
        players: group.participants.map((participant) => ({
          id: participant.id,
          name: participantDisplayName(participant),
          club: participant.player?.club_name ?? null,
          dropped: participant.current_status === "dropped",
        })),
        standings: standingsByGroup.get(group.id) ?? new Map(),
        matches: roundRobinMatches.flatMap((match) => {
          const edit = roundRobinEdits[match.id];
          const groupId = Number(edit?.groupId ?? match.group_id ?? 0);
          const player1Id = Number(edit?.player1 ?? match.player1_participant_id ?? 0);
          const player2Id = Number(edit?.player2 ?? match.player2_participant_id ?? 0);
          if (groupId !== group.id || player1Id <= 0 || player2Id <= 0 || player1Id === player2Id) return [];
          const roundNumber = roundRobinEffectiveRoundNumber(match, edit);
          return [{
            id: match.id,
            roundNumber,
            status: String(match.status),
            scheduledDate: match.scheduled_date,
            scheduledTime: match.scheduled_time,
            court: match.court,
            player1Id,
            player2Id,
            winnerId: match.winner_participant_id ?? null,
            scoreText: match.score_text,
            drop: match.match_kind === "tie" ? null : dropMatchState(match),
            jornadaRange: roundNumber !== null ? savedJornadaRange(windows, roundNumber, [divisionId]) : null,
            jornadaShort: roundNumber !== null ? savedJornadaRangeShort(windows, roundNumber, [divisionId]) : null,
          }];
        }),
      }));
      const qualifyText = drawPosterQualifyText(qualificationPreview);
      const sponsorLogos = (detail.data.sponsor_banners ?? []).map((banner) => uploadAssetHref(banner.image_url));
      const t = detail.data.tournament;
      target.write(buildDrawPosterHtml({
        tournamentName: t.name,
        categoryName: activeDivision?.name ?? "",
        formatLabel: "Round Robin · todos contra todos",
        venue: t.venue_name,
        ball: t.official_ball,
        qualifyText,
        organizerLogoUrl: uploadAssetHref(t.organizer_logo_url),
        tournamentLogoUrl: uploadAssetHref(t.logo_url),
        sponsorLogoUrls: sponsorLogos,
        groups: posterGroups,
      }));
    } catch (err) {
      target.fail(err instanceof ApiError ? err.message || "No pudimos armar el póster." : "Error de red. Intenta otra vez.");
    }
  }

  function printRoundRobinVisual() {
    const node = roundRobinVisualRef.current;
    if (!node) {
      setError("No encontré la tabla visual Round Robin para imprimir.");
      return;
    }
    const printScale = roundRobinPrintScale(
      roundRobinVisualGroups.reduce((max, group) => Math.max(max, group.participants.length), 0),
    );
    // Eduardo 2026-10-05: debajo de la tabla de cada grupo, sus jornadas con
    // las fechas guardadas («Jornada 1 · del martes 6 al domingo 11 de
    // octubre de 2026») y quién descansa. Sin fechas ni descansos, como antes.
    const sheet = node.cloneNode(true) as HTMLElement;
    const overview = jornadaOverview?.tournamentId === tournamentId ? jornadaOverview.data : null;
    if (overview) {
      // Las jornadas de TODOS los partidos del grupo, no las de la tabla:
      // `matchesByPair` guarda uno por pareja y, con ida y vuelta, la vuelta
      // tapaba a la ida (sus fechas y quién descansa se perdían).
      const roundNumbersByGroup = new Map<number, number[]>();
      for (const match of roundRobinMatches) {
        const edit = roundRobinEdits[match.id];
        const groupId = Number(edit?.groupId ?? match.group_id ?? 0);
        const roundNumber = roundRobinEffectiveRoundNumber(match, edit);
        if (groupId <= 0 || roundNumber === null) continue;
        roundNumbersByGroup.set(groupId, [...(roundNumbersByGroup.get(groupId) ?? []), roundNumber]);
      }
      for (const group of roundRobinVisualGroups) {
        const legend = groupJornadasPrintHtml({
          roundNumbers: roundNumbersByGroup.get(group.id) ?? [],
          groupId: group.id,
          divisionId: groupsById.get(group.id)?.division_id ?? selectedDivisionId ?? null,
          windows: overview.saved_windows ?? [],
          rests: overview.rest_by_round ?? [],
        });
        if (legend) sheet.querySelector(`[data-rr-group-id="${group.id}"]`)?.insertAdjacentHTML("beforeend", legend);
      }
    }
    try {
      openPrintWindow({
        title: `Round Robin · ${tournament?.name ?? "Torneos123"}`,
        eyebrow: "Round Robin",
        heading: tournament?.name ?? "Torneos123",
        subtitle: activeDivision?.name,
        bodyHtml: sheet.outerHTML,
        landscape: true,
        // A multi-group table must flow across pages (readable), not shrink to one.
        fitToPage: false,
        includeAppStyles: true,
        organizerSlot: true,
        // Eduardo 2026-09-24: aquí va el LOGO DEL ORGANIZADOR, no el del torneo.
        organizerLogoUrl: uploadAssetHref(tournament?.organizer_logo_url),
        extraCss: `
          .round-robin-visual { margin: 0; padding: 0; border: 0; box-shadow: none; }
          .round-robin-visual__header { display: none !important; }
          /* «Acomodar grupos» es solo de pantalla: nunca sale en el papel. */
          .round-robin-arrange, .round-robin-arrange__body, .round-robin-arrange__preview { display: none !important; }
          /* El aviso de partidos que sobran y su etiqueta son del organizador. */
          .rr-stray-notice, .round-robin-matrix__ghost-tag { display: none !important; }
          .round-robin-visual__groups { display: block; }
          /* Un grupo por hoja: nunca se corta un grupo entre páginas. */
          .round-robin-visual__group { break-inside: avoid; page-break-inside: avoid; break-after: page; page-break-after: always; margin: 0 0 12px; overflow: visible !important; }
          .round-robin-visual__group:last-child { break-after: auto; page-break-after: auto; margin-bottom: 0; }
          .round-robin-visual__group > summary { display: flex; padding: 6px 0; }
          .round-robin-visual__wrap { overflow: visible !important; }
          /* Matriz compacta para hoja carta: celdas a contenido, no al viewport. */
          /* En pantalla la tabla mide "max-content" con columnas de 9.6rem y
             scroll horizontal; en papel no hay scroll: ocupa el 100% de la
             hoja y las columnas se reparten (con 7+ participantes se cortaba). */
          .round-robin-matrix { width: 100% !important; min-width: 0 !important; max-width: 100% !important; font-size: ${printScale.base}; table-layout: fixed; }
          .round-robin-matrix thead th:first-child, .round-robin-matrix tbody th { width: ${printScale.nameCol} !important; min-width: 0 !important; max-width: none !important; height: auto !important; }
          .round-robin-matrix thead th:not(:first-child), .round-robin-matrix__cell { width: auto !important; min-width: 0 !important; max-width: none !important; height: auto !important; }
          .round-robin-matrix th, .round-robin-matrix td { white-space: normal !important; overflow-wrap: anywhere; position: static !important; }
          .round-robin-matrix__match { min-height: 2.3rem !important; width: 100%; }
          .round-robin-matrix__match strong { font-size: ${printScale.score} !important; white-space: normal !important; }
          .round-robin-matrix__match span { font-size: ${printScale.detail} !important; max-width: none !important; white-space: normal !important; }
          /* Light-print palette: the on-screen matrix colors assume the dark theme. */
          .round-robin-matrix th, .round-robin-matrix td { color: #10220f !important; }
          .round-robin-matrix__cell { background: #ffffff !important; }
          .round-robin-matrix__cell.is-diagonal { background: repeating-linear-gradient(-45deg, #eef3e7, #eef3e7 2px, #f8fbf4 2px, #f8fbf4 6px) !important; }
          .round-robin-matrix__cell.has-match { background: #f4f9ec !important; }
          .round-robin-matrix__cell.is-current { box-shadow: none !important; }
          .round-robin-matrix__cell.is-won { background: #e9f4dd !important; box-shadow: inset 0 0 0 1px #b9d89e !important; }
          .round-robin-matrix__cell.is-lost { background: #fbeeec !important; box-shadow: inset 0 0 0 1px #ecc7c1 !important; }
          .round-robin-matrix__match strong { color: #1d3414 !important; }
          .round-robin-matrix__match span { color: #57704c !important; }
          .round-robin-matrix__cell.is-won .round-robin-matrix__score { color: #2f6b1a !important; }
          .round-robin-matrix__cell.is-lost .round-robin-matrix__score { color: #a33d2f !important; }
          .rr-print-jornadas { margin: 8px 0 0; font-size: 0.8rem; color: #2e4d22; }
          .rr-print-jornadas ul { margin: 0; padding-left: 1.1rem; }
          .rr-print-jornadas li { margin: 2px 0; }
        `,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo abrir la impresión del Round Robin.");
    }
  }

  function printRoundRobinPlayoffVisual() {
    // Playoffs now renders the same `.bracket-pro` draw sheet as the elimination
    // bracket, so it shares the single unified, page-fitted print path.
    printEliminationVisual(roundRobinPlayoffVisualRef.current, "Bracket de playoffs");
  }

  function setEliminationEdit(match: TournamentMatch, field: keyof EliminationEdit, value: string) {
    setEliminationEdits((current) => {
      const existing = current[match.id] ?? eliminationEditFromMatch(match);
      const next = { ...existing, [field]: value };
      if (field === "groupId") {
        next.player1 = "";
        next.player2 = "";
      }
      return { ...current, [match.id]: next };
    });
  }

  async function saveEliminationMatch(match: TournamentMatch) {
    if (!token) return;
    const edit = eliminationEdits[match.id] ?? eliminationEditFromMatch(match);
    // El select de Grupo MUESTRA groups[0] como fallback cuando el valor está
    // vacío (matches generados con group_id null); el guardado debe resolver
    // el grupo con la MISMA regla o rechaza con "Selecciona un grupo válido"
    // aunque el admin vea "Grupo 1" seleccionado (reporte Mex Tour Cup).
    const nextGroupId = Number(edit.groupId || match.group_id || groups[0]?.id || 0);
    const nextPlayer1 = edit.player1 ? Number(edit.player1) : null;
    const nextPlayer2 = edit.player2 ? Number(edit.player2) : null;

    if (groups.length > 0 && (!Number.isFinite(nextGroupId) || nextGroupId <= 0)) {
      setError("Selecciona un grupo válido para el matchup.");
      return;
    }
    if (nextPlayer1 !== null && nextPlayer2 !== null && nextPlayer1 === nextPlayer2) {
      setError("Un jugador no puede enfrentarse contra sí mismo.");
      return;
    }
    if (nextPlayer1 === null && nextPlayer2 === null) {
      setError("Selecciona al menos un jugador o deja solo un BYE.");
      return;
    }

    const savingOpId = ++savingEliminationOpSeqRef.current;
    setSavingEliminationOp({ matchId: match.id, opId: savingOpId });
    setError(null);
    setMessage(null);
    // Editar un matchup muta el cuadro (11º-13º gates): la mutación abre
    // ANTES de la API.
    await runMatchesMutation(getMatchesLifecycle(), {
      mutate: async (checkpoint) => {
      const byeWinner = nextPlayer1 ?? nextPlayer2;
      const isBye = nextPlayer1 === null || nextPlayer2 === null;
      await api.matches.update(token, tournamentId, match.id, {
        group_id: nextGroupId > 0 ? nextGroupId : null,
        player1_participant_id: nextPlayer1,
        player2_participant_id: nextPlayer2,
        status: isBye ? "completed" : "scheduled",
        winner_participant_id: isBye ? byeWinner : null,
        score_text: isBye ? "BYE" : null,
      });
      checkpoint();
      setMessage(`Enfrentamiento #${match.match_number ?? match.id} guardado.`);
      setEditingEliminationMatchIds((current) => {
        const next = new Set(current);
        next.delete(match.id);
        return next;
      });
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          setError(err.message || "No se pudo guardar el matchup.");
        } else {
          setError("Error de red.");
        }
      },
      reload: (reloadOpts) => load({ keepError: reloadOpts.keepError }),
      // Ownership del spinner de fila POR OPERACIÓN (18º-20º gates): solo
      // se apaga si esta operación exacta sigue siendo la dueña — un
      // guardado stale de la MISMA fila jamás apaga el de uno más nuevo.
      finish: () => setSavingEliminationOp((current) => (current !== null && current.opId === savingOpId ? null : current)),
    });
  }

  if (Number.isNaN(tournamentId)) {
    return (
      <>
        {setupMode && <TournamentSetupProgress current={9} />}
        <section className="card">
          <p className="form-error">ID de torneo inválido.</p>
        </section>
      </>
    );
  }

  // Candado por categoría (post-borrador): sin formato guardado no se opera el Draw.
  if (activeDivision && divisionNeedsFormat(activeDivision)) {
    return (
      <>
        {setupMode && <TournamentSetupProgress current={9} />}
        <DivisionFormatGuardCard tournamentId={tournamentId} division={activeDivision} stepLabel="Draw" />
      </>
    );
  }

  // El "|| !tournament" cubre al load stale abortado en checkpoint: su await
  // también resuelve (y marca hasLoadedOnce) sin haber poblado nada.
  if (loading && (!hasLoadedOnce || !tournament)) {
    return (
      <>
        {setupMode && <TournamentSetupProgress current={9} />}
        <section className="card">
          <p className="muted">Cargando enfrentamientos…</p>
        </section>
      </>
    );
  }

  if (!tournament) {
    return (
      <>
        {setupMode && <TournamentSetupProgress current={9} />}
        <section className="card">
          <p className="form-error">{error ?? "Torneo no encontrado."}</p>
          <Link className="btn btn--ghost" to="/admin/tournaments">
            Volver
          </Link>
        </section>
      </>
    );
  }


  const selectedRound =
    selectedRoundNumber !== null
      ? roundsByNumber.get(selectedRoundNumber) ?? null
      : null;
  const selectedPreviousRoundResultsSummary =
    selectedRoundNumber !== null && selectedRoundNumber > 1
      ? roundResultSummaries.get(selectedRoundNumber - 1) ?? null
      : null;
  const canGenerateSelectedRound =
    selectedRoundNumber !== null
    && isLadder
    && (selectedRoundNumber === 1 || isRoundResultPublished(selectedRoundNumber - 1));
  const selectedDraftHasMatchups =
    !!selectedRound
    && selectedRound.status === "draft"
    && selectedRound.matchups.length > 0;
  const selectedDraftIsEmpty =
    !!selectedRound
    && selectedRound.status === "draft"
    && selectedRound.matchups.length === 0;
  const selectedDraftBlocked =
    !!selectedRound
    && selectedRound.status === "draft"
    && !canGenerateSelectedRound;
  const selectedRoundGenerationBlocked =
    selectedRoundNumber !== null
    && !canGenerateSelectedRound
    && selectedRoundNumber > 1
      ? `Primero publica todos los resultados de la Semana ${selectedRoundNumber - 1}.`
      : null;
  const selectedRoundRobinMatches =
    selectedRoundNumber !== null
      ? roundRobinMatchesByNumber.get(selectedRoundNumber) ?? []
      : [];
  // Eduardo 2026-10-05 («que aparezcan en las jornadas los que descansan»):
  // debajo de la tabla de la jornada elegida, quién descansa en cada grupo de
  // la categoría (el mismo dato del servidor que Calendario y Resultados).
  const selectedRoundRestLines = restScreenLines(
    restsForJornada(
      jornadaOverview?.tournamentId === tournamentId ? jornadaOverview.data.rest_by_round : null,
      selectedRoundNumber,
      new Set(groups.map((group) => group.id)),
    ),
  );
  const normalizedMatchSearchQuery = normalizeSearchText(matchSearchQuery);

  const eliminationRounds = (() => {
    const map = new Map<string, TournamentMatch[]>();
    for (const match of tournamentMatches) {
      if (normalizedMatchSearchQuery) {
        const searchable = normalizeSearchText([
          match.player1?.display_name,
          match.player2?.display_name,
          match.winner?.display_name,
          match.round_label,
          match.score_text,
          match.status,
        ].filter(Boolean).join(" "));
        if (!searchable.includes(normalizedMatchSearchQuery)) continue;
      }
      const label = match.round_label || "Sin ronda";
      const list = map.get(label) ?? [];
      list.push(match);
      map.set(label, list);
    }
    return [...map.entries()]
      .map(([label, matches]) => ({
        label,
        matches: [...matches].sort((a, b) => (a.match_number ?? 0) - (b.match_number ?? 0)),
      }))
      .sort((a, b) => (ELIMINATION_ROUND_ORDER.get(a.label) ?? 99) - (ELIMINATION_ROUND_ORDER.get(b.label) ?? 99));
  })();

  const eliminationVisualDraw = (() => {
    if (normalizedMatchSearchQuery || tournamentMatches.length === 0) {
      return { rounds: [] as EliminationVisualDrawRound[], baseRows: 0 };
    }

    const matchesByLabel = new Map<string, TournamentMatch[]>();
    for (const round of eliminationRounds) {
      matchesByLabel.set(round.label, round.matches);
    }

    const preliminaryMatches = matchesByLabel.get("Preliminar") ?? [];
    const configuredFirstLabel = eliminationRoundLabelForDrawSize(eliminationConfig?.bracket_size ?? 2);
    const configuredFirstMatches = matchesByLabel.get(configuredFirstLabel) ?? [];
    const partialConfiguredRound = preliminaryMatches.length === 0
      && configuredFirstMatches.length > 0
      && configuredFirstMatches.some((match) => (match.notes ?? "").includes("pasan directo al cuadro de"));
    const entryMatches = partialConfiguredRound ? configuredFirstMatches : preliminaryMatches;
    const entryParticipantIds = new Set<number>();
    for (const match of entryMatches) {
      if (match.player1_participant_id !== null) entryParticipantIds.add(match.player1_participant_id);
      if (match.player2_participant_id !== null) entryParticipantIds.add(match.player2_participant_id);
    }
    const preservedDirectAdvancerIds = directAdvancerIdsFromNotes(entryMatches.map((match) => match.notes));
    const participantById = new Map(participants.map((participant) => [participant.id, participant]));
    const directAdvancers = preservedDirectAdvancerIds
      ? preservedDirectAdvancerIds
          .map((participantId) => participantById.get(participantId) ?? null)
          .filter((participant): participant is TournamentParticipant => participant !== null)
      : entryMatches.length > 0
        ? drawParticipants.filter((participant) => !entryParticipantIds.has(participant.id))
        : [];
    // Preliminar de cuadro COMPLETO (0 descansos, rareza histórica: la primera
    // ronda entera se etiqueta "Preliminar", p.ej. categoría A con 16): la
    // ronda configurada arranca en los GANADORES. Sin este fallback se pintaba
    // una columna fantasma ("Ronda 16" de 4 cajas que en realidad son Cuartos)
    // y, tras generar la siguiente ronda real, quedaría duplicada y vacía.
    const fullPreliminaryWinnerCount = directAdvancers.length === 0
      && preliminaryMatches.length > 1
      && (preliminaryMatches.length & (preliminaryMatches.length - 1)) === 0
      ? preliminaryMatches.length
      : null;
    const directAdvancerTargetSize = directAdvancers.length > 0
      ? partialConfiguredRound
        ? eliminationConfig?.bracket_size ?? null
        : directAdvancers.length + preliminaryMatches.length
      : fullPreliminaryWinnerCount;
    const directAdvancerTargetLabel = directAdvancerTargetSize
      ? eliminationRoundLabelForDrawSize(directAdvancerTargetSize)
      : null;
    const preliminaryTargetEntrants = !partialConfiguredRound && directAdvancers.length > 0
      ? orderPreliminaryTargetEntrants(directAdvancers, preliminaryMatches.length, true)
      : [];
    const configuredLabels = directAdvancerTargetSize
      ? eliminationMainRoundLabelsForDrawSize(directAdvancerTargetSize)
      : eliminationMainRoundLabelsForConfig(eliminationConfig);
    const existingMainLabels = eliminationRounds
      .map((round) => round.label)
      .filter((label) => label !== "3er Lugar" && !isConsolationLabel(label));
    const labels = Array.from(new Set([...existingMainLabels, ...configuredLabels]))
      .filter((label) => label !== "3er Lugar")
      .filter((label) => {
        if (!directAdvancerTargetLabel || label === "Preliminar") return true;
        return (ELIMINATION_ROUND_ORDER.get(label) ?? 99) >= (ELIMINATION_ROUND_ORDER.get(directAdvancerTargetLabel) ?? 99);
      })
      .sort((a, b) => (ELIMINATION_ROUND_ORDER.get(a) ?? 99) - (ELIMINATION_ROUND_ORDER.get(b) ?? 99));

    if (labels.length === 0) return { rounds: [] as EliminationVisualDrawRound[], baseRows: 0 };

    const configuredMatchCountByLabel = new Map<string, number>();
    // El cuadro REAL manda sobre la config: con preliminares generadas, el
    // tamaño de las columnas sale de directos+previas (p.ej. 14+2=16 aunque la
    // categoría tenga configurado Ronda 32); las etiquetas ya se derivan igual.
    let currentDrawSize = Math.max(directAdvancerTargetSize ?? eliminationConfig?.bracket_size ?? 0, 2);
    for (const label of configuredLabels) {
      configuredMatchCountByLabel.set(label, Math.max(Math.floor(currentDrawSize / 2), 1));
      currentDrawSize = Math.max(Math.floor(currentDrawSize / 2), 1);
    }
    const partialRoundSlots = partialConfiguredRound
      ? partialEliminationRoundSlots(
          configuredMatchCountByLabel.get(configuredFirstLabel) ?? configuredFirstMatches.length,
          directAdvancers.length,
        )
      : [];
    const partialNextRoundSources = partialConfiguredRound
      ? partialEliminationNextRoundSources(
          configuredMatchCountByLabel.get(configuredFirstLabel) ?? configuredFirstMatches.length,
          directAdvancers.length,
        )
      : [];
    const partialRoundIndex = labels.indexOf(configuredFirstLabel);

    let previousRoundMatchCount = 0;
    const expectedCounts = labels.map((label, index) => {
      const existingCount = matchesByLabel.get(label)?.length ?? 0;
      const configuredCount = configuredMatchCountByLabel.get(label) ?? 0;
      const projectedFromPrevious = previousRoundMatchCount > 0 ? Math.max(Math.ceil(previousRoundMatchCount / 2), 1) : 0;
      const expected = partialConfiguredRound && label === configuredFirstLabel
        ? configuredCount
        : !partialConfiguredRound && label === directAdvancerTargetLabel && preliminaryTargetEntrants.length > 0
          ? Math.max(configuredCount, preliminaryTargetEntrants.length / 2)
          : existingCount > 0
          ? existingCount
          : index === 0
            ? Math.max(configuredCount, label === "Final" ? 1 : 0)
            : Math.max(projectedFromPrevious, label === "Final" ? 1 : 0);
      previousRoundMatchCount = expected;
      return expected;
    });

    const baseRows = Math.max(expectedCounts[0] * 2, 2);
    const rounds = labels.map<EliminationVisualDrawRound>((label, roundIndex) => {
      const expectedMatchCount = Math.max(expectedCounts[roundIndex], 1);
      const rowSpan = Math.max(Math.floor(baseRows / expectedMatchCount), 1);
      const matches = matchesByLabel.get(label) ?? [];
      const slots: EliminationVisualDrawSlot[] = Array.from({ length: expectedMatchCount }, (_, slotIndex) => {
        const partialSlot = partialConfiguredRound && label === configuredFirstLabel
          ? partialRoundSlots[slotIndex] ?? null
          : null;
        const match = partialSlot
          ? partialSlot.matchIndex === null ? null : matches[partialSlot.matchIndex] ?? null
          : matches[slotIndex] ?? null;
        const upstreamStart = slotIndex * 2 + 1;
        const partialUpstreamSlots = partialConfiguredRound && roundIndex === partialRoundIndex + 1
          ? partialNextRoundSources[slotIndex] ?? null
          : null;
        const hasDirectAdvancerEntrants = !match
          && !partialConfiguredRound
          && label === directAdvancerTargetLabel
          && directAdvancers.length > 0;
        const firstEntrantIndex = slotIndex * 2;
        const preliminaryTargetPlayer1 = hasDirectAdvancerEntrants
          ? preliminaryTargetEntrants[firstEntrantIndex] ?? null
          : null;
        const preliminaryTargetPlayer2 = hasDirectAdvancerEntrants
          ? preliminaryTargetEntrants[firstEntrantIndex + 1] ?? null
          : null;
        const directPlayer1 = partialSlot?.directAdvancerIndex != null
          ? directAdvancers[partialSlot.directAdvancerIndex] ?? null
          : partialUpstreamSlots?.[0]?.directAdvancerIndex != null
            ? directAdvancers[partialUpstreamSlots[0].directAdvancerIndex] ?? null
            : preliminaryTargetPlayer1?.participant
              ? preliminaryTargetPlayer1.participant
              : null;
        const directPlayer2 = partialUpstreamSlots?.[1]?.directAdvancerIndex != null
          ? directAdvancers[partialUpstreamSlots[1].directAdvancerIndex] ?? null
          : preliminaryTargetPlayer2?.participant
            ? preliminaryTargetPlayer2.participant
            : null;
        const winnerSourceStart = hasDirectAdvancerEntrants
          ? Math.max(firstEntrantIndex - directAdvancers.length + 1, 1)
          : upstreamStart;
        const sources = partialSlot?.directAdvancerIndex != null
          ? ["", "BYE"]
          : partialUpstreamSlots
            ? partialUpstreamSlots.map((slot) => (
                slot?.matchIndex != null ? `G M${slot.matchIndex + 1}` : ""
              ))
            : hasDirectAdvancerEntrants
              ? [preliminaryTargetPlayer1?.source ?? "", preliminaryTargetPlayer2?.source ?? ""]
              : roundIndex === 0
              ? []
              : [
                  directPlayer1 ? "" : `G M${winnerSourceStart}`,
                  directPlayer2 ? "" : `G M${winnerSourceStart + 1}`,
                ];
        const rowStart = slotIndex * rowSpan + 1;
        return {
          key: match ? `match-${match.id}` : `placeholder-${label}-${slotIndex}`,
          roundLabel: label,
          slotNumber: slotIndex + 1,
          match,
          sources,
          directAdvancers: [directPlayer1, directPlayer2],
          gridRow: `${rowStart} / span ${rowSpan}`,
          rowSpan,
        };
      });
      return {
        label,
        displayLabel: eliminationRoundCompactDisplayLabel(label, labels[roundIndex + 1]),
        expectedMatchCount,
        slots,
      };
    });

    return { rounds, baseRows };
  })();

  // Normalize the computed draw into the presentation-agnostic bracket model the
  // <EliminationBracket> sheet consumes (globos + connectors + champion + 3er lugar).
  const { rounds: eliminationBracketRounds, thirdPlace: eliminationThirdPlaceMatch } = (() => {
    const seedByParticipant = new Map<number, number | null>();
    for (const participant of participants) {
      seedByParticipant.set(participant.id, participant.initial_rank ?? participant.current_rank ?? null);
    }
    const gamesFor = (match: TournamentMatch, side: "p1" | "p2"): number[] =>
      (match.score_sets ?? []).map((set) => (side === "p1" ? set.player1_games : set.player2_games));
    const briefCompetitor = (
      brief: MatchParticipantBrief | null,
      participantId: number | null,
      match: TournamentMatch,
      side: "p1" | "p2",
    ): BracketCompetitor | null => {
      if (!brief) return null;
      return {
        name: brief.short_name?.trim() || brief.display_name || (participantId ? `#${participantId}` : "Por definir"),
        seed: participantId != null ? seedByParticipant.get(participantId) ?? null : null,
        games: gamesFor(match, side),
        isWinner: match.winner_participant_id != null && match.winner_participant_id === participantId,
        tag: competitorLoserTag(match, participantId),
      };
    };
    const directCompetitor = (participant: TournamentParticipant | null): BracketCompetitor | null =>
      participant
        ? { name: compactParticipantDisplayName(participant), seed: seedByParticipant.get(participant.id) ?? null, games: [], isWinner: false }
        : null;
    const sourceCompetitor = (source: string | undefined): BracketCompetitor | null =>
      source
        ? { name: source, seed: null, games: [], isWinner: false }
        : null;

    // Placeholder del esqueleto: el lado vacío muestra de qué partido viene
    // el clasificado (marcador [pX_from=Ronda#N] en notes) como "G M#".
    const deadSides = deadBracketSides(tournamentMatches);
    const feedSource = (match: TournamentMatch, side: 1 | 2): string | undefined => {
      if (isDeadSide(deadSides, match, side)) return DEAD_SIDE_LABEL;
      const found = (match.notes ?? "").match(new RegExp(`\\[p${side}_from=[^\\]#]+#(\\d+)(:perdedor)?\\]`));
      if (!found) return undefined;
      return found[2] ? `Perdedor M${found[1]}` : `G M${found[1]}`;
    };
    const rounds: BracketRound[] = eliminationVisualDraw.rounds.map((round) => ({
      label: round.displayLabel,
      matches: round.slots.map((slot) => {
        const match = slot.match;
        if (match) {
          return {
            id: `m${match.id}`,
            matchId: match.id,
            number: match.match_number ?? null,
            top: briefCompetitor(match.player1, match.player1_participant_id, match, "p1"),
            bottom: briefCompetitor(match.player2, match.player2_participant_id, match, "p2"),
            topSource: match.player1 ? undefined : feedSource(match, 1),
            bottomSource: match.player2 ? undefined : feedSource(match, 2),
            pending: !match.player1 || !match.player2,
          };
        }
        return {
          id: slot.key,
          top: directCompetitor(slot.directAdvancers[0]) ?? sourceCompetitor(slot.sources[0]),
          bottom: directCompetitor(slot.directAdvancers[1]) ?? sourceCompetitor(slot.sources[1]),
          pending: true,
        };
      }),
    }));

    // "3er Lugar" is excluded from the main draw columns; surface it as a
    // mini-box under the final instead of hiding it entirely.
    const thirdMatch = tournamentMatches.find((match) => isThirdPlaceRoundLabel(match.round_label)) ?? null;
    const thirdPlace: BracketMatch | null = thirdMatch
      ? {
        id: `m${thirdMatch.id}`,
        matchId: thirdMatch.id,
        number: thirdMatch.match_number ?? null,
        top: briefCompetitor(thirdMatch.player1, thirdMatch.player1_participant_id, thirdMatch, "p1"),
        bottom: briefCompetitor(thirdMatch.player2, thirdMatch.player2_participant_id, thirdMatch, "p2"),
        topSource: thirdMatch.player1 ? undefined : feedSource(thirdMatch, 1),
        bottomSource: thirdMatch.player2 ? undefined : feedSource(thirdMatch, 2),
        pending: !thirdMatch.player1 || !thirdMatch.player2,
      }
      : null;

    return { rounds, thirdPlace };
  })();

  // El cuadro de consolación tiene su propia tarjeta: los conteos y el botón
  // «Generar siguiente ronda» miran solo el cuadro principal.
  const mainDrawMatches = tournamentMatches.filter((match) => !isConsolationLabel(match.round_label));
  const eliminationTotalMatches = mainDrawMatches.length;
  const eliminationByeCount = mainDrawMatches.filter(isEliminationBye).length;
  const eliminationDirectAdvancerSummary = getEliminationDirectAdvancerSummary({
    participantCount: drawParticipants.length,
    technicalByeCount: eliminationByeCount,
    config: eliminationConfig,
  });
  const eliminationActualEntryMatches = tournamentMatches.filter(
    (match) => match.round_label === "Preliminar"
      || (match.notes ?? "").includes("pasan directo al cuadro de"),
  );
  const eliminationActualEntryParticipantIds = new Set<number>();
  for (const match of eliminationActualEntryMatches) {
    if (match.player1_participant_id !== null) eliminationActualEntryParticipantIds.add(match.player1_participant_id);
    if (match.player2_participant_id !== null) eliminationActualEntryParticipantIds.add(match.player2_participant_id);
  }
  const eliminationActualDirectAdvancerCount = eliminationActualEntryMatches.length > 0
    ? drawParticipants.filter((participant) => !eliminationActualEntryParticipantIds.has(participant.id)).length
    : 0;
  const eliminationUsesActualDirectAdvancers = tournamentMatches.length > 0 && eliminationActualEntryMatches.length > 0;
  const eliminationDisplayDirectAdvancerCount = eliminationUsesActualDirectAdvancers
    ? eliminationActualDirectAdvancerCount
    : eliminationDirectAdvancerSummary.directAdvancerCount;
  const eliminationDisplayHasDirectAdvancers = eliminationDisplayDirectAdvancerCount > 0;
  const eliminationDisplayByeHelper = eliminationUsesActualDirectAdvancers
    ? eliminationActualDirectAdvancerCount > 0
      ? `${eliminationActualDirectAdvancerCount} jugador(es) tienen BYE en la ronda inicial y avanzan directo.`
      : "0 BYE: todos los jugadores disputan la ronda inicial."
    : eliminationDirectAdvancerSummary.helperText;
  const eliminationResolvedCount = mainDrawMatches.filter(isEliminationResolved).length;
  // Pendientes = armados sin resultado. Los placeholders del esqueleto (rondas
  // futuras sin jugadores) no bloquean "Generar siguiente ronda".
  const eliminationPendingCount = mainDrawMatches.filter(
    (match) => isEliminationArmed(match) && !isEliminationResolved(match),
  ).length;
  const eliminationTerminalMatches = tournamentMatches.filter(
    (match) => match.round_label === "Final" || match.round_label === "3er Lugar",
  );
  const eliminationHasFinalRound = eliminationTerminalMatches.some(
    (match) => match.round_label === "Final" && isEliminationArmed(match),
  );
  const eliminationTerminalPendingCount = eliminationTerminalMatches.filter(
    (match) => !isEliminationResolved(match),
  ).length;
  const eliminationBracketComplete = eliminationHasFinalRound && eliminationTerminalPendingCount === 0;
  const eliminationScheduledCount = tournamentMatches.filter(
    (match) => !!match.scheduled_date || !!match.scheduled_time || !!match.court,
  ).length;
  const eliminationFilteredMatches = eliminationRounds.reduce(
    (total, round) => total + round.matches.length,
    0,
  );
  const activeEliminationRoundLabel = (() => {
    if (eliminationRounds.length === 0) return "";
    const openRound = eliminationRounds
      .find((round) => round.matches.some((match) => isEliminationArmed(match) && !isEliminationResolved(match)))
      ?? [...eliminationRounds]
        .reverse()
        .find((round) => round.matches.some((match) => !isEliminationResolved(match)));
    return (openRound ?? eliminationRounds[eliminationRounds.length - 1]).label;
  })();
  // En el wizard, calendarizar navega al paso 10 (setup/schedule) para que
  // el stepper siga visible — el link viejo a /schedule sacaba al admin del
  // asistente y se saltaba Comunicación (reporte Eduardo 2026-08-27).
  const schedulePathBase = setupMode
    ? `/admin/tournaments/${tournamentId}/setup/schedule`
    : `/admin/tournaments/${tournamentId}/schedule`;
  const eliminationScheduleLink = activeEliminationRoundLabel
    ? `${schedulePathBase}?${selectedDivisionId ? `division=${selectedDivisionId}&` : ""}round_label=${encodeURIComponent(activeEliminationRoundLabel)}`
    : `${schedulePathBase}${selectedDivisionId ? `?division=${selectedDivisionId}` : ""}`;
  const manualDrawParticipantById = new Map(participants.map((participant) => [participant.id, participant]));
  const manualDrawOrderedParticipants = manualDrawParticipantIds
    .map((id) => manualDrawParticipantById.get(id))
    .filter((participant): participant is TournamentParticipant => Boolean(participant));
  const previewDrawSize = effectivePreviewDrawSize(eliminationConfig?.bracket_size, manualDrawOrderedParticipants.length);
  const previewHalf = Math.max(previewDrawSize / 2, 1);
  const previewParticipantCount = manualDrawOrderedParticipants.length;
  const previewPlayInCount = previewParticipantCount > previewDrawSize
    ? previewParticipantCount - previewDrawSize
    : previewParticipantCount > previewHalf
      ? previewParticipantCount - previewHalf
      : Math.floor(previewParticipantCount / 2);
  const previewDirectCount = previewParticipantCount > previewHalf
    ? Math.max(previewParticipantCount - (previewPlayInCount * 2), 0)
    : Math.max(previewParticipantCount - (previewPlayInCount * 2), 0);
  const previewDirectAdvancers = manualDrawOrderedParticipants.slice(0, previewDirectCount);
  const previewMatchParticipants = manualDrawOrderedParticipants.slice(previewDirectCount);
  const previewMatchups = Array.from({ length: previewPlayInCount }, (_, index) => ({
    slot: index + 1,
    left: previewMatchParticipants[index] ?? null,
    right: previewMatchParticipants[(previewPlayInCount * 2) - index - 1] ?? null,
  }));
  const manualEliminationPreviewBracketRounds = (() => {
    const competitor = (participant: TournamentParticipant | null): BracketCompetitor | null => participant
      ? {
          name: compactParticipantDisplayName(participant),
          seed: participant.initial_rank ?? participant.current_rank ?? null,
          games: [],
          isWinner: false,
        }
      : null;
    const rounds: BracketRound[] = [];
    const isPartialConfiguredRound = isPartialRoundOf16Preview(
      previewDrawSize,
      previewParticipantCount,
      previewDirectCount,
      previewPlayInCount,
    );

    if (isPartialConfiguredRound) {
      const previewStructure = partialEliminationPreviewStructure(previewDrawSize, previewDirectCount);
      const initialSlots = previewStructure.initialSlots;
      rounds.push({
        label: eliminationRoundCompactDisplayLabel(eliminationRoundLabelForDrawSize(previewDrawSize)),
        matches: initialSlots.map((slot, index): BracketMatch => {
          if (slot.matchIndex !== null) {
            const matchup = previewMatchups[slot.matchIndex];
            return {
              id: `manual-preview-${previewDrawSize}-${index + 1}`,
              top: competitor(matchup?.left ?? null),
              bottom: competitor(matchup?.right ?? null),
              pending: true,
            };
          }
          const direct = slot.directAdvancerIndex !== null
            ? previewDirectAdvancers[slot.directAdvancerIndex] ?? null
            : null;
          return {
            id: `manual-preview-${previewDrawSize}-${index + 1}`,
            top: competitor(direct),
            bottom: null,
            bottomSource: direct ? "BYE" : undefined,
            pending: true,
          };
        }),
      });

      const quarterSources = previewStructure.nextRoundSources;
      rounds.push({
        label: eliminationRoundCompactDisplayLabel(eliminationRoundLabelForDrawSize(previewHalf)),
        matches: quarterSources.map((pair, index): BracketMatch => {
          const side = (source: (typeof pair)[number]) => source.directAdvancerIndex !== null
            ? {
                competitor: competitor(previewDirectAdvancers[source.directAdvancerIndex] ?? null),
                source: undefined,
              }
            : {
                competitor: null,
                source: source.matchIndex !== null ? `G M${source.matchIndex + 1}` : undefined,
              };
          const top = side(pair[0]);
          const bottom = side(pair[1]);
          return {
            id: `manual-preview-${previewHalf}-${index + 1}`,
            top: top.competitor,
            bottom: bottom.competitor,
            topSource: top.source,
            bottomSource: bottom.source,
            pending: true,
          };
        }),
      });

      let size = previewHalf / 2;
      while (size >= 2) {
        rounds.push({
          label: eliminationRoundCompactDisplayLabel(eliminationRoundLabelForDrawSize(size)),
          matches: Array.from({ length: size / 2 }, (_, index): BracketMatch => ({
            id: `manual-preview-${size}-${index + 1}`,
            top: null,
            bottom: null,
            pending: true,
          })),
        });
        size /= 2;
      }
      return rounds;
    }

    const targetSize = previewDirectCount + previewPlayInCount;
    if (targetSize < 2 || targetSize & (targetSize - 1)) return [] as BracketRound[];
    const entrants = orderPreliminaryTargetEntrants(
      previewDirectAdvancers,
      previewPlayInCount,
      eliminationConfig?.default_generation_mode !== "random",
    );
    let size = targetSize;
    while (size >= 2) {
      const label = eliminationRoundCompactDisplayLabel(eliminationRoundLabelForDrawSize(size));
      const matches = Array.from({ length: size / 2 }, (_, index): BracketMatch => {
        if (size === targetSize) {
          const top = entrants[index * 2];
          const bottom = entrants[index * 2 + 1];
          return {
            id: `manual-preview-${size}-${index + 1}`,
            top: competitor(top?.participant ?? null),
            bottom: competitor(bottom?.participant ?? null),
            topSource: top?.participant ? undefined : top?.source,
            bottomSource: bottom?.participant ? undefined : bottom?.source,
            pending: true,
          };
        }
        return { id: `manual-preview-${size}-${index + 1}`, top: null, bottom: null, pending: true };
      });
      rounds.push({ label, matches });
      size /= 2;
    }
    return rounds;
  })();
  const setManualDrawSlot = (slotIndex: number, participantId: number) => {
    setManualDrawParticipantIds((current) => {
      const next = [...current];
      const existingIndex = next.indexOf(participantId);
      if (existingIndex >= 0) {
        [next[existingIndex], next[slotIndex]] = [next[slotIndex], next[existingIndex]];
      } else {
        next[slotIndex] = participantId;
      }
      return next;
    });
  };

  // Eduardo 2026-10-02: en el asistente con el torneo en borrador, los chips
  // del checklist abren su paso (no la operación del torneo).
  const checklistBase = checklistBasePath(tournamentId, setupMode, tournament?.status);
  // Revisión del borrador: el chip «Calendario» respeta el candado del paso
  // 10 del stepper (sin draw en alguna categoría no se abre el Calendario).
  // Partido sin fecha: sin calendario de canchas no está «Sin programar», lo
  // coordinan los jugadores (Eduardo 2026-10-05).
  const unscheduledMatchText = usesCourtCalendar ? "Sin programar" : "Lo coordinan los jugadores";
  // Fin del paso 9 (Eduardo 2026-10-05): ya no se publica aquí, solo se sigue
  // a Calendario (paso 10), donde está «Publicar el torneo». Mismo candado
  // que el paso 10 del stepper (Formato, Grupos y Draw en todas las categorías).
  const canContinueToSchedule = canAdvanceFromSetupStep(readinessRows, 9, readinessState);
  const continueToScheduleBlockedReason =
    readinessState === "loading"
      ? "Verificando el estado de todas las categorías…"
      : readinessState === "failed"
        ? "No pudimos verificar que Formato, Grupos y Draw estén completos. Recarga la página para intentar otra vez."
        : setupGate(readinessRows).reason;

  // Rediseño del Draw (maquetas Draw-RR/Elim/Escalera/Equipos, oct 2026):
  // encabezado navy con indicadores por formato y tarjetas numeradas.
  const drawReadinessRow =
    readinessRows.find((row) => row.division_id !== null && row.division_id === selectedDivisionId)
    ?? (readinessRows.length === 1 ? readinessRows[0] : null);
  const drawFormatTitle = isLadder && !isLadderPlayoffs
    ? "Escalera"
    : isPhasePlayoffBuilder
      ? isLadderPlayoffs
        ? "Playoffs de escalera"
        : isTeamPlay
          ? "Por equipos"
          : "Round Robin"
      : formatConfig?.format_type === "elimination"
        ? "Eliminatoria"
        : null;
  const drawHeroChip = drawReadinessRow
    ? drawReadinessRow.has_draw
      ? (drawReadinessRow.out_of_draw ?? 0) > 0
        ? `${drawReadinessRow.out_of_draw} fuera del draw`
        : "✓ Draw creado"
      : "Falta draw"
    : null;
  const drawHeroKpis: Array<{ label: string; value: string | number }> = isLadder && !isLadderPlayoffs
    ? [
        { label: "Semanas", value: roundCount || availableRoundNumbers.length },
        { label: "Publicadas", value: rounds.filter((round) => round.status === "published").length },
        { label: "Semana actual", value: latestPublishedRoundNumber ?? "—" },
      ]
    : isPhasePlayoffBuilder
      ? [
          { label: isTeamPlay ? "Equipos" : "Grupos", value: isTeamPlay ? drawParticipants.length : groups.length },
          { label: isTeamPlay ? "Series" : "Partidos", value: roundRobinMatches.length },
          { label: "Jornadas", value: availableRoundNumbers.length },
          {
            label: "Playoffs",
            value: roundRobinPlayoffVisualRounds.length > 0
              ? rrUnfilledPlaceholderCount > 0
                ? "Por definir"
                : "Armados"
              : "Sin cuadro",
          },
        ]
      : formatConfig?.format_type === "elimination"
        ? [
            { label: "Jugadores", value: drawParticipants.length },
            { label: "Partidos", value: eliminationTotalMatches },
            {
              label: eliminationDisplayHasDirectAdvancers || eliminationUsesActualDirectAdvancers ? "BYE / directo" : "BYE",
              value: eliminationDisplayHasDirectAdvancers || eliminationUsesActualDirectAdvancers
                ? eliminationDisplayDirectAdvancerCount
                : eliminationDirectAdvancerSummary.technicalByeCount,
            },
            { label: "Rondas", value: eliminationRounds.length },
          ]
        : [];
  const drawHero = (
    <section className="wiz-hero wiz-hero--navy drw-hero" aria-label="Resumen del draw">
      <div className="drw-hero__top">
        <div>
          <p className="eyebrow">
            {[
              setupMode ? "Paso 9 de 10" : null,
              activeDivision?.name ?? null,
              tournament.name,
            ].filter(Boolean).join(" · ")}
          </p>
          <h1>{drawFormatTitle ? `Draw · ${drawFormatTitle}` : "Draw"}</h1>
          <p className="wiz-hero__lead">Todo lo del draw de esta categoría, en orden.</p>
        </div>
        {drawHeroChip ? (
          <span className={`grp-hero__chip${drawReadinessRow?.has_draw && !(drawReadinessRow.out_of_draw ?? 0) ? " is-ok" : ""}`}>
            {drawHeroChip}
          </span>
        ) : null}
      </div>
      {drawHeroKpis.length > 0 ? (
        <div className="drw-hero__kpis">
          {drawHeroKpis.map((kpi) => (
            <div key={kpi.label} className="drw-hero__kpi">
              <span>{kpi.label}</span>
              <strong>{kpi.value}</strong>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
  const thirdPlaceEnabled = isPhasePlayoffBuilder
    ? roundRobinConfig?.playoff?.third_place_match !== false
    : eliminationConfig?.third_place_match !== false;

  return (
    <div className="wiz-step wiz-step--draw">
      {setupMode ? <TournamentSetupProgress current={9} tournamentStatus={tournament.status} hero={drawHero} /> : drawHero}

      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
      {message && <p className="form-message">{message}</p>}

      <DivisionChecklistCard
        tournamentId={tournamentId}
        rows={readinessRows}
        state={readinessState}
        checklistBase={checklistBase}
        lockScheduleChip={setupMode && tournament?.status === "draft"}
        usesCourtCalendar={usesCourtCalendar}
        selectedDivisionId={selectedDivisionId}
        alert={
          readinessRows.some((row) => (row.out_of_draw ?? 0) > 0) && (
            <div className="alert alert--info" role="alert">
              <strong>Hay inscritos nuevos fuera del draw.</strong> Se
              inscribieron después de generar el draw: asígnalos a su grupo (si
              la categoría lleva grupos) y rearma el draw para que jueguen.
              {/* Alta tardía en ELIMINACIÓN con el bracket jugándose: rearmar
                  está bloqueado por el backend (correcto) — decirlo aquí, no
                  prometer un rearme que va a dar 409. `scheduled`/`resolved`
                  del readiness son la mejor señal disponible por fila. */}
              {readinessRows.some(
                (row) =>
                  (row.out_of_draw ?? 0) > 0 &&
                  row.format_type === "elimination" &&
                  ((row.scheduled ?? 0) > 0 || (row.resolved ?? 0) > 0),
              ) && (
                <p style={{ margin: "0.4rem 0 0" }}>
                  Ojo: en <strong>eliminación con el cuadro ya jugándose</strong> no
                  se puede rearmar el bracket — el inscrito nuevo quedará fuera de
                  esa eliminatoria (o quita fechas y resultados para rearmar,
                  perdiendo el avance).
                </p>
              )}
              <div className="form__actions" style={{ marginTop: "0.5rem" }}>
                {readinessRows
                  .filter((row) => (row.out_of_draw ?? 0) > 0)
                  .map((row) => {
                    const eliminationLocked =
                      row.format_type === "elimination" &&
                      ((row.scheduled ?? 0) > 0 || (row.resolved ?? 0) > 0);
                    return (
                    <span key={row.division_id ?? "torneo"} className="form__actions" style={{ gap: "0.4rem" }}>
                      {row.format_type === "round_robin" && (
                        <Link
                          className="btn btn--ghost btn--compact"
                          to={`${checklistBase}/groups${row.division_id ? `?division=${row.division_id}` : ""}`}
                        >
                          1) Asignar grupos · {row.name}
                        </Link>
                      )}
                      <Link
                        className={`btn btn--compact${eliminationLocked ? " btn--ghost" : ""}`}
                        to={`${checklistBase}/rounds${row.division_id ? `?division=${row.division_id}&regen=1` : "?regen=1"}`}
                        title={eliminationLocked
                          ? "El cuadro ya tiene calendario o resultados: ver opciones reales."
                          : undefined}
                      >
                        {row.format_type === "round_robin" ? "2) " : ""}
                        {eliminationLocked
                          ? `Ver opciones · ${row.name} (${row.out_of_draw})`
                          : `Rearmar draw incluyéndolos · ${row.name} (${row.out_of_draw})`}
                      </Link>
                    </span>
                    );
                  })}
              </div>
            </div>
          )
        }
      />

      {(!isLadder || isLadderPlayoffs) && (
        <>
          {isPhasePlayoffBuilder ? (
            <>
              <section className="card wiz-card drw-card" data-draw-card="partidos">
                <div className="wiz-card__head drw-card__head">
                  <h2 className="hint-label">
                    {isLadderPlayoffs ? "Partidos de playoffs" : isTeamPlay ? "Series por grupo" : "Partidos de grupos"}
                    <FieldHint label="¿Qué se arma aquí?">
                      {isLadderPlayoffs
                        ? "Revisa los cruces reales de playoffs generados desde el ranking final de la categoría. Los ganadores avanzan a semifinal y final."
                        : "Genera todos-contra-todos por grupo y divide los partidos en jornadas configuradas en Formato. Cada partido queda editable antes de programar canchas y horarios."}
                    </FieldHint>
                  </h2>
                </div>
                {showGenerateRoundRobin ? (
                  <div className="form__actions drw-actions">
                    <button
                      type="button"
                      className="btn"
                      onClick={handleGenerateRoundRobin}
                      disabled={generating || generateBlockedByArrange}
                    >
                      {generating
                        ? "Generando…"
                        : rrParticipantsWithoutMatches > 0
                          ? `Agregar partidos de ${rrParticipantsWithoutMatches} participante${rrParticipantsWithoutMatches === 1 ? "" : "s"} nuevo${rrParticipantsWithoutMatches === 1 ? "" : "s"}`
                          : "Generar Round Robin"}
                    </button>
                    {generateBlockedByArrange ? (
                      <p className="muted round-robin-generate-hint" data-no-toast>
                        {generateBlockedByArrangeMessage(arrangeDirtyGroupIds)}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {/* Mismo patrón de tabs que Resultados (pedido Eduardo
                    2026-09-01: "igualar draw a resultados en la forma de ver
                    las jornadas" — la grilla de pills J.1/P.15 se veía distinta
                    al segmented de la otra pantalla). El conteo de partidos se
                    conserva en el tooltip. */}
                {/* Eduardo 2026-09-17: lista desplegable, igual que en Calendario y
                    Resultados (los chips no caben con 8+ jornadas). El conteo de
                    partidos va en la opción. */}
                <label className="player-games-round-filter admin-results-round-filter" aria-label="Seleccionar jornada">
                  <span>Jornada</span>
                  <select className="t123-select"
                    value={selectedRoundNumber ?? ""}
                    onChange={(event) => {
                      const next = Number(event.target.value);
                      if (Number.isFinite(next) && next > 0) setSelectedRoundNumber(next);
                    }}
                  >
                    {availableRoundNumbers.map((roundNumber) => {
                      const count = roundRobinMatchesByNumber.get(roundNumber)?.length ?? 0;
                      const isPlayoffRound = phaseBaseRoundCount > 0 && roundNumber > phaseBaseRoundCount;
                      return (
                        <option key={roundNumber} value={roundNumber}>
                          {isPlayoffRound ? `Playoffs ${roundNumber}` : `Jornada ${roundNumber}`} · {count} partido{count === 1 ? "" : "s"}
                        </option>
                      );
                    })}
                  </select>
                  <small>
                    {selectedRoundNumber === null ? "Sin jornada" : `Mostrando jornada ${selectedRoundNumber}`}
                  </small>
                </label>

                {!isLadderPlayoffs && roundRobinVisualGroups.length > 0 && (
                  <section ref={roundRobinVisualRef} className="round-robin-visual" aria-label="Tabla visual Round Robin">
                    <div className="round-robin-visual__header">
                      <div>
                        <h3 className="hint-label">Tabla visual Round Robin<FieldHint label="¿Qué muestra «Tabla visual Round Robin»?">{arranging
                            ? "Sube o baja a cada jugador con las flechas. El lugar en la hoja define contra quién juega en cada jornada."
                            : `Cruce participantes contra participantes. La diagonal se bloquea y el verde marca los partidos de Jornada ${selectedRoundNumber ?? "—"}.`}</FieldHint></h3>
                      </div>
                      <div className="round-robin-visual__actions">
                        {arrangementStatus ? (
                          <button
                            type="button"
                            className="btn btn--small"
                            onClick={() => void handleToggleArrange()}
                            disabled={
                              arranging
                                ? arrangeBusyGroupId !== null
                                : arrangeDisabledReason !== null || generating
                            }
                          >
                            {arranging ? "Terminar de acomodar" : "Acomodar grupos"}
                          </button>
                        ) : null}
                        {!arranging ? (
                          <button type="button" className="btn btn--small" onClick={() => printRoundRobinVisual()}>
                            PDF / imprimir
                          </button>
                        ) : null}
                        {!arranging ? (
                          <button type="button" className="btn btn--small" onClick={() => void printDrawPoster()}>
                            Imprimir póster (1 hoja por grupo)
                          </button>
                        ) : null}
                        {arrangementStatus && !arranging && arrangeDisabledReason ? (
                          <p className="muted round-robin-visual__actions-hint" data-no-toast>
                            {arrangeDisabledReason}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="round-robin-visual__groups">
                      {roundRobinVisualGroups.map((group) => {
                        const sourceGroup = groupsById.get(group.id) ?? null;
                        if (arranging && sourceGroup) {
                          return renderArrangeGroup(group, sourceGroup);
                        }
                        return (
                          <details key={group.id} className="round-robin-visual__group" data-rr-group-id={group.id} open>
                            <summary>
                              <span>{group.name}</span>
                              <small>{group.participants.length} participantes · {group.matchesByPair.size} partidos</small>
                            </summary>
                            {token && sourceGroup ? (
                              // Torneo 71: partidos de quien ya no está en el
                              // grupo (o repetidos) que siguen «por jugar».
                              <GroupStrayMatchesNotice
                                token={token}
                                tournamentId={tournamentId}
                                groupId={group.id}
                                groupName={group.name}
                                matches={sourceGroup.ghost_matches}
                                canEdit={arrangementStatus?.can_edit !== false}
                                onDone={async (text) => {
                                  setError(null);
                                  setMessage(text);
                                  await load({ keepError: false });
                                }}
                                onError={(text) => setError(text)}
                              />
                            ) : null}
                            {(() => {
                              // Tabla estilo póster (Eduardo 2026-10-15): mismos textos que
                              // lib/drawPoster, con G-P / Games / Lugar y rol de juegos.
                              const standings = rrStandingsByGroup.get(group.id) ?? null;
                              const anyPlayed = !!standings && [...standings.values()].some((row) => row.matches_played > 0);
                              const numberById = new Map(group.participants.map((participant, index) => [participant.id, index + 1]));
                              const rolByRound = new Map<number, Array<{ key: number; text: string; muted: boolean }>>();
                              const pairMatches = [...group.matchesByPair.values()].sort(
                                (a, b) => (a.matchNumber ?? 1_000_000) - (b.matchNumber ?? 1_000_000) || a.id - b.id,
                              );
                              for (const visualMatch of pairMatches) {
                                const head = `${numberById.get(visualMatch.player1Id) ?? "?"} vs ${numberById.get(visualMatch.player2Id) ?? "?"}`;
                                const winnerId = visualMatch.winnerParticipantId;
                                const winnerScore = winnerId !== null
                                  ? visualMatch.isTie
                                    ? tieScoreFromPerspective(visualMatch.scoreText, visualMatch.player1Id === winnerId)
                                    : roundRobinVisualResultText(visualMatch, winnerId)
                                  : null;
                                const entry = visualMatch.drop
                                  ? { text: `${head} · ${dropMatchLabel(visualMatch.drop).toLowerCase()}`, muted: true }
                                  : winnerId !== null
                                    ? { text: `${head} · ${winnerScore || rrNoScoreLabel(visualMatch.status).toLowerCase()}`, muted: false }
                                    : { text: `${head} · ${rrRolWhen(visualMatch.scheduledDate, visualMatch.scheduledTime, visualMatch.court)}`, muted: false };
                                const round = visualMatch.roundNumber ?? 0;
                                rolByRound.set(round, [...(rolByRound.get(round) ?? []), { key: visualMatch.id, ...entry }]);
                              }
                              const rolRounds = [...rolByRound.entries()].sort((a, b) => a[0] - b[0]);
                              return (
                                <>
                                  <div className="responsive-table-wrap round-robin-visual__wrap rr-poster__wrap">
                                    <table className="round-robin-matrix rr-poster">
                                      <thead>
                                        <tr>
                                          <th className="rr-poster__th-player">Jugador</th>
                                          {group.participants.map((participant, index) => (
                                            <th key={participant.id} title={participantDisplayName(participant)}>
                                              <span
                                                className="round-robin-matrix__col-index rr-poster__num"
                                                title={group.hasPositions ? RR_SHEET_PLACE_TOOLTIP : undefined}
                                              >
                                                {index + 1}
                                              </span>
                                            </th>
                                          ))}
                                          <th className="rr-poster__th-stat">G-P</th>
                                          <th className="rr-poster__th-stat">Games</th>
                                          <th className="rr-poster__th-stat">Lugar</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {group.participants.map((rowParticipant, rowIndex) => {
                                          const standing = standings?.get(rowParticipant.id) ?? null;
                                          const club = rowParticipant.player?.club_name?.trim() || "";
                                          return (
                                            <tr key={rowParticipant.id} className={group.ghostIds.has(rowParticipant.id) ? "is-ghost" : undefined}>
                                              <th scope="row" className="rr-poster__player">
                                                <span
                                                  className="round-robin-matrix__rank rr-poster__num"
                                                  title={group.hasPositions ? RR_SHEET_PLACE_TOOLTIP : undefined}
                                                >
                                                  {rowIndex + 1}
                                                </span>
                                                <span className="rr-poster__who">
                                                  <span className="round-robin-matrix__participant-name" title={participantDisplayName(rowParticipant)}>
                                                    {nameWithDropMark(compactParticipantDisplayName(rowParticipant), rowParticipant)}
                                                  </span>
                                                  {club ? <small className="rr-poster__club">{club}</small> : null}
                                                  {group.ghostIds.has(rowParticipant.id) ? (
                                                    <span className="round-robin-matrix__ghost-tag">ya no está en este grupo</span>
                                                  ) : null}
                                                </span>
                                              </th>
                                              {group.participants.map((columnParticipant) => {
                                                const isDiagonal = rowParticipant.id === columnParticipant.id;
                                                const visualMatch = isDiagonal
                                                  ? null
                                                  : group.matchesByPair.get(roundRobinPairKey(rowParticipant.id, columnParticipant.id));
                                                const isCurrent = !!visualMatch && visualMatch.roundNumber === selectedRoundNumber;
                                                const isScheduled = !!visualMatch?.scheduledDate;
                                                const resultText = visualMatch ? roundRobinVisualResultText(visualMatch, rowParticipant.id) : null;
                                                const rowWon = !!visualMatch && visualMatch.winnerParticipantId === rowParticipant.id;
                                                const rowLost = !!visualMatch && visualMatch.winnerParticipantId !== null && !rowWon;
                                                // Eduardo 2026-10-05 (torneo 69): contra alguien dado de
                                                // baja la celda dice qué pasó con el partido.
                                                const drop = visualMatch?.drop ?? null;
                                                const isPending = !!visualMatch && !drop && visualMatch.winnerParticipantId === null;
                                                const when = visualMatch ? rrWhen(visualMatch.scheduledDate, visualMatch.scheduledTime, visualMatch.court) : null;
                                                const jornadaChip = visualMatch ? (
                                                  <span className="rr-poster__chip">
                                                    {visualMatch.roundNumber !== null ? `Jornada ${visualMatch.roundNumber}` : "Sin jornada"}
                                                  </span>
                                                ) : null;
                                                return (
                                                  <td
                                                    key={columnParticipant.id}
                                                    className={`round-robin-matrix__cell${isDiagonal ? " is-diagonal" : ""}${visualMatch ? " has-match" : ""}${isCurrent ? " is-current" : ""}${rowWon ? " is-won" : ""}${rowLost ? " is-lost" : ""}${isPending ? " is-pending" : ""}${drop ? " is-off" : ""}${drop?.kind === "cancelled" ? " is-drop" : ""}`}
                                                    data-drop-match={drop?.kind}
                                                    title={
                                                      isDiagonal
                                                        ? "Mismo jugador"
                                                        : visualMatch
                                                          ? `${visualMatch.isTie ? "Serie · " : ""}${participantDisplayName(rowParticipant)} vs ${participantDisplayName(columnParticipant)} · Jornada ${visualMatch.roundNumber ?? "—"}${drop ? ` · ${dropMatchLabel(drop)}` : ""}${visualMatch.isTie && visualMatch.winnerParticipantId !== null ? ` · Ganó ${participantDisplayName(rowWon ? rowParticipant : columnParticipant)}` : resultText ? ` · ${rowWon ? "Ganó" : "Perdió"} ${resultText}` : isScheduled ? ` · ${visualMatch.scheduledDate}${visualMatch.scheduledTime ? ` ${visualMatch.scheduledTime}` : ""}${courtNoticeName(visualMatch.court) ? ` · ${courtNoticeName(visualMatch.court)}` : ""}` : ""}`
                                                          : "Sin cruce generado"
                                                    }
                                                  >
                                                    {isDiagonal ? (
                                                      <span aria-label="Mismo jugador" className="rr-poster__ball">
                                                        <svg viewBox="0 0 28 28" aria-hidden="true">
                                                          <circle cx="14" cy="14" r="12" fill="#d8f14e" stroke="#8aa61a" strokeWidth="2" />
                                                          <path d="M5 8c5 3 5 9 0 12M23 8c-5 3-5 9 0 12" fill="none" stroke="#ffffff" strokeWidth="2" />
                                                        </svg>
                                                      </span>
                                                    ) : visualMatch ? (
                                                      <button
                                                        type="button"
                                                        className="round-robin-matrix__match"
                                                        onClick={() => {
                                                          if (visualMatch.roundNumber !== null) {
                                                            setSelectedRoundNumber(visualMatch.roundNumber);
                                                          }
                                                        }}
                                                      >
                                                        {visualMatch.isTie ? (
                                                          // Eduardo 2026-09-17: en la matriz una serie no lista sus
                                                          // partidos; solo "Serie" y, ya definida, quién la ganó.
                                                          visualMatch.winnerParticipantId !== null ? (
                                                            <>
                                                              <strong className="round-robin-matrix__score">
                                                                Serie {tieScoreFromPerspective(visualMatch.scoreText, visualMatch.player1Id === rowParticipant.id) ?? ""}
                                                              </strong>
                                                              <span>
                                                                {rowWon ? "Ganó" : "Perdió"} · {compactParticipantDisplayName(rowWon ? rowParticipant : columnParticipant)}
                                                                {visualMatch.roundNumber !== null ? ` · J${visualMatch.roundNumber}` : ""}
                                                              </span>
                                                            </>
                                                          ) : (
                                                            <>
                                                              {jornadaChip}
                                                              <strong>Serie</strong>
                                                              <span>{when?.day ? [when.day, when.rest].filter(Boolean).join(" · ") : "por programar"}</span>
                                                            </>
                                                          )
                                                        ) : resultText ? (
                                                          <>
                                                            <strong className="round-robin-matrix__score">{resultText}</strong>
                                                            <span>{rowWon ? "Ganó" : "Perdió"}{visualMatch.roundNumber !== null ? ` · J${visualMatch.roundNumber}` : ""}</span>
                                                          </>
                                                        ) : visualMatch.winnerParticipantId !== null && !drop ? (
                                                          // W.O. / default sin marcador: dice qué pasó, no «por programar».
                                                          <>
                                                            <strong className="round-robin-matrix__score">{rrNoScoreLabel(visualMatch.status)}</strong>
                                                            <span>{rowWon ? "Ganó" : "Perdió"}{visualMatch.roundNumber !== null ? ` · J${visualMatch.roundNumber}` : ""}</span>
                                                          </>
                                                        ) : drop ? (
                                                          <>
                                                            {jornadaChip}
                                                            <span className="round-robin-matrix__drop">{dropMatchLabel(drop)}</span>
                                                          </>
                                                        ) : when?.day ? (
                                                          <>
                                                            {jornadaChip}
                                                            <strong className="rr-poster__day">{when.day}</strong>
                                                            {when.rest ? <span>{when.rest}</span> : null}
                                                          </>
                                                        ) : (
                                                          <>
                                                            {jornadaChip}
                                                            <span>por programar</span>
                                                          </>
                                                        )}
                                                      </button>
                                                    ) : null}
                                                  </td>
                                                );
                                              })}
                                              <td className="rr-poster__stat rr-poster__stat--gp">{standing ? `${standing.wins}-${standing.losses}` : "—"}</td>
                                              <td className="rr-poster__stat">{standing ? `${standing.games_won}-${standing.games_lost}` : "—"}</td>
                                              <td className="rr-poster__stat rr-poster__place">{standing && anyPlayed ? `${standing.position}º` : "—"}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                  {rolRounds.length > 0 ? (
                                    <div className="rr-poster__rol" aria-label={`Rol de juegos · ${group.name}`}>
                                      <span className="rr-poster__eyebrow">Rol de juegos</span>
                                      <div className="rr-poster__rol-grid">
                                        {rolRounds.map(([round, items]) => (
                                          <div key={round} className="rr-poster__rol-col">
                                            <button
                                              type="button"
                                              className={`rr-poster__pill${round === selectedRoundNumber ? " is-current" : ""}`}
                                              onClick={() => {
                                                if (round > 0) setSelectedRoundNumber(round);
                                              }}
                                            >
                                              {round > 0 ? `Jornada ${round}` : "Sin jornada"}
                                            </button>
                                            {items.map((item) => (
                                              <span key={item.key} className={item.muted ? "rr-poster__muted" : undefined}>{item.text}</span>
                                            ))}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}
                                </>
                              );
                            })()}
                          </details>
                        );
                      })}
                    </div>
                    {!arranging ? (
                      <div className="rr-poster__legend" aria-label="Leyenda de la tabla">
                        <span><i className="rr-poster__sw rr-poster__sw--won" />Ganó · marcador</span>
                        <span><i className="rr-poster__sw rr-poster__sw--lost" />Perdió</span>
                        <span><i className="rr-poster__sw rr-poster__sw--pending" />Por jugar · día · hora · cancha</span>
                        <span><i className="rr-poster__sw rr-poster__sw--off" />Cancelado / W.O.</span>
                        <span><i className="rr-poster__sw rr-poster__sw--current" />Jornada seleccionada</span>
                      </div>
                    ) : null}
                  </section>
                )}
                {changeGroupFor && token ? renderChangeGroupDialog(changeGroupFor) : null}
                {selectedRoundRestLines.length > 0 ? (
                  <div className="schedule-rest-lines" data-no-toast data-jornada-rests>
                    {selectedRoundRestLines.map((line) => (
                      <p key={line.key} className="muted schedule-rest-line">
                        {line.text}
                      </p>
                    ))}
                  </div>
                ) : null}
              </section>
              {isTeamPlay && !isLadderPlayoffs ? (
                <section className="card wiz-card drw-card" data-draw-card="equipos">
                  <div className="wiz-card__head drw-card__head">
                    <h2 className="hint-label">
                      Alineaciones, elegibilidad y posiciones
                      <FieldHint label="¿Por qué viven en otros pasos?">
                        Lo propio del juego por equipos se maneja en su paso; aquí solo está el atajo.
                      </FieldHint>
                    </h2>
                  </div>
                  <div className="drw-links">
                    {[
                      { what: "Elegibilidad", where: "Paso Grupos", detail: "Pareja completa y pagos; las no elegibles quedan fuera del reparto con su motivo.", to: `${checklistBase}/groups`, label: "Ir a Grupos" },
                      { what: "Alineación por línea", where: "Paso Calendario", detail: "Jugador por línea; sin alineación dice «Alineación por definir».", to: schedulePathBase, label: "Ir a Calendario" },
                      { what: "Modo de alineación", where: "Paso Formato", detail: "Sección «Juego por equipos» (capitán / sorteo).", to: `/admin/tournaments/${tournamentId}/setup/format`, label: "Ir a Formato" },
                      { what: "Posiciones por series", where: "Posiciones", detail: "Tabla calculada por series ganadas.", to: `/admin/tournaments/${tournamentId}/standings`, label: "Ir a Posiciones" },
                    ].map((item) => (
                      <div key={item.what} className="drw-links__row">
                        <div className="drw-links__text">
                          <strong>{item.what}</strong>
                          <small>{item.where} · {item.detail}</small>
                        </div>
                        <Link
                          className="btn btn--ghost btn--compact"
                          to={`${item.to}${selectedDivisionId ? `?division=${selectedDivisionId}` : ""}`}
                        >
                          {item.label}
                        </Link>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}
              {showCreatePlayoffPlaceholders || rrUnfilledPlaceholderCount > 0 || showGeneratePlayoffs || roundRobinPlayoffVisualRounds.length > 0 || (!isLadderPlayoffs && qualificationPreview?.enabled) ? (
                <section className="card wiz-card drw-card" data-draw-card="playoffs">
                  <div className="wiz-card__head drw-card__head">
                    <h2 className="hint-label">
                      {isTeamPlay ? "Playoffs por equipos" : "Playoffs"}
                      {!isLadderPlayoffs ? (
                        <FieldHint label="¿Cómo se llenan los playoffs?">
                          {rrUnfilledPlaceholderCount > 0
                            ? "Los playoffs están «por definir» (1.º Grupo A, Ganador SF1…): prográmalos en Calendario. Al capturar el último resultado de grupos se llenan solos con los clasificados y conservan fecha, hora y cancha."
                            : "Los playoffs se generan solos al capturar el último resultado de grupos. Si corriges o despublicas un resultado de grupos, el cuadro que aún no se juega se reacomoda solo; una ronda ya jugada no se toca."}
                        </FieldHint>
                      ) : null}
                    </h2>
                    {rrUnfilledPlaceholderCount > 0 ? <span className="drw-pill">Por definir</span> : null}
                  </div>
                  {!isLadderPlayoffs ? (
                    <p className="drw-chipline hint-label">
                      <span className={`drw-pill${thirdPlaceEnabled ? " is-ok" : ""}`}>
                        Partido por 3er lugar: {thirdPlaceEnabled ? "sí" : "no"}
                      </span>
                      <FieldHint label="¿Dónde se cambia el 3er lugar?">Se cambia en el paso Formato de la categoría.</FieldHint>
                    </p>
                  ) : null}
                  {showCreatePlayoffPlaceholders || showGeneratePlayoffs ? (
                    <div className="form__actions drw-actions hint-label">
                      {showCreatePlayoffPlaceholders ? (
                        <button
                          type="button"
                          className="btn"
                          onClick={handleCreatePlayoffPlaceholders}
                          disabled={generating}
                        >
                          {generating ? "Creando…" : "Crear cuadro de playoffs por definir"}
                        </button>
                      ) : null}
                      {showCreatePlayoffPlaceholders ? (
                        <FieldHint label="¿Para qué crear el cuadro por definir?">
                          Crea el cuadro de playoffs con lugares por definir (1.º Grupo A vs 2.º Grupo B…) para programar fecha, hora y cancha antes de que terminen los grupos.
                        </FieldHint>
                      ) : null}
                      {showGeneratePlayoffs ? (
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={handleGenerateRoundRobinPlayoffs}
                          disabled={generating}
                        >
                          {generating ? "Generando…" : "Generar Playoffs"}
                        </button>
                      ) : null}
                      {showGeneratePlayoffs && !isLadderPlayoffs ? (
                        <FieldHint label="¿Cuándo uso «Generar Playoffs»?">
                          Respaldo: solo si sembraste a mano, los grupos terminaron sin cuadro o el armado automático no pudo.
                        </FieldHint>
                      ) : null}
                    </div>
                  ) : null}
                  {roundRobinPlayoffVisualRounds.length > 0 && (
                    <section ref={roundRobinPlayoffVisualRef} className="round-robin-playoff-visual elimination-visual" aria-label="Bracket visual Playoffs">
                      <div className="round-robin-visual__header elimination-visual__header">
                        <div>
                          <h3 className="hint-label">Bracket visual Playoffs<FieldHint label="¿Qué muestra «Bracket visual Playoffs»?">Esquema del cuadro con globos, conectores y campeón. Toca una tarjeta para abrir su jornada abajo.</FieldHint></h3>
                        </div>
                        <div className="form__actions rr-playoff-visual__actions">
                          <button
                            type="button"
                            className="btn btn--small"
                            onClick={() => printRoundRobinPlayoffVisual()}
                          >
                            PDF / imprimir
                          </button>
                          <button
                            type="button"
                            className="btn btn--small btn--ghost"
                            onClick={() => void handleRegeneratePlayoffs()}
                            disabled={!regenerateState.enabled}
                            title={regenerateState.reason ?? "Borra este cuadro y lo arma de nuevo con la configuración actual."}
                            data-regenerate-playoffs
                          >
                            Rehacer cuadro de playoffs
                          </button>
                        </div>
                      </div>
                      <div className="elimination-visual__viewport">
                        <div className="elimination-visual__bracket" style={{ "--elimination-bracket-zoom": eliminationVisualZoom } as CSSProperties}>
                          <EliminationBracket rounds={roundRobinPlayoffBracketRounds} onMatchClick={openRoundRobinPlayoffMatch} thirdPlace={roundRobinPlayoffThirdPlace} />
                        </div>
                      </div>
                    </section>
                  )}
                <ConsolationBracketCard matches={roundRobinMatches} zoom={eliminationVisualZoom} />

                {rrUnfilledPlaceholderCount > 0 ? (
                  <div className="drw-manual" role="group" aria-label="Acción manual">
                    <strong className="drw-manual__title">Acción manual</strong>
                    <div className="form__actions hint-label">
                      <button
                        type="button"
                        className="btn btn--ghost btn--compact"
                        onClick={handleFillPlayoffPlaceholders}
                        disabled={generating || !rrGroupStageComplete}
                        title={rrGroupStageComplete ? undefined : "Se activa cuando todos los resultados de grupos estén capturados."}
                      >
                        {generating ? "Llenando…" : "Llenar playoffs con los clasificados"}
                      </button>
                      <FieldHint label="¿Cuándo uso «Llenar playoffs»?">
                        Úsalo solo si corregiste un resultado de grupos; normalmente se llenan solos.
                      </FieldHint>
                    </div>
                  </div>
                ) : null}
              </section>
              ) : null}
              {qualificationPreview?.enabled || (qualificationPreview && roundRobinConfig?.schema_version === 2) ? (
                <section className="card wiz-card drw-card" data-draw-card="seeds">
                  <div className="wiz-card__head drw-card__head">
                    <h2 className="hint-label">
                      Clasificación y seeds
                      <FieldHint label="¿Qué muestra esta sección?">
                        Lectura en vivo de standings por grupo: aplica las reglas de Formato y ordena sembrados sin generar partidos todavía. Este preview no escribe partidos.
                      </FieldHint>
                    </h2>
                    {!qualificationPreview?.enabled ? (
                      <p>El preview de clasificación se activará cuando el formato de la categoría sea “Grupos + playoffs”.</p>
                    ) : null}
                  </div>
                  {qualificationPreview?.enabled && (
                    <section className="round-robin-qualification-preview" aria-label="Preview de clasificación a playoffs">
                      <div className="round-robin-visual__header">
                        <div>
                          <h3 className="hint-label">Preview de clasificación a playoffs<FieldHint label="¿Qué muestra «Preview de clasificación a playoffs»?">Lectura en vivo de standings por grupo: aplica las reglas de Formato y ordena sembrados sin generar partidos todavía.</FieldHint></h3>
                        </div>
                        <div className="setup-summary-chip">
                          {qualificationPreview.summary.qualifier_count} clasificado{qualificationPreview.summary.qualifier_count === 1 ? "" : "s"}
                        </div>
                      </div>
                      {playoffPreviewNamesPending ? (
                        <p className="playoff-preview-pending-note" data-no-toast data-playoff-names-pending>
                          {PLAYOFF_NAMES_PENDING_NOTE}
                        </p>
                      ) : null}
                      {qualificationPreview.summary.playoffs_auto_blocked?.reason ? (
                        <p className="field-warning">
                          ⚠️ Los playoffs no se generaron solos: {qualificationPreview.summary.playoffs_auto_blocked.reason}
                        </p>
                      ) : null}
                      {qualificationPreview.summary.same_group_warning ? (
                        <p className="field-warning" data-same-group-warning>
                          ⚠️ {qualificationPreview.summary.same_group_warning}
                        </p>
                      ) : null}
                      {qualificationPreview.summary.groups_mismatch ? (
                        <p className="field-warning">
                          ⚠️ El Formato dice {qualificationPreview.summary.configured_groups_count} grupo{qualificationPreview.summary.configured_groups_count === 1 ? "" : "s"}, pero esta categoría tiene {qualificationPreview.summary.real_groups_count}. Los clasificados y los playoffs automáticos se calculan con los grupos reales: si el cuadro no sale como esperas, corrige el número de grupos en Formato (Paso 5).
                        </p>
                      ) : null}
                      <div className="responsive-table-wrap">
                        <table className="data-table tournament-players-table qualification-preview-table">
                          <thead>
                            <tr>
                              <th>Seed</th>
                              <th>Jugador / pareja</th>
                              <th>Grupo</th>
                              <th>Pos.</th>
                              {showPointsColumn(qualificationPreview.standings) && <th>Pts</th>}
                              <th>W-L</th>
                              <th>Dif.</th>
                              <th>Clasifica por</th>
                              <th>Seed manual</th>
                            </tr>
                          </thead>
                          <tbody>
                            {qualificationPreview.qualifiers.length === 0 ? (
                              <tr>
                                <td colSpan={showPointsColumn(qualificationPreview.standings) ? 9 : 8}>
                                  Aún no hay clasificados calculables. Captura resultados de grupos o revisa que existan jugadores asignados.
                                </td>
                              </tr>
                            ) : qualificationPreview.qualifiers.map((row) => (
                              <tr key={row.participant_id}>
                                <td>#{row.seed}</td>
                                <td>
                                  {playoffPreviewPlaceholders?.get(row.participant_id) ?? row.display_name}
                                </td>
                                <td>{row.group_name ?? "—"}</td>
                                <td>{row.position}°</td>
                                {showPointsColumn(qualificationPreview.standings) && <td>{row.points}</td>}
                                <td>{row.wins}-{row.losses}</td>
                                <td>{row.differential}</td>
                                <td>{row.qualification_reason}</td>
                                <td>
                                  <input
                                    className="seed-override-input"
                                    type="number"
                                    min={1}
                                    max={qualificationPreview.qualifiers.length}
                                    value={seedOverrideDrafts[row.participant_id] ?? String(row.seed)}
                                    disabled={Boolean(manualSeedLock) || savingSeedOverrides}
                                    aria-label={`Seed manual para ${playoffPreviewPlaceholders?.get(row.participant_id) ?? row.display_name}`}
                                    onChange={(event) =>
                                      setSeedOverrideDrafts((current) => ({
                                        ...current,
                                        [row.participant_id]: event.target.value,
                                      }))
                                    }
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {qualificationPreview.qualifiers.length > 0 && (
                        <div className="qualification-seed-cards" aria-label="Sembrados de playoffs por ranking final">
                          {qualificationPreview.qualifiers.map((row) => (
                            <article key={row.participant_id} className="qualification-seed-card">
                              <strong>#{row.seed}</strong>
                              <div>
                                <span>{playoffPreviewPlaceholders?.get(row.participant_id) ?? row.display_name}</span>
                                <small>
                                  {playoffPreviewPlaceholders ? "Por definir" : `Rank ${row.position}`}
                                </small>
                              </div>
                            </article>
                          ))}
                        </div>
                      )}
                      <div className="seed-override-panel" aria-label="Overrides manuales de sembrados con auditoría">
                        <div>
                          <strong className="hint-label">
                            Overrides manuales de seeds
                            <FieldHint>
                              Ajusta comodines/sembrados antes de generar playoffs. Requiere razón y queda auditado; se abre cuando hay resultados de grupos y se bloquea cuando ya existen resultados de playoffs.
                            </FieldHint>
                          </strong>
                        </div>
                        <label>
                          Razón del ajuste
                          <input
                            type="text"
                            value={seedOverrideReason}
                            disabled={Boolean(manualSeedLock) || savingSeedOverrides || qualificationPreview.qualifiers.length === 0}
                            placeholder="Ej. criterio del comité / lesión / wildcard autorizado"
                            onChange={(event) => setSeedOverrideReason(event.target.value)}
                          />
                        </label>
                        <div className="seed-override-panel__actions">
                          <button
                            type="button"
                            className="btn btn--ghost"
                            disabled={Boolean(manualSeedLock) || savingSeedOverrides || qualificationPreview.qualifiers.length === 0}
                            onClick={() => void handleSaveSeedOverrides()}
                          >
                            {savingSeedOverrides ? "Guardando…" : "Guardar seeds manuales"}
                          </button>
                          {qualificationPreview.manual_seed_overrides?.length ? (
                            <small>
                              Último override: {qualificationPreview.manual_seed_overrides[0]?.reason ?? "sin razón"}
                            </small>
                          ) : null}
                          {manualSeedLock ? (
                            <small data-manual-seeds-locked data-no-toast>
                              {manualSeedLock}
                            </small>
                          ) : null}
                        </div>
                      </div>
                      <p className="muted" style={{ marginTop: "0.75rem" }}>
                        Este preview no escribe partidos. Los seeds se usan abajo para previsualizar el bracket antes de generar cruces reales.
                      </p>
                    </section>
                  )}

                  {qualificationPreview?.enabled && bracketPreview && roundRobinPlayoffVisualRounds.length === 0 && (
                    <section ref={playoffBracketPreviewVisualRef} className="round-robin-playoff-visual elimination-visual" aria-label="Preview de bracket de playoffs">
                      <div className="round-robin-visual__header elimination-visual__header">
                        <div>
                          <h3 className="hint-label">Preview de bracket de playoffs<FieldHint label="¿Qué muestra «Preview de bracket de playoffs»?">Simulación read-only con preliminares/BYEs según la política de Formato. Todavía no genera partidos reales.</FieldHint></h3>
                        </div>
                        <div className="table-actions">
                          <span className="setup-summary-chip">
                            {bracketPreview.preliminary_matches_count > 0
                              ? `${bracketPreview.preliminary_matches_count} preliminar(es)`
                              : `${bracketPreview.bye_count} BYE(s)`}
                          </span>
                          {previewBracketRounds.length > 0 && (
                            <button
                              type="button"
                              className="btn btn--small"
                              onClick={() => printEliminationVisual(playoffBracketPreviewVisualRef.current, "Preview playoffs")}
                            >
                              PDF / imprimir
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="muted" style={{ marginBottom: "0.75rem" }}>
                        {bracketPreview.summary}
                      </p>
                      {playoffPreviewNamesPending && previewBracketRounds.length > 0 ? (
                        <p className="playoff-preview-pending-note" data-no-toast data-playoff-bracket-names-pending>
                          {PLAYOFF_NAMES_PENDING_NOTE}
                        </p>
                      ) : null}
                      {previewBracketRounds.length === 0 ? (
                        <p className="muted">
                          {bracketPreview.enabled
                            ? "El bracket está marcado como manual; en el siguiente sprint podremos agregar edición de sembrados antes de generar."
                            : "Aún faltan clasificados suficientes para armar el bracket."}
                        </p>
                      ) : (
                        <div className="elimination-visual__viewport">
                          <div className="elimination-visual__bracket" style={{ "--elimination-bracket-zoom": eliminationVisualZoom } as CSSProperties}>
                            <EliminationBracket rounds={previewBracketRounds} />
                          </div>
                        </div>
                      )}
                    </section>
                  )}

                </section>
              ) : null}
              <section className="card wiz-card drw-card" data-draw-card="editar">
                <div className="wiz-card__head drw-card__head">
                  <h2>Editar partidos</h2>
                  <p>{roundRobinMatches.length} partido{roundRobinMatches.length === 1 ? "" : "s"} · cambia jugadores, jornada o grupo.</p>
                </div>
                <div className="responsive-table-wrap">
                  {/* Clase propia (Eduardo 2026-08-30): heredaba
                      tournament-players-table, cuyo nth-child(1) mide 23rem
                      para el NOMBRE del jugador — aquí la primera columna es
                      "#1" y quedaba absurdamente ancha. */}
                  <table className="data-table rr-matchups-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Jornada</th>
                        <th>Grupo</th>
                        <th>Jugador 1</th>
                        <th>Jugador 2</th>
                        <th>Estado</th>
                        <th>Calendario</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRoundRobinMatches.length === 0 ? (
                        <tr>
                          <td colSpan={8}>
                            {roundRobinMatches.length === 0
                              ? (isLadderPlayoffs ? "Aún no hay playoffs generados para esta categoría." : "Aún no hay enfrentamientos generados. Usa Generar Round Robin.")
                              : "Esta jornada no tiene enfrentamientos asignados."}
                          </td>
                        </tr>
                      ) : (
                        selectedRoundRobinMatches.map((match, index) => {
                          const edit = roundRobinEdits[match.id] ?? editFromRoundRobinMatch(match);
                          const selectedGroupId = Number(edit.groupId || match.group_id || 0);
                          const isPlayoffEdit = selectedGroupId <= 0 && match.group_id === null && isPhasePlayoffMatch(match);
                          const playerOptions = isPlayoffEdit ? playoffPlayerOptions : rankedPlayerOptions(
                            selectedGroupId > 0
                              ? groupParticipants[selectedGroupId] || []
                              : allRoundRobinPlayerOptions,
                          );
                          const dirty = !roundRobinEditMatches(edit, match);
                          const saving = savingRoundRobinMatchId === match.id;
                          const matchResultText = playoffMatchResultLabel(match);
                          const playerSelectLocked = playerSelectDisabled({
                            saving,
                            isPlayoff: isPlayoffEdit,
                            groupId: selectedGroupId,
                            hasResult: isPlayoffEdit && Boolean(match.winner_participant_id || matchResultText),
                          });
                          const optionLabel = (participant: TournamentParticipant) => isPlayoffEdit
                            ? playoffPlayerOptionLabel(compactParticipantDisplayName(participant), playoffOptionInfoById.get(participant.id))
                            : rankedPlayerOptionLabel(participant);
                          return (
                            <tr key={match.id}>
                              <td>#{match.match_number ?? index + 1}</td>
                              <td>
                                <select
                                  className="t123-select input input--compact"
                                  value={edit.roundNumber}
                                  onChange={(event) => setRoundRobinEdit(match, "roundNumber", event.target.value)}
                                  disabled={saving}
                                >
                                  {availableRoundNumbers.map((roundNumber) => (
                                    <option key={roundNumber} value={roundNumber}>
                                      Jornada {roundNumber}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <select
                                  className="t123-select input input--compact"
                                  value={edit.groupId}
                                  onChange={(event) => setRoundRobinEdit(match, "groupId", event.target.value)}
                                  disabled={saving}
                                >
                                  <option value="">Playoffs / sin grupo</option>
                                  {groups.map((group) => (
                                    <option key={group.id} value={group.id}>
                                      {group.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                {/* Etiqueta visible que se parte en 2 renglones (un select
                                    nativo cerrado corta los nombres largos); el select real
                                    va encima, transparente, y abre la lista de siempre. */}
                                <div className={`rr-player-pick${playerSelectLocked ? " is-disabled" : ""}`}>
                                <span className="rr-player-pick__label" aria-hidden="true">
                                  {(() => {
                                    const current = playerOptions.find((participant) => String(participant.id) === String(edit.player1));
                                    if (current) return optionLabel(current);
                                    const brief = match.player1_participant_id != null && String(match.player1_participant_id) === String(edit.player1) ? match.player1 : null;
                                    return brief ? matchParticipantName(brief, "— jugador —") : "— jugador —";
                                  })()}
                                </span>
                                <select
                                  className="rr-player-pick__select"
                                  aria-label="Jugador 1"
                                  value={edit.player1}
                                  onChange={(event) => setRoundRobinEdit(match, "player1", event.target.value)}
                                  disabled={playerSelectLocked}
                                  title={isPlayoffEdit && playerSelectLocked && !saving ? "Este partido ya tiene resultado: bórralo antes de cambiar jugadores." : undefined}
                                >
                                  <option value="">— jugador —</option>
                                  {playerOptions.map((participant) => (
                                    <option key={participant.id} value={participant.id}>
                                      {optionLabel(participant)}
                                    </option>
                                  ))}
                                </select>
                                </div>
                              </td>
                              <td>
                                {/* Etiqueta visible que se parte en 2 renglones (un select
                                    nativo cerrado corta los nombres largos); el select real
                                    va encima, transparente, y abre la lista de siempre. */}
                                <div className={`rr-player-pick${playerSelectLocked ? " is-disabled" : ""}`}>
                                <span className="rr-player-pick__label" aria-hidden="true">
                                  {(() => {
                                    const current = playerOptions.find((participant) => String(participant.id) === String(edit.player2));
                                    if (current) return optionLabel(current);
                                    const brief = match.player2_participant_id != null && String(match.player2_participant_id) === String(edit.player2) ? match.player2 : null;
                                    return brief ? matchParticipantName(brief, "— jugador —") : "— jugador —";
                                  })()}
                                </span>
                                <select
                                  className="rr-player-pick__select"
                                  aria-label="Jugador 2"
                                  value={edit.player2}
                                  onChange={(event) => setRoundRobinEdit(match, "player2", event.target.value)}
                                  disabled={playerSelectLocked}
                                  title={isPlayoffEdit && playerSelectLocked && !saving ? "Este partido ya tiene resultado: bórralo antes de cambiar jugadores." : undefined}
                                >
                                  <option value="">— jugador —</option>
                                  {playerOptions.map((participant) => (
                                    <option key={participant.id} value={participant.id}>
                                      {optionLabel(participant)}
                                    </option>
                                  ))}
                                </select>
                                </div>
                              </td>
                              <td>{statusLabel(match.status)}</td>
                              <td>
                                {matchResultText ?? (match.scheduled_date
                                  ? `${formatDateWithTime(match.scheduled_date, match.scheduled_time)}${courtNoticeName(match.court) ? ` · ${courtNoticeName(match.court)}` : ""}`
                                  : unscheduledMatchText)}
                              </td>
                              <td>
                                <div className="table-actions">
                                  <button
                                    type="button"
                                    className="btn btn--small"
                                    onClick={() => void saveRoundRobinMatch(match)}
                                    disabled={!dirty || saving}
                                  >
                                    {saving ? "Guardando…" : "Guardar"}
                                  </button>
                                  {dirty && (
                                    <button
                                      type="button"
                                      className="btn btn--small btn--ghost"
                                      onClick={() => setRoundRobinEdits((current) => {
                                        const next = { ...current };
                                        delete next[match.id];
                                        return next;
                                      })}
                                      disabled={saving}
                                    >
                                      Cancelar
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                {roundRobinChangedCount > 0 && (
                  <p className="muted">
                    {roundRobinChangedCount} cambio{roundRobinChangedCount === 1 ? "" : "s"} pendiente{roundRobinChangedCount === 1 ? "" : "s"} por guardar.
                  </p>
                )}
                {/* En el wizard el botón se oculta (Eduardo 2026-08-30): saltaba
                    directo al paso 10 brincándose Comunicación; el Calendario se
                    alcanza con «Continuar a Calendario», al final del paso 9.
                    Fuera del wizard sigue. */}
                {!setupMode ? (
                  <div className="form__actions">
                    <Link
                      className="btn btn--ghost"
                      to={`${schedulePathBase}${selectedDivisionId ? `?division=${selectedDivisionId}` : ""}`}
                    >
                      Programar en Calendario
                    </Link>
                  </div>
                ) : null}
              </section>
            </>
          ) : formatConfig?.format_type === "elimination" ? (
            <>
              <section className="card wiz-card drw-card" data-draw-card="armar">
                <div className="wiz-card__head drw-card__head">
                  <h2 className="hint-label">
                    Armar el cuadro
                    <FieldHint label="¿Qué se arma aquí?">
                      Primero arma los enfrentamientos del cuadro. Después entra a Calendario solo para poner fecha, hora y cancha a esos partidos.
                    </FieldHint>
                  </h2>
                </div>
                {eliminationTotalMatches === 0 && (
                  <div className="form__actions">
                    {eliminationTotalMatches === 0 ? (
                      <>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => void handleGenerateElimination("ranking")}
                          disabled={generating}
                        >
                          {generating ? "Armando…" : "Armar por ranking"}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={() => void handleGenerateElimination("random")}
                          disabled={generating}
                        >
                          Random
                        </button>
                      </>
                    ) : null}
                  </div>
                )}
                {eliminationConfig && (
                  <p className="muted" style={{ marginTop: "0.75rem" }}>
                    Formato: Ronda {eliminationConfig.bracket_size ?? 128} · {eliminationConfig.bracket_round_count ?? 7} rondas · default {eliminationConfig.default_generation_mode === "random" ? "random" : "por ranking"}.
                  </p>
                )}
                <div className="elimination-summary-grid" aria-label="Resumen eliminatoria">
                  <div className="elimination-summary-card">
                    <span>Jugadores</span>
                    <strong>{drawParticipants.length}</strong>
                    <small>{groups.length} grupo{groups.length === 1 ? "" : "s"}</small>
                  </div>
                  <div className="elimination-summary-card">
                    <span>Partidos</span>
                    <strong>{eliminationTotalMatches}</strong>
                    <small>Cuadro cargado</small>
                  </div>
                  <div className="elimination-summary-card elimination-summary-card--bye">
                    <span>
                      {eliminationDisplayHasDirectAdvancers || eliminationUsesActualDirectAdvancers ? "BYEs / directo" : "BYEs"}
                    </span>
                    <strong>
                      {eliminationDisplayHasDirectAdvancers || eliminationUsesActualDirectAdvancers
                        ? eliminationDisplayDirectAdvancerCount
                        : eliminationDirectAdvancerSummary.technicalByeCount}
                    </strong>
                    <small>{eliminationDisplayByeHelper}</small>
                  </div>
                  <div className="elimination-summary-card">
                    <span>Resultados</span>
                    <strong>{eliminationResolvedCount}/{eliminationTotalMatches}</strong>
                    <small>
                      {eliminationBracketComplete
                        ? "Bracket completo"
                        : `${eliminationPendingCount} pendiente${eliminationPendingCount === 1 ? "" : "s"}`}
                    </small>
                  </div>
                  <div className="elimination-summary-card">
                    <span>Calendario</span>
                    {usesCourtCalendar ? (
                      <>
                        <strong>{eliminationScheduledCount}</strong>
                        <small>Con fecha, hora o cancha</small>
                      </>
                    ) : (
                      <>
                        <strong>—</strong>
                        <small>Sin calendario de canchas</small>
                      </>
                    )}
                  </div>
                </div>
                {(eliminationByeCount > 0 || eliminationDisplayHasDirectAdvancers) && (
                  <p className="elimination-bye-note">
                    {eliminationDisplayHasDirectAdvancers
                      ? `${eliminationDisplayDirectAdvancerCount} jugador(es) descansan la preliminar porque no aparecen en los enfrentamientos reales de esa ronda.`
                      : "BYE significa que ese jugador avanza sin jugar esa ronda. No es un error ni un jugador sin asignar."}
                  </p>
                )}
              </section>
              {token ? (
                <DrawCeremonyAdminCard
                  token={token}
                  tournamentId={tournamentId}
                  divisionId={selectedDivisionId ?? null}
                  // Fingerprint real (ids+status+updated_at): un rearmado del MISMO
                  // tamaño también recarga el acta (gate de #135).
                  refreshKey={`${selectedDivisionId ?? "all"}-${drawCeremonyRefreshFingerprint(tournamentMatches)}`}
                  matchesSnapshot={{ matches: tournamentMatches, owner: tournamentMatchesOwner }}
                  tournamentName={tournament?.name ?? null}
                  divisionName={activeDivision?.name ?? null}
                  organizerLogoUrl={uploadAssetHref(tournament?.organizer_logo_url)}
                />
              ) : null}
              <section className="card wiz-card drw-card" data-draw-card="previo">
                <div className="wiz-card__head drw-card__head">
                  <h2>{eliminationTotalMatches === 0 ? "Bracket previo y armado manual" : "Jugadores inscritos"}</h2>
                </div>
                {eliminationTotalMatches === 0 && !normalizedMatchSearchQuery ? (
                  <section className="elimination-draw-start-card" aria-label="Armar draw eliminatorio">
                    <div className="elimination-draw-start-card__header">
                      <div>
                        <span className="elimination-draw-start-card__eyebrow">Draw de eliminatoria</span>
                        <h3 className="hint-label">Bracket previo con jugadores<FieldHint label="¿Qué muestra «Bracket previo con jugadores»?">Ajusta manualmente las posiciones si lo necesitas, imprime la vista previa o genera el draw oficial por ranking/random/manual.</FieldHint></h3>
                      </div>
                      <button
                        type="button"
                        className="btn btn--small btn--ghost"
                        onClick={() => printEliminationVisual(eliminationPreviewVisualRef.current, "Draw previo")}
                      >
                        PDF / imprimir
                      </button>
                    </div>
                    <div ref={eliminationPreviewVisualRef} className="elimination-visual elimination-visual--preview" aria-label="Bracket visual previo con zoom">
                      <div className="elimination-visual__tools" aria-label="Herramientas del draw previo">
                        <button type="button" className="btn btn--small btn--ghost" onClick={() => updateEliminationVisualZoom(-0.12)}>
                          − Zoom
                        </button>
                        <span className="elimination-visual__zoom-value">{Math.round(eliminationVisualZoom * 100)}%</span>
                        <button type="button" className="btn btn--small btn--ghost" onClick={() => updateEliminationVisualZoom(0.12)}>
                          + Zoom
                        </button>
                        <button type="button" className="btn btn--small btn--ghost" onClick={() => setEliminationVisualZoom(0.62)}>
                          Ver todo
                        </button>
                      </div>
                      <div className="elimination-visual__viewport">
                        <div className="elimination-visual__bracket" style={{ "--elimination-bracket-zoom": eliminationVisualZoom } as CSSProperties}>
                          <EliminationBracket rounds={manualEliminationPreviewBracketRounds} />
                        </div>
                      </div>
                    </div>
                    <div className="elimination-manual-draw-preview" aria-label="Vista previa del draw manual">
                      {previewDirectAdvancers.length > 0 && (
                        <div className="elimination-manual-draw-preview__direct">
                          <strong>Pasan directo</strong>
                          {previewDirectAdvancers.map((participant, index) => (
                            <label key={participant.id}>
                              <span>D{index + 1}</span>
                              <select className="t123-select"
                                value={participant.id}
                                onChange={(event) => setManualDrawSlot(index, Number(event.target.value))}
                              >
                                {manualDrawRankSortedParticipants.map((option) => (
                                  <option key={option.id} value={option.id}>{participantDisplayName(option)}</option>
                                ))}
                              </select>
                            </label>
                          ))}
                        </div>
                      )}
                      <div className="elimination-manual-draw-preview__matches">
                        {previewMatchups.map((matchup) => {
                          const leftIndex = previewDirectCount + matchup.slot - 1;
                          const rightIndex = previewDirectCount + (previewPlayInCount * 2) - matchup.slot;
                          return (
                            <article key={matchup.slot} className="elimination-manual-draw-match">
                              <strong>Enfrentamiento {matchup.slot}</strong>
                              <label>
                                <span>Jugador 1</span>
                                <select className="t123-select"
                                  value={matchup.left?.id ?? ""}
                                  onChange={(event) => setManualDrawSlot(leftIndex, Number(event.target.value))}
                                >
                                  {manualDrawRankSortedParticipants.map((option) => (
                                    <option key={option.id} value={option.id}>{participantDisplayName(option)}</option>
                                  ))}
                                </select>
                              </label>
                              <label>
                                <span>Jugador 2</span>
                                <select className="t123-select"
                                  value={matchup.right?.id ?? ""}
                                  onChange={(event) => setManualDrawSlot(rightIndex, Number(event.target.value))}
                                >
                                  {manualDrawRankSortedParticipants.map((option) => (
                                    <option key={option.id} value={option.id}>{participantDisplayName(option)}</option>
                                  ))}
                                </select>
                              </label>
                            </article>
                          );
                        })}
                      </div>
                    </div>
                    <div className="form__actions">
                      <button
                        type="button"
                        className="btn"
                        onClick={() => void handleGenerateElimination("ranking")}
                        disabled={generating || drawParticipants.length < 2}
                      >
                        {generating ? "Armando…" : "Armar por ranking"}
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => void handleGenerateElimination("random")}
                        disabled={generating || drawParticipants.length < 2}
                      >
                        Random
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => void handleGenerateElimination("manual", false, manualDrawParticipantIds)}
                        disabled={generating || drawParticipants.length < 2 || manualDrawParticipantIds.length !== drawParticipants.length}
                      >
                        Armar manual
                      </button>
                    </div>
                  </section>
                ) : null}
                <details className="elimination-seeding-roster" aria-label="Jugadores inscritos para armar eliminatoria">
                  <summary className="elimination-seeding-roster__header">
                    <div>
                      <span>Jugadores inscritos para el Draw</span>
                      <strong>{drawParticipants.length} jugador{drawParticipants.length === 1 ? "" : "es"} cargado{drawParticipants.length === 1 ? "" : "s"}</strong>
                    </div>
                    <small>{activeDivision ? `Categoría ${activeDivision.name}` : "Categoría activa"}</small>
                  </summary>
                  {groups.length > 0 ? (
                    <div className="elimination-seeding-roster__groups">
                      {groups.map((group) => {
                        const list = groupParticipants[group.id] || [];
                        const groupRankMap = buildGroupRankMap(list, eliminationConfig?.seeding_source ?? "initial_rank");
                        return (
                          <article key={group.id} className="elimination-seeding-group">
                            <div className="elimination-seeding-group__title">
                              <strong>{group.name}</strong>
                              <small>{list.length} jugador{list.length === 1 ? "" : "es"}</small>
                            </div>
                            {list.length > 0 ? (
                              <ol>
                                {list.map((participant) => {
                                  const rank = participantGroupRankValue(participant, groupRankMap);
                                  return (
                                    <li key={participant.id}>
                                      <span>{participantDisplayName(participant)}</span>
                                      <small>{rank !== null ? `#${rank}` : "Sin ranking"}</small>
                                    </li>
                                  );
                                })}
                              </ol>
                            ) : (
                              <p className="muted">Grupo sin jugadores asignados.</p>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  ) : drawParticipants.length > 0 ? (
                    <div className="elimination-seeding-roster__ungrouped">
                      {drawParticipants.map((participant) => (
                        <span key={participant.id}>{participantDisplayName(participant)}</span>
                      ))}
                    </div>
                  ) : (
                    <p className="muted">No hay jugadores cargados en esta categoría. Regresa a Alta/Jugadores para agregarlos antes de armar la eliminatoria.</p>
                  )}
                </details>
              </section>
              {eliminationTotalMatches > 0 && !normalizedMatchSearchQuery ? (
                <section className="card wiz-card drw-card" data-draw-card="cuadro">
                  <div className="wiz-card__head drw-card__head">
                    <h2>Cuadro</h2>
                  </div>
                  <p className="drw-chipline hint-label">
                    <span className={`drw-pill${thirdPlaceEnabled ? " is-ok" : ""}`}>
                      Partido por 3er lugar: {thirdPlaceEnabled ? "sí" : "no"}
                    </span>
                    <FieldHint label="¿Dónde se cambia el 3er lugar?">Se cambia en el paso Formato de la categoría.</FieldHint>
                  </p>
                  {eliminationTotalMatches > 0 && !normalizedMatchSearchQuery && (() => {
                    // Rearmar destruye el cuadro: se bloquea con calendario O con
                    // resultados publicados, y siempre pide confirmación.
                    const rearmeBloqueado = eliminationScheduledCount > 0 || eliminationResolvedCount > 0;
                    const razonRearme = eliminationResolvedCount > 0
                      ? `No se puede rearmar: ${eliminationResolvedCount} partido(s) ya tienen resultado publicado. Despublícalos en Resultados y el botón se habilita.`
                      : eliminationScheduledCount > 0
                        ? `No se puede rearmar: ${eliminationScheduledCount} partido(s) de esta categoría ya tienen fecha, hora o cancha. Desprográmalos en Calendario y el botón se habilita.`
                        : "Rearma el cuadro desde cero. Solo mientras no haya calendario ni resultados.";
                    const confirmRearme = async (mode: "ranking" | "random") => {
                      setRearmMenuOpen(false);
                      const modeLabel = mode === "ranking" ? "por ranking" : "random";
                      if (!(await askConfirm(`¿Rearmar el cuadro ${modeLabel}? Se borran los enfrentamientos actuales de esta categoría y se vuelven a sortear.`, { acceptLabel: "Sí, rearmar el cuadro", tone: "danger" }))) return;
                      void handleGenerateElimination(mode, true);
                    };
                    return (
                      <div className="elimination-draw-actions" aria-label="Acciones del cuadro">
                        <div className="elimination-draw-actions__main">
                          {!setupMode ? (
                            <Link className="btn" to={eliminationScheduleLink}>
                              Calendarizar enfrentamientos
                            </Link>
                          ) : null}
                          {!eliminationTerminalMatches.some((match) => match.round_label === "Final") && (
                            <button
                              type="button"
                              className="btn btn--ghost"
                              onClick={() => void handleMaterializeEliminationSkeleton()}
                              disabled={generating}
                              title="Crea las rondas futuras del cuadro (con los BYEs ya colocados) para programar fecha, hora y cancha desde ya; los nombres se rellenan solos al avanzar el bracket."
                            >
                              Completar rondas del cuadro
                            </button>
                          )}
                          <div className="elimination-draw-actions__rearm">
                            <button
                              type="button"
                              className="btn btn--ghost"
                              onClick={() => setRearmMenuOpen((open) => !open)}
                              disabled={generating || rearmeBloqueado}
                              title={razonRearme}
                              aria-expanded={rearmMenuOpen}
                            >
                              Rearmar cuadro ▾
                            </button>
                            {rearmMenuOpen && !rearmeBloqueado ? (
                              <div className="elimination-draw-actions__menu" role="menu">
                                <button type="button" role="menuitem" className="btn btn--compact btn--ghost" onClick={() => confirmRearme("ranking")}>
                                  Por ranking
                                </button>
                                <button type="button" role="menuitem" className="btn btn--compact btn--ghost" onClick={() => confirmRearme("random")}>
                                  Random
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                        {rearmeBloqueado ? <p className="muted rearme-bloqueado-nota">{razonRearme}</p> : null}
                        <details className="elimination-draw-actions__more">
                          <summary>Más opciones</summary>
                          <div className="elimination-draw-actions__more-body">
                            <p className="muted">
                              El cuadro avanza solo al publicar cada resultado. Este botón solo rellena lo que falte si algo quedó pendiente.
                            </p>
                            {eliminationHasFinalRound ? (
                              <button
                                type="button"
                                className="btn btn--ghost btn--compact"
                                disabled
                                title={
                                  eliminationBracketComplete
                                    ? "La Final ya tiene resultado; no hay otra ronda después."
                                    : "La Final ya está armada. Captura Final y 3er Lugar para cerrar el bracket."
                                }
                              >
                                {eliminationBracketComplete ? "Bracket completo" : "Final armada"}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn--ghost btn--compact"
                                onClick={() => void handleGenerateEliminationNextRound()}
                                disabled={generating}
                                title={
                                  eliminationPendingCount > 0
                                    ? "Con esqueleto, el cuadro avanza solo al publicar cada resultado; este botón rellena lo que falte y te dirá si faltan resultados."
                                    : "Genera la siguiente ronda siguiendo el camino del bracket."
                                }
                              >
                                {generating ? "Generando…" : "Generar siguiente ronda"}
                              </button>
                            )}
                          </div>
                        </details>
                      </div>
                    );
                  })()}
                  {eliminationRounds.length > 0 && !normalizedMatchSearchQuery && (
                    <section ref={eliminationVisualRef} className="elimination-visual" aria-label="Bracket visual Eliminatoria">
                      <div className="round-robin-visual__header elimination-visual__header">
                        <div>
                          <span className="elimination-visual__eyebrow">Draw oficial · arriba → abajo</span>
                          <h3 className="hint-label">Bracket visual Eliminatoria<FieldHint label="¿Qué muestra «Bracket visual Eliminatoria»?">Lectura del cuadro completo: los sembrados de arriba empiezan en la primera tarjeta de cada columna. Usa zoom out para ver todo el draw o zoom in para revisar cada cruce.</FieldHint></h3>
                        </div>
                        <div className="elimination-visual__tools" aria-label="Herramientas del draw">
                          <button type="button" className="btn btn--small btn--ghost" onClick={() => updateEliminationVisualZoom(-0.12)}>
                            − Zoom
                          </button>
                          <span className="elimination-visual__zoom-value">{Math.round(eliminationVisualZoom * 100)}%</span>
                          <button type="button" className="btn btn--small btn--ghost" onClick={() => updateEliminationVisualZoom(0.12)}>
                            + Zoom
                          </button>
                          <button type="button" className="btn btn--small btn--ghost" onClick={() => setEliminationVisualZoom(0.62)}>
                            Ver todo
                          </button>
                          <button
                            type="button"
                            className={`btn btn--small${bracketSwapMode ? "" : " btn--ghost"}`}
                            onClick={() => {
                              setBracketSwapMode((current) => !current);
                              setSwapSelectionId(null);
                              setMessage(bracketSwapMode ? null : "Modo acomodar: toca dos enfrentamientos de la misma ronda para intercambiar su posición en el cuadro. Solo enfrentamientos sin resultado.");
                            }}
                          >
                            {bracketSwapMode ? "Salir de acomodar" : "Acomodar cuadro"}
                          </button>
                          <button type="button" className="btn btn--small" onClick={() => printEliminationVisual()}>
                            PDF / imprimir
                          </button>
                        </div>
                      </div>
                      <div className="elimination-visual__legend" aria-label="Cómo leer el draw">
                        <span><strong>Arriba</strong> primer sembrado/camino superior</span>
                        <span><strong>Centro</strong> avance por ronda</span>
                        <span><strong>Verde</strong> ganador o enfrentamiento resuelto</span>
                        <span><strong>Dorado</strong> BYE</span>
                      </div>
                      <div className="elimination-visual__viewport">
                        <div
                          className="elimination-visual__bracket"
                          style={{ "--elimination-bracket-zoom": eliminationVisualZoom } as CSSProperties}
                        >
                          <EliminationBracket
                            rounds={eliminationBracketRounds}
                            onMatchClick={bracketSwapMode ? (matchId) => void handleBracketSwapClick(matchId) : openEliminationMatchEditor}
                            thirdPlace={eliminationThirdPlaceMatch}
                            highlightMatchId={bracketSwapMode ? swapSelectionId : null}
                          />
                        </div>
                      </div>
                    </section>
                  )}
                  <ConsolationBracketCard
                    matches={tournamentMatches}
                    zoom={eliminationVisualZoom}
                    onMatchClick={openEliminationMatchEditor}
                  />
                </section>
              ) : null}
              <section className="card wiz-card drw-card" data-draw-card="editar">
                <div className="wiz-card__head drw-card__head">
                  <h2>Editar partidos</h2>
                </div>
                <div className="match-search" role="search" aria-label="Buscar jugador en enfrentamientos">
                  <label className="match-search__label" htmlFor="matchup-player-search">
                    Buscar jugador
                  </label>
                  <div className="match-search__row">
                    <input
                      id="matchup-player-search"
                      className="input match-search__input"
                      type="search"
                      value={matchSearchQuery}
                      onChange={(event) => setMatchSearchQuery(event.target.value)}
                      placeholder="Ej. Sinner, Alcaraz, Zverev…"
                      autoComplete="off"
                    />
                    {matchSearchQuery && (
                      <button
                        type="button"
                        className="btn btn--ghost match-search__clear"
                        onClick={() => setMatchSearchQuery("")}
                      >
                        Limpiar
                      </button>
                    )}
                  </div>
                  <p className="match-search__summary">
                    {normalizedMatchSearchQuery
                      ? `${eliminationFilteredMatches}/${eliminationTotalMatches} enfrentamientos encontrados`
                      : "Escribe un nombre para ver solo los enfrentamientos de ese jugador."}
                  </p>
                </div>
                {eliminationRounds.length === 0 ? (
                  <div className="elimination-empty-state">
                    <p className="muted">
                      {normalizedMatchSearchQuery
                        ? "No encontré enfrentamientos para ese jugador. Revisa el nombre o limpia la búsqueda."
                        : "Aún no hay enfrentamientos generados para esta eliminatoria."}
                    </p>
                    {!normalizedMatchSearchQuery && (
                      <div className="form__actions">
                        <button
                          type="button"
                          className="btn"
                          onClick={() => void handleGenerateElimination("ranking")}
                          disabled={generating}
                        >
                          {generating ? "Armando…" : "Armar por ranking"}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={() => void handleGenerateElimination("random")}
                          disabled={generating}
                        >
                          Random
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="elimination-bracket" aria-label="Bracket eliminatorio">
                    {eliminationRounds.map((round) => {
                      const completed = round.matches.filter(isEliminationResolved).length;
                      const byesInRound = round.matches.filter(isEliminationBye).length;
                      return (
                        <section key={round.label} className="elimination-bracket__round">
                          <header>
                            <strong>{eliminationRoundDisplayLabel(round.label)}</strong>
                            <small>
                              {completed}/{round.matches.length} resultados
                              {byesInRound > 0 ? ` · ${byesInRound} BYE` : ""}
                            </small>
                          </header>
                          <div className="elimination-bracket__matches">
                            {round.matches.map((match) => {
                              const edit = eliminationEdits[match.id] ?? eliminationEditFromMatch(match);
                              const selectedGroupId = Number(edit.groupId || match.group_id || groups[0]?.id || 0);
                              const playerOptions = rankedPlayerOptions(groupParticipants[selectedGroupId] || []);
                              const winnerId = match.winner_participant_id;
                              const dirty = !eliminationEditMatches(edit, match);
                              const saving = savingEliminationMatchId === match.id;
                              const terminal = isTerminalEliminationMatch(match);
                              const editing = !terminal || dirty || editingEliminationMatchIds.has(match.id);
                              const player1Name = matchParticipantName(
                                match.player1,
                                match.player1_participant_id ? `#${match.player1_participant_id}` : "BYE",
                              );
                              const player2Name = matchParticipantName(
                                match.player2,
                                match.player2_participant_id ? `#${match.player2_participant_id}` : "BYE",
                              );
                              const winnerName = matchParticipantName(
                                match.winner,
                                winnerId ? `Ganador #${winnerId}` : "Por definir",
                              );
                              const matchResultText = playoffMatchResultLabel(match);
                              const calendarText = matchResultText ?? (match.scheduled_date
                                ? `${formatDateWithTime(match.scheduled_date, match.scheduled_time)}${courtNoticeName(match.court) ? ` · ${courtNoticeName(match.court)}` : ""}`
                                : unscheduledMatchText);

                              if (!editing) {
                                return (
                                  <article
                                    id={`elimination-match-${match.id}`}
                                    key={match.id}
                                    className={`elimination-match elimination-match--readonly is-${match.status}${isEliminationBye(match) ? " has-bye" : ""}`}
                                    tabIndex={-1}
                                  >
                                    <div className="elimination-match__meta">
                                      <span>#{match.match_number ?? match.id}</span>
                                      <span>{calendarText}</span>
                                    </div>
                                    <div className={`elimination-match__player${winnerId && winnerId === match.player1_participant_id ? " is-winner" : ""}${!match.player1_participant_id ? " is-bye" : ""}`}>
                                      <span>{player1Name}</span>
                                    </div>
                                    <div className={`elimination-match__player${winnerId && winnerId === match.player2_participant_id ? " is-winner" : ""}${!match.player2_participant_id ? " is-bye" : ""}`}>
                                      <span>{player2Name}</span>
                                    </div>
                                    <div className="elimination-match__result elimination-match__result--summary">
                                      <strong>Ganó: {winnerName}</strong>
                                      <span>{matchScoreFromWinner(match).trim() || statusLabel(match.status)}</span>
                                    </div>
                                    <div className="table-actions">
                                      {match.player1_participant_id && match.player2_participant_id ? (
                                        <Link
                                          className="btn btn--small"
                                          to={captureResultPath(match.id)}
                                        >
                                          {winnerId ? "Editar resultado" : "Capturar resultado"}
                                        </Link>
                                      ) : null}
                                      <button
                                        type="button"
                                        className="btn btn--small btn--ghost"
                                        onClick={() => openEliminationMatchEditor(match.id)}
                                      >
                                        Editar enfrentamiento
                                      </button>
                                    </div>
                                  </article>
                                );
                              }

                              return (
                                <article
                                  id={`elimination-match-${match.id}`}
                                  key={match.id}
                                  className={`elimination-match is-${match.status}${isEliminationBye(match) ? " has-bye" : ""}`}
                                  tabIndex={-1}
                                >
                                  <div className="elimination-match__meta">
                                    <span>#{match.match_number ?? match.id}</span>
                                    <span>{formatDate(match.scheduled_date, "Sin fecha")}</span>
                                  </div>
                                  {terminal && (
                                    <p className="elimination-match__edit-note">
                                      Resultado capturado; edición manual abierta.
                                    </p>
                                  )}
                                  <label className="elimination-match__field">
                                    <small>Grupo</small>
                                    <select
                                      className="t123-select input input--compact"
                                      value={edit.groupId || (groups[0]?.id ? String(groups[0].id) : "")}
                                      onChange={(event) => setEliminationEdit(match, "groupId", event.target.value)}
                                      disabled={saving}
                                    >
                                      <option value="">— grupo —</option>
                                      {groups.map((group) => (
                                        <option key={group.id} value={group.id}>
                                          {group.name}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <div className={`elimination-match__player${winnerId && winnerId === match.player1_participant_id ? " is-winner" : ""}${isByeValue(edit.player1) ? " is-bye" : ""}`}>
                                    <select
                                      className="t123-select input input--compact"
                                      value={edit.player1}
                                      onChange={(event) => setEliminationEdit(match, "player1", event.target.value)}
                                      disabled={saving || !selectedGroupId}
                                    >
                                      <option value="">BYE</option>
                                      {playerOptions.map((participant) => (
                                        <option key={participant.id} value={participant.id}>
                                          {rankedPlayerOptionLabel(participant)}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className={`elimination-match__player${winnerId && winnerId === match.player2_participant_id ? " is-winner" : ""}${isByeValue(edit.player2) ? " is-bye" : ""}`}>
                                    <select
                                      className="t123-select input input--compact"
                                      value={edit.player2}
                                      onChange={(event) => setEliminationEdit(match, "player2", event.target.value)}
                                      disabled={saving || !selectedGroupId}
                                    >
                                      <option value="">BYE</option>
                                      {playerOptions.map((participant) => (
                                        <option key={participant.id} value={participant.id}>
                                          {rankedPlayerOptionLabel(participant)}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="elimination-match__result">
                                    {matchScoreFromWinner(match) || statusLabel(match.status)}
                                  </div>
                                  <div className="table-actions">
                                    {/* Un cruce pendiente siempre se ve en modo edición (smoke de
                                        Hermes 2026-09-13): el botón de captura también va aquí. */}
                                    {match.player1_participant_id && match.player2_participant_id ? (
                                      <Link className="btn btn--small" to={captureResultPath(match.id)}>
                                        {winnerId ? "Editar resultado" : "Capturar resultado"}
                                      </Link>
                                    ) : null}
                                    <button
                                      type="button"
                                      className="btn btn--small"
                                      onClick={() => void saveEliminationMatch(match)}
                                      disabled={!dirty || saving}
                                    >
                                      {saving ? "Guardando…" : "Guardar"}
                                    </button>
                                    {(dirty || terminal) && (
                                      <button
                                        type="button"
                                        className="btn btn--small btn--ghost"
                                        onClick={() => {
                                          setEliminationEdits((current) => {
                                            const next = { ...current };
                                            delete next[match.id];
                                            return next;
                                          });
                                          setEditingEliminationMatchIds((current) => {
                                            const next = new Set(current);
                                            next.delete(match.id);
                                            return next;
                                          });
                                        }}
                                        disabled={saving}
                                      >
                                        {dirty ? "Cancelar" : "Cerrar edición"}
                                      </button>
                                    )}
                                  </div>
                                </article>
                              );
                            })}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          ) : (
            <section className="card">
              <p>
                El Draw está disponible para Escalera, Round Robin
                y Eliminatoria configurada. Revisa el formato del torneo.
              </p>
            </section>
          )}
        </>
      )}

      {isLadder && !isLadderPlayoffs && ladderConfig && (
        <section className="card setup-call-card setup-wizard-card setup-rounds-summary-card">
          <div className="setup-call-card__heading"><span>01</span><div><h2>Resumen del formato</h2><p>Base de reglas para generar semanas, playoffs y retos.</p></div></div>
          <ul className="list list--compact">
            <li>
              Ranking utilizado:{" "}
              <strong>
                {ladderConfig.ranking_scope === "group_initial_rank"
                  ? "ranking inicial del grupo"
                  : ladderConfig.ranking_scope === "manual"
                    ? "manual"
                    : "ranking actual del grupo"}
              </strong>
              .
            </li>
          </ul>
          <p className="muted" style={{ marginTop: "0.5rem" }}>
            {latestPublishedRoundNumber !== null
              ? `${ladderRoundButtonLabel(latestPublishedRoundNumber, ladderConfig.round_count)} actual publicada: ${latestPublishedRoundNumber}. ${
                  isRoundResultPublished(latestPublishedRoundNumber) && nextDraftRoundNumber !== null
                    ? `Resultados completos; puedes armar ${ladderRoundButtonLabel(nextDraftRoundNumber, ladderConfig.round_count)}.`
                    : "Aún faltan resultados para abrir los siguientes matchups."
                }`
              : "Genera Semana 1 para iniciar el torneo."}
          </p>
          {selectedRoundNumber !== null && selectedDraftHasMatchups && canGenerateSelectedRound && (
            <div className="form__actions" style={{ marginTop: "0.5rem" }}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => void handleGenerate({ round_number: selectedRoundNumber, regenerate_drafts: true })}
                disabled={generating}
                title="Regenera este borrador; no toca semanas publicadas."
              >
                {generating ? "Regenerando…" : `Regenerar ${ladderRoundButtonLabel(selectedRoundNumber, ladderConfig.round_count)}`}
              </button>
            </div>
          )}
          {missingLadderWeeks.length > 0 && (
            <div style={{ marginTop: "0.75rem" }}>
              <div className="form__actions hint-label">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={handleGenerateAllLadderWeeks}
                  disabled={generating}
                  title="Crea en una sola pasada todas las semanas que faltan, como borrador. No toca semanas publicadas."
                >
                  {generating
                    ? "Generando…"
                    : `Generar las ${missingLadderWeeks.length} semana(s) faltantes (borrador)`}
                </button>
                <FieldHint label="¿Cómo se generan las semanas faltantes?">
                  Los borradores se emparejan con el orden actual de la escalera;
                  antes de publicar una semana puedes regenerarla para tomar el
                  orden más reciente.
                </FieldHint>
              </div>
            </div>
          )}
        {availableRoundNumbers.length > 0 && (
          <div className="rounds-selector-card drw-ladder-selector">
            <p className="drw-subtitle hint-label">Semana<FieldHint label="¿Qué semana se muestra?">Elige la semana que quieres armar. Solo se mostrará esa para mantener la pantalla corta en celular.</FieldHint></p>
            <div className="segmented segmented--scroll rounds-selector">
              {availableRoundNumbers.map((rn) => {
                const isActive = rn === selectedRoundNumber;
                const resultSummary = roundResultSummaries.get(rn);
                const hasPublishedResults = !!resultSummary && resultSummary.pending === 0;
                const hasPendingResults = !!resultSummary && resultSummary.pending > 0;
                const resultBadge = resultSummary
                  ? resultSummary.pending === 0
                    ? null
                    : `${resultSummary.pending} resultado${resultSummary.pending === 1 ? "" : "s"} pendiente${resultSummary.pending === 1 ? "" : "s"}`
                  : null;

                return (
                  <button
                    key={rn}
                    type="button"
                    className={`segmented__btn rounds-selector__btn${
                      isActive ? " is-active" : ""
                    }${rn === latestPublishedRoundNumber ? " is-current" : ""}${hasPublishedResults ? " has-published-results" : ""}${hasPendingResults ? " has-pending-results" : ""}`}
                    data-round-selector-button={rn}
                    onClick={() => setSelectedRoundNumber(rn)}
                    aria-pressed={isActive}
                  >
                    <span className="rounds-selector__btn-label">
                      {ladderRoundButtonLabel(rn, ladderBaseRoundCount(ladderConfig) || roundCount)}
                    </span>
                    {rn === latestPublishedRoundNumber && (
                      <span className="rounds-selector__current-pill">
                        Semana actual
                      </span>
                    )}
                    {resultBadge && (
                      <span
                        className={`rounds-selector__btn-status${
                          hasPendingResults ? " rounds-selector__btn-status--pending" : ""
                        }`}
                      >
                        {resultBadge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {latestPublishedRoundNumber !== null && (
              <p className="rounds-selector__legend">
                <span className="rounds-selector__legend-dot" aria-hidden="true" />
                La pestaña con borde verde y etiqueta “Semana actual” es la semana operativa que se abre automáticamente.
              </p>
            )}
          </div>
        )}

        {selectedRoundNumber !== null && !selectedRound && (
          <div className="drw-ladder-note">
            <p className="muted">
              Semana {selectedRoundNumber} aún no se ha generado.
              {selectedPreviousRoundResultsSummary
                ? ` Semana ${selectedRoundNumber - 1}: ${selectedPreviousRoundResultsSummary.published}/${selectedPreviousRoundResultsSummary.total} resultados publicados.`
                : ""}
            </p>
            {canGenerateSelectedRound ? (
              <div className="form__actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() =>
                    void handleGenerate({ round_number: selectedRoundNumber! })
                  }
                  disabled={generating}
                >
                  {generating
                    ? "Generando…"
                    : `Generar Semana ${selectedRoundNumber}`}
                </button>
              </div>
            ) : (
              <p className="form-error">{selectedRoundGenerationBlocked}</p>
            )}
          </div>
        )}

        {selectedRoundNumber !== null && selectedDraftIsEmpty && (
          <div className="drw-ladder-note">
            <p className="muted">
              Semana {selectedRoundNumber} está en borrador, pero aún no tiene retos generados.
              {selectedPreviousRoundResultsSummary
                ? ` Semana ${selectedRoundNumber - 1}: ${selectedPreviousRoundResultsSummary.published}/${selectedPreviousRoundResultsSummary.total} resultados publicados.`
                : ""}
            </p>
            {canGenerateSelectedRound ? (
              <div className="form__actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() =>
                    void handleGenerate({ round_number: selectedRoundNumber! })
                  }
                  disabled={generating}
                >
                  {generating
                    ? "Generando…"
                    : `Generar Semana ${selectedRoundNumber}`}
                </button>
              </div>
            ) : (
              <p className="form-error">{selectedRoundGenerationBlocked}</p>
            )}
          </div>
        )}

        {selectedRoundNumber !== null && selectedDraftBlocked && !selectedDraftIsEmpty && (
          <div className="drw-ladder-note">
            <p className="muted">
              Semana {selectedRoundNumber} está bloqueada hasta que se publiquen los resultados de la semana anterior.
              {selectedPreviousRoundResultsSummary
                ? ` Semana ${selectedRoundNumber - 1}: ${selectedPreviousRoundResultsSummary.published}/${selectedPreviousRoundResultsSummary.total} resultados publicados.`
                : ""}
            </p>
            <p className="form-error">{selectedRoundGenerationBlocked}</p>
          </div>
        )}

        </section>
      )}

      {selectedRound && !selectedDraftIsEmpty && !selectedDraftBlocked && (
        <SelectedRoundDetails
          tournamentId={tournament.id}
          round={selectedRound}
          groups={groups}
          groupParticipants={groupParticipants}
          participants={participants}
          rankingScope={ladderConfig?.ranking_scope ?? "group_current_rank"}
          tournamentName={tournament.name}
          divisionName={activeDivision?.name ?? "Playoffs"}
          onChanged={load}
          token={token!}
        />
      )}

      {isLadder && !isLadderPlayoffs && ladderConfig && (
        <section className="card wiz-card drw-card" data-draw-card="playoffs-escalera">
          <div className="wiz-card__head drw-card__head">
            <h2>Playoffs de escalera</h2>
          </div>
          <div className="form__actions hint-label">
              <button
                type="button"
                className="btn"
                onClick={() => void handleGeneratePlayoffs()}
                disabled={generating}
                title="Arma el siguiente playoff desde el ranking actual o desde ganadores del playoff anterior."
              >
                {generating ? "Armando…" : "Generar Playoffs"}
              </button>
            <FieldHint label="¿Qué hace «Generar Playoffs»?">
              Arma el siguiente playoff desde el ranking actual o desde ganadores del playoff anterior. Al generarlos, esta categoría usa la vista de playoffs (jornadas y bracket).
            </FieldHint>
          </div>
        </section>
      )}

      {setupMode && (
        // Eduardo 2026-10-05: «Publicar el torneo» pasa al final de
        // Calendario; el Draw solo sigue al paso 10.
        <div className="wiz-bottombar wiz-bottombar--brand fmt-bottombar drw-bottombar" data-no-toast data-setup-continue-schedule>
          <Link
            className="wiz-bottombar__back"
            to={`/admin/tournaments/${tournamentId}/setup/groups${selectedDivisionId ? `?division=${selectedDivisionId}` : ""}`}
          >
            ‹ Volver a Grupos
          </Link>
          <div className="fmt-bottombar__actions">
            <FieldHint label="¿Qué sigue después del Draw?">
              {tournament.status === "draft"
                ? "En Calendario (paso 10) revisas las fechas y, al final, publicas el torneo."
                : "El torneo ya está publicado. El Calendario (paso 10) es opcional."}
            </FieldHint>
            {canContinueToSchedule ? (
              <Link
                className="wiz-bottombar__next fmt-bottombar__next"
                to={`/admin/tournaments/${tournamentId}/setup/schedule${selectedDivisionId ? `?division=${selectedDivisionId}` : ""}`}
              >
                Continuar a Calendario
              </Link>
            ) : (
              <button className="wiz-bottombar__next fmt-bottombar__next" type="button" disabled title={continueToScheduleBlockedReason ?? undefined}>
                {readinessState === "loading" ? "Verificando categorías…" : "Completa todos los draws para continuar"}
              </button>
            )}
          </div>
          {!canContinueToSchedule && readinessState !== "loading" && continueToScheduleBlockedReason ? (
            <p className="drw-bottombar__reason">{continueToScheduleBlockedReason}</p>
          ) : null}
        </div>
      )}

      {!setupMode && <TournamentBottomNav tournamentId={tournament.id} />}
    </div>
  );
}
